import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { NotificationsConfig } from './NotificationsConfig';
import { NotificationTemplateModal } from './NotificationTemplateModal';
import { WebhookActionConfig } from './WebhookActionConfig';
import { CommandActionConfig } from './CommandActionConfig';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { getFileTimestamp } from '../../utils/timezone';
import { Card, CardContent } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { NumberInput } from '../ui/number-input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Switch } from '../ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { PaginationFooter, paginateArray, usePersistedPageSize } from '../shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useDebouncedValue, useSkipFirstEffect } from '../shared/useUrlPagination';
import { toast } from 'sonner';
import { deviceDisplayName } from '../../utils/deviceName';
import { HelpTip, EventRulesHelpPanel, ExplainRuleBanner } from './EventRuleHelp';
import { RuleTestPreview } from './RuleTestPreview';
import {
  Plus,
  Settings,
  Gauge,
  AlertTriangle,
  Pencil,
  Trash2,
  Loader2,
  Power,
  PowerOff,
  Zap,
  Clock,
  Shield,
  MapPin,
  Route,
  Search,
  ChevronDown,
  ChevronRight,
  Volume2,
  ArrowUpFromLine,
  ArrowDownToLine,
  Copy,
  X,
  Check,
  Upload,
  Download,
  Webhook,
  Terminal,
  Mail,
  MessageSquare,
  FileText,
  Globe,
  Send,
  Link2,
  Users,
  Crosshair,
  CreditCard,
  Ban,
  MoreVertical,
  Bell,
  Smartphone,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useIsMobileOrWebview } from '../../hooks/useIsMobileOrWebview';

// Compact per-rule indicator of which notification channels are switched on.
const NOTIF_CHANNELS = [
  { key: 'system_message', label: 'System Message', Icon: MessageSquare, onClass: 'text-blue-600 dark:text-blue-400' },
  { key: 'push_notification', label: 'Push', Icon: Bell, onClass: 'text-amber-600 dark:text-amber-400' },
  { key: 'email_enabled', label: 'Email', Icon: Mail, onClass: 'text-emerald-600 dark:text-emerald-400' },
  { key: 'sms_enabled', label: 'SMS', Icon: Smartphone, onClass: 'text-violet-600 dark:text-violet-400' },
];

const RuleNotificationIcons = ({ rule }) => {
  const n = rule.notifications || {};
  return (
    <div className="flex items-center gap-1.5" data-testid={`rule-notifications-${rule.id}`}>
      {NOTIF_CHANNELS.map(({ key, label, Icon, onClass }) => {
        // Backend defaults system_message to ON when the rule has no explicit setting.
        const on = key === 'system_message' ? n[key] !== false : !!n[key];
        return (
          <span
            key={key}
            title={`${label}: ${on ? 'On' : 'Off'}`}
            data-testid={`rule-notif-${key}-${rule.id}`}
            data-on={on ? 'true' : 'false'}
            className={on ? onClass : 'text-slate-300 dark:text-slate-600'}
          >
            <Icon className="w-3.5 h-3.5" strokeWidth={on ? 2.4 : 1.8} />
          </span>
        );
      })}
    </div>
  );
};

// Reusable pill/tag input for multi-value fields
const ValuePillInput = ({ values = [], onChange, placeholder = 'Add value...', testId = '' }) => {
  const [inputVal, setInputVal] = useState('');
  const addValue = () => {
    const v = inputVal.trim();
    if (v && !values.includes(v)) {
      onChange([...values, v]);
      setInputVal('');
    }
  };
  const removeValue = (v) => onChange(values.filter(x => x !== v));
  return (
    <div className="space-y-1.5">
      <div className="flex flex-wrap gap-1.5 min-h-[28px]">
        {values.map(v => (
          <span key={v} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-mono">
            {v}
            <button type="button" onClick={() => removeValue(v)} className="text-muted-foreground hover:text-destructive ml-0.5">&times;</button>
          </span>
        ))}
      </div>
      <div className="flex gap-1.5">
        <Input
          data-testid={testId}
          placeholder={placeholder}
          value={inputVal}
          onChange={e => setInputVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addValue(); } }}
          className="h-7 text-xs flex-1"
        />
        <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={addValue} disabled={!inputVal.trim()}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

// Human-readable duration formatting
const formatDuration = (totalSecs) => {
  if (totalSecs == null || totalSecs === 0) return '0s';
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  const parts = [];
  if (h > 0) parts.push(`${h} hour${h !== 1 ? 's' : ''}`);
  if (m > 0) parts.push(`${m} min${m !== 1 ? 's' : ''}`);
  if (s > 0) parts.push(`${s}s`);
  return parts.join(' ') || '0s';
};

// Smart best-fit unit for a seconds value
const bestUnit = (secs) => {
  if (secs > 0 && secs % 3600 === 0) return 'hours';
  if (secs > 0 && secs % 60 === 0) return 'minutes';
  return 'seconds';
};
const toUnitValue = (secs, unit) => {
  if (unit === 'hours') return secs / 3600;
  if (unit === 'minutes') return secs / 60;
  return secs;
};
const fromUnitValue = (val, unit) => {
  if (unit === 'hours') return Math.round(val * 3600);
  if (unit === 'minutes') return Math.round(val * 60);
  return Math.round(val);
};

// Reusable Duration Input with unit selector (seconds/minutes/hours)
const DurationInput = ({ value = 0, onChange, label, helpText, testId = '', className = '', size = 'sm', labelSuffix = null }) => {
  const [unit, setUnit] = useState(() => bestUnit(value || 0));
  const [localVal, setLocalVal] = useState(() => String(toUnitValue(value || 0, bestUnit(value || 0))));
  // Sync when external value changes (e.g. form reset, preset switch)
  const prevExternal = React.useRef(value);
  React.useEffect(() => {
    if (value !== prevExternal.current) {
      prevExternal.current = value;
      const u = bestUnit(value || 0);
      setUnit(u);
      setLocalVal(String(toUnitValue(value || 0, u)));
    }
  }, [value]);
  const handleValChange = (e) => {
    const raw = e.target.value;
    setLocalVal(raw);
    const num = parseFloat(raw);
    if (!isNaN(num) && num >= 0) {
      const secs = fromUnitValue(num, unit);
      prevExternal.current = secs;
      onChange(secs);
    } else if (raw === '' || raw === '0') {
      prevExternal.current = 0;
      onChange(0);
    }
  };
  const handleUnitChange = (newUnit) => {
    const currentSecs = fromUnitValue(parseFloat(localVal) || 0, unit);
    setUnit(newUnit);
    setLocalVal(String(toUnitValue(currentSecs, newUnit)));
  };
  return (
    <div className={`space-y-1 ${className}`}>
      {label && <div className="min-h-[20px] flex items-center gap-1"><Label className="text-xs">{label}</Label>{labelSuffix}</div>}
      <div className="flex items-center gap-1.5">
        <Input
          type="number"
          min="0"
          step={unit === 'hours' ? '0.5' : '1'}
          value={localVal}
          onChange={handleValChange}
          className={`${size === 'lg' ? 'h-10 text-sm' : 'h-8 text-xs'} flex-1`}
          data-testid={testId}
        />
        <select
          value={unit}
          onChange={(e) => handleUnitChange(e.target.value)}
          className={`${size === 'lg' ? 'h-10 text-sm' : 'h-8 text-xs'} rounded-md border bg-background px-2 cursor-pointer`}
          data-testid={testId ? `${testId}-unit` : undefined}
        >
          <option value="seconds">sec</option>
          <option value="minutes">min</option>
          <option value="hours">hours</option>
        </select>
      </div>
      {helpText && <p className="text-[10px] text-muted-foreground">{helpText}</p>}
    </div>
  );
};



const EVENT_TYPES = [
  { value: 'sensor_io_event', label: 'Sensor / IO Event', icon: Zap, category: 'Telemetry' },
  { value: 'overspeed_fixed', label: 'Overspeed (Fixed Limit)', icon: Gauge, category: 'Speed' },
  { value: 'overspeed_road_sign', label: 'Overspeed (Road Sign)', icon: Shield, category: 'Speed' },
  { value: 'zone_in', label: 'Zone Entering', icon: Zap, category: 'Geofence' },
  { value: 'zone_out', label: 'Zone Exiting', icon: Zap, category: 'Geofence' },
  { value: 'place_in', label: 'Place Entering', icon: MapPin, category: 'Geofence' },
  { value: 'place_out', label: 'Place Exiting', icon: MapPin, category: 'Geofence' },
  { value: 'route_deviation', label: 'Route Deviation (Future)', icon: Zap, category: 'Geofence', disabled: true },
  { value: 'route_entering', label: 'Route Entering', icon: Route, category: 'Geofence' },
  { value: 'route_exiting', label: 'Route Exiting', icon: Route, category: 'Geofence' },
  { value: 'vehicle_proximity', label: 'Proximity Enter', icon: Zap, category: 'Geofence' },
  { value: 'vehicle_proximity_exit', label: 'Proximity Exit', icon: Zap, category: 'Geofence' },
  { value: 'harsh_driving', label: 'Harsh Driving', icon: AlertTriangle, category: 'Driver' },
  { value: 'crash_detection', label: 'Crash Detection', icon: AlertTriangle, category: 'Driver' },
  { value: 'video_event', label: 'Video Event', icon: AlertTriangle, category: 'Driver' },
  { value: 'device_event', label: 'Device Event', icon: Zap, category: 'Device' },
  { value: 'excessive_idle', label: 'Excessive Idling', icon: Clock, category: 'Telemetry' },
  { value: 'underspeed', label: 'Underspeed', icon: Gauge, category: 'Speed' },
  { value: 'stopped', label: 'Stopped', icon: Zap, category: 'Telemetry' },
  { value: 'moving', label: 'Moving', icon: Zap, category: 'Telemetry' },
  { value: 'gprs_connected', label: 'GPRS Connected', icon: Zap, category: 'Connection' },
  { value: 'gprs_not_connected', label: 'GPRS Not Connected', icon: Zap, category: 'Connection' },
  { value: 'gps_connected', label: 'GPS Connected', icon: Zap, category: 'Connection' },
  { value: 'gps_not_connected', label: 'GPS Not Connected', icon: Zap, category: 'Connection' },
  { value: 'driver_bound', label: 'Driver Bound', icon: Users, category: 'People & Assets', description: 'Fires every time a driver is bound to a vehicle (BLE, RFID/iButton or ballot)' },
  { value: 'driver_change', label: 'Driver Change', icon: Users, category: 'People & Assets', description: 'Fires only when the active driver switches from one person to another' },
  { value: 'unmatched_rfid_scan', label: 'Unmatched RFID / iButton Scan', icon: CreditCard, category: 'People & Assets', description: 'Fires when an RFID / iButton card that is not registered to your account is scanned in a vehicle' },
  { value: 'disabled_rfid_scan', label: 'Disabled RFID / iButton Scan', icon: Ban, category: 'People & Assets', description: 'Fires when a card that is DISABLED in your card list is scanned in a vehicle' },
  { value: 'unallocated_rfid_scan', label: 'Unallocated RFID / iButton Scan', icon: CreditCard, category: 'People & Assets', description: 'Fires when a card that is in your card list but not allocated to a person is scanned in a vehicle' },
  { value: 'person_bound', label: 'Staff / Passenger Bound', icon: Users, category: 'People & Assets', description: 'Fires when a staff member or passenger boards (binds to) a vehicle' },
  { value: 'asset_in_transit', label: 'Asset In Transit', icon: Zap, category: 'People & Assets', description: 'A trailer/equipment beacon is on board and the vehicle has been underway for the configured delay (once per trip)' },
  { value: 'asset_movement_watch', label: 'Asset Movement Watch', icon: Zap, category: 'People & Assets', description: 'An asset travels in a vehicle it should not be in (authorized / watch list modes)' },
  { value: 'asset_dropoff', label: 'Asset Drop-off', icon: Zap, category: 'People & Assets', description: 'An asset is left somewhere and the vehicle drives away — records the drop location' },
  { value: 'left_behind_asset', label: 'Left-Behind Asset', icon: Zap, category: 'People & Assets', description: 'An asset\'s HOME vehicle drives off without it — set the Home vehicle on the asset\'s Fleet profile' },
  { value: 'person_arrival', label: 'Person Arrival', icon: Users, category: 'People & Assets', description: 'A chosen person (via their carrying vehicle) enters a monitored Place' },
  { value: 'person_departure', label: 'Person Departure', icon: Users, category: 'People & Assets', description: 'A chosen person (via their carrying vehicle) leaves a monitored Place' },
  { value: 'occupancy_departure', label: 'Occupancy Departure', icon: Users, category: 'People & Assets', description: 'A vehicle leaves a monitored Place while occupied — lists every driver, staff, passenger and asset on board' },
  { value: 'occupancy_count', label: 'Occupancy Count Threshold', icon: Users, category: 'People & Assets', description: 'The number of people on board crosses your limit — e.g. over capacity, or too few on a trip' },
];

const SEVERITY_OPTIONS = [
  { value: 'info', label: 'Info', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { value: 'warning', label: 'Warning', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'critical', label: 'Critical', color: 'bg-red-100 text-red-700 border-red-300' },
];

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const WEEKDAY_FULL = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Same alert tones as Road Speed Alert in Live Tracking (MoreFeaturesModal.jsx)
// Alert tone + duration catalogues now live in a shared constants module so the Settings
// page's Driver Confirmation Alert Sound picker uses the exact same list.
import { ALERT_TONES, ALERT_DURATIONS } from '../../constants/alertTones';

const TRANSITION_CARD_COLOR_EVENT_TYPES = [
  'zone_in',
  'zone_out',
  'place_in',
  'place_out',
  'ignition_on',
  'ignition_off',
  'moving',
  'stopped',
  'route_entering',
  'route_exiting',
  'gprs_connected',
  'gprs_not_connected',
  'gps_connected',
  'gps_not_connected',
  'vehicle_proximity',
  'vehicle_proximity_exit',
  'harsh_driving',
  'crash_detection',
  'video_event',
  'device_event',
  'driver_bound',
  'driver_change',
  'unmatched_rfid_scan',
  'disabled_rfid_scan',
  'unallocated_rfid_scan',
  'asset_in_transit',
  'asset_movement_watch',
  'asset_dropoff',
  'left_behind_asset',
  'person_arrival',
  'person_departure',
  'occupancy_departure',
  'person_bound',
];

const GEOFENCE_TRANSITION_EVENT_TYPES = ['zone_in', 'zone_out', 'place_in', 'place_out', 'route_entering', 'route_exiting'];

const CARD_COLOR_DURATION_OPTIONS = [
  { id: '5s', label: '5 seconds' },
  { id: '10s', label: '10 seconds' },
  { id: '30s', label: '30 seconds' },
  { id: 'until_click', label: 'Persistent until device card click' },
  { id: 'custom', label: 'Custom seconds' },
];

const SENSOR_IO_PRESETS = [
  { key: 'custom', label: 'Custom' },
  { key: 'ignition_on', label: 'Ignition On' },
  { key: 'ignition_off', label: 'Ignition Off' },
  { key: 'door_open_any', label: 'Any Door Opened' },
  { key: 'door_closed_any', label: 'Any Door Closed' },
  { key: 'door_closed_all', label: 'All Doors Closed' },
  { key: 'park_brake_on', label: 'Park Brake On' },
  { key: 'park_brake_off', label: 'Park Brake Off' },
  { key: 'pto_on', label: 'PTO On' },
  { key: 'pto_off', label: 'PTO Off' },
  { key: 'immobilizer_on', label: 'Immobilizer On' },
  { key: 'immobilizer_off', label: 'Immobilizer Off' },
  { key: 'vehicle_locked', label: 'Vehicle Locked' },
  { key: 'vehicle_unlocked', label: 'Vehicle Unlocked' },
  { key: 'alarm_activated', label: 'Alarm Activated' },
  { key: 'alarm_deactivated', label: 'Alarm Deactivated' },
  { key: 'temperature_high', label: 'Temperature High' },
  { key: 'temperature_low', label: 'Temperature Low' },
  { key: 'fuel_low', label: 'Fuel' },
  { key: 'battery', label: 'Battery' },
  { key: 'odometer_reached', label: 'Odometer Reached' },
  { key: 'engine_hours_reached', label: 'Engine Hours Reached' },
];

const SENSOR_IO_PRESET_LABELS = Object.fromEntries(SENSOR_IO_PRESETS.map((preset) => [preset.key, preset.label]));
const SENSOR_IO_DEVICE_METRIC_PRESETS = ['odometer_reached', 'engine_hours_reached'];
const OPERATIONAL_TRANSITION_EVENT_TYPES = ['stopped', 'moving'];
const CONNECTION_EVENT_TYPES = ['gprs_connected', 'gprs_not_connected', 'gps_connected', 'gps_not_connected'];

// Short config summary suffix shown under a rule's name (desktop table + mobile cards).
const getRuleConfigSummary = (rule) => {
  const c = rule.config || {};
  if (rule.event_type === 'overspeed_fixed' && c.speed_limit_kmh) return ` - ${c.speed_limit_kmh} km/h`;
  if (rule.event_type === 'overspeed_road_sign' && c.speed_buffer_value != null) return ` - +${c.speed_buffer_value}${c.speed_buffer_type === 'percent' ? '%' : ' km/h'}`;
  if (rule.event_type === 'underspeed' && c.speed_limit_kmh != null) return ` - below ${c.speed_limit_kmh} km/h`;
  if (rule.event_type === 'excessive_idle' && c.idle_duration_secs != null) return ` - ${c.idle_duration_secs}s idle`;
  if (rule.event_type === 'sensor_io_event' && c.template_key) return ` - ${SENSOR_IO_PRESET_LABELS[c.template_key] || 'Sensor / IO Event'}`;
  if (rule.event_type === 'zone_in' || rule.event_type === 'zone_out') return ` - ${(c.monitored_zone_ids || []).length} zone(s)`;
  if (rule.event_type === 'place_in' || rule.event_type === 'place_out') return ` - ${(c.monitored_place_ids || []).length} place(s)`;
  if (rule.event_type === 'route_entering' || rule.event_type === 'route_exiting') return ` - ${(c.monitored_route_ids || []).length} route(s)`;
  if (CONNECTION_EVENT_TYPES.includes(rule.event_type) && c.trigger_duration_secs != null) return ` - ${formatDuration(c.trigger_duration_secs)} delay`;
  if (rule.event_type === 'harsh_driving') {
    const parts = [];
    if ((c.acceleration_values || []).length || c.acceleration_value) parts.push('Accel');
    if ((c.braking_values || []).length || c.braking_value) parts.push('Brake');
    if ((c.cornering_values || []).length || c.cornering_value) parts.push('Corner');
    if ((c.cornering_left_values || []).length || c.cornering_left_value) parts.push('Corner L');
    if ((c.cornering_right_values || []).length || c.cornering_right_value) parts.push('Corner R');
    return parts.length ? ` - ${parts.join(', ')}` : '';
  }
  if (rule.event_type === 'crash_detection' && c.trigger_values?.length > 0) return ` - Values: ${c.trigger_values.join(', ')}`;
  if ((rule.event_type === 'video_event' || rule.event_type === 'device_event') && c.trigger_values?.length > 0) return ` - ${c.trigger_values.length} trigger(s)`;
  if (ASSET_EVENT_TYPES.includes(rule.event_type)) {
    const assets = (c.asset_ids || []).length;
    const scope = assets ? `${assets} asset(s)` : (c.asset_ids === undefined ? 'all assets (legacy)' : 'no assets selected');
    if (rule.event_type === 'asset_movement_watch') return ` - ${scope}, ${(c.watch_mode || 'authorized') === 'authorized' ? 'except listed vehicles' : 'only listed vehicles'}`;
    return ` - ${scope}`;
  }
  if (rule.event_type === 'person_arrival' || rule.event_type === 'person_departure') return ` - ${(c.person_ids || []).length} person(s), ${(c.monitored_place_ids || []).length + (c.monitored_zone_ids || []).length + (c.monitored_route_ids || []).length} area(s)`;
  if (rule.event_type === 'occupancy_departure') return ` - ${(c.monitored_place_ids || []).length + (c.monitored_zone_ids || []).length + (c.monitored_route_ids || []).length} area(s)`;
  if (rule.event_type === 'occupancy_count') return ` - ${(c.count_mode || 'at_or_above') === 'at_or_below' ? '≤' : '≥'} ${c.occupancy_threshold || '?'} people${c.only_on_trip ? ', on trip only' : ''}`;
  if (rule.event_type === 'person_bound') {
    if (c.staff_ids === undefined && c.passenger_ids === undefined && c.group_ids === undefined) return ` - ${(c.entity_types || ['staff', 'passenger']).join('/')}${(c.person_ids || []).length ? `, ${c.person_ids.length} person(s)` : ', everyone (legacy)'}`;
    const gTxt = (c.group_ids || []).length ? `, ${c.group_ids.length} group(s)` : '';
    return ` - ${(c.staff_ids || []).length} staff, ${(c.passenger_ids || []).length} passenger(s)${gTxt}`;
  }
  return '';
};
// Shared explicit-selection list: search + Select All + checkboxes (same UX as the Devices card)
const PickList = ({ label, items, selectedIds, onChange, testIdPrefix, height = 150, emptyNote }) => {
  const [q, setQ] = useState('');
  const shown = items.filter(i => !q || (i.label || '').toLowerCase().includes(q.toLowerCase()));
  const allShownSelected = shown.length > 0 && shown.every(i => selectedIds.includes(i.id));
  const toggle = (id) => {
    const ids = new Set(selectedIds);
    ids.has(id) ? ids.delete(id) : ids.add(id);
    onChange(Array.from(ids));
  };
  const toggleAll = () => {
    const ids = new Set(selectedIds);
    shown.forEach(i => allShownSelected ? ids.delete(i.id) : ids.add(i.id));
    onChange(Array.from(ids));
  };
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs">{label}</Label>
        <button
          type="button"
          className="text-[10px] text-primary hover:underline shrink-0 disabled:opacity-50 disabled:no-underline"
          onClick={toggleAll}
          disabled={shown.length === 0}
          data-testid={`${testIdPrefix}-select-all`}
        >
          {allShownSelected ? 'Deselect All' : 'Select All'}
        </button>
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="pl-8 h-8 text-sm" data-testid={`${testIdPrefix}-search`} />
      </div>
      <ScrollArea style={{ height: `${height}px` }} className="border rounded-lg">
        {items.length === 0 && <p className="text-xs text-muted-foreground p-3">{emptyNote || 'Nothing available.'}</p>}
        {items.length > 0 && shown.length === 0 && <p className="text-xs text-muted-foreground p-3">No matches for "{q}".</p>}
        {shown.map(i => (
          <div key={i.id} className="flex items-center gap-2 py-1 px-3 hover:bg-muted/50 cursor-pointer" onClick={() => toggle(i.id)} data-testid={`${testIdPrefix}-item-${i.id}`}>
            <Checkbox checked={selectedIds.includes(i.id)} />
            <span className="text-xs">{i.label}</span>
            {i.meta && <span className="text-[10px] text-muted-foreground capitalize ml-auto">{i.meta}</span>}
          </div>
        ))}
      </ScrollArea>
      <p className="text-[10px] text-muted-foreground">{selectedIds.length} selected</p>
    </div>
  );
};

const PROXIMITY_EVENT_TYPES = ['vehicle_proximity', 'vehicle_proximity_exit'];
const RFID_SCAN_ALERT_EVENT_TYPES = ['unmatched_rfid_scan', 'disabled_rfid_scan', 'unallocated_rfid_scan'];
const DRIVER_BINDING_EVENT_TYPES = ['driver_bound', 'driver_change'];
const ASSET_EVENT_TYPES = ['asset_in_transit', 'asset_movement_watch', 'asset_dropoff', 'left_behind_asset'];
const HARSH_DRIVING_EVENT_TYPES = ['harsh_driving'];
const CRASH_DETECTION_EVENT_TYPES = ['crash_detection'];

// Suggested source keywords for Harsh Driving and Crash Detection
const HARSH_DRIVING_SOURCE_KEYWORDS = ['event code', 'green driving type', 'green driving'];
const CRASH_DETECTION_SOURCE_KEYWORDS = ['event code', 'crash detection', 'crash'];

const getHarshDrivingSuggestedSources = (options = []) =>
  options.filter(o => matchesSourceKeywords(o, HARSH_DRIVING_SOURCE_KEYWORDS));

const getCrashDetectionSuggestedSources = (options = []) =>
  options.filter(o => matchesSourceKeywords(o, CRASH_DETECTION_SOURCE_KEYWORDS));

const VIDEO_EVENT_TYPES = ['video_event'];
const VIDEO_EVENT_SOURCE_KEYWORDS = ['event code', 'video'];
const DEVICE_EVENT_TYPES = ['device_event'];
const DEVICE_EVENT_SOURCE_KEYWORDS = ['event code'];

const getVideoEventSuggestedSources = (options = []) =>
  options.filter(o => matchesSourceKeywords(o, VIDEO_EVENT_SOURCE_KEYWORDS));

const getDeviceEventSuggestedSources = (options = []) =>
  options.filter(o => matchesSourceKeywords(o, DEVICE_EVENT_SOURCE_KEYWORDS));

// Video event code catalog — grouped by category
const VIDEO_EVENT_CATALOG = [
  { category: 'DMS — Driver Monitoring', items: [
    { code: '655', label: 'Phone Use Detected' },
    { code: '656', label: 'Smoking Detected' },
    { code: '657', label: 'Fatigue / Tired' },
    { code: '658', label: 'Yawn Detected' },
    { code: '659', label: 'Distracted Driving' },
    { code: '660', label: 'Driver Not Found' },
    { code: '661', label: 'Camera Covered' },
    { code: '662', label: 'IR Sensor Blocked' },
    { code: '663', label: 'No Seatbelt' },
  ]},
  { category: 'LDW — Lane Departure Warning', items: [
    { code: '651', label: 'Left Lane Departure' },
    { code: '652', label: 'Right Lane Departure' },
  ]},
  { category: 'ADAS — Advanced Driver Assistance', items: [
    { code: '648', label: 'Forward Collision Warning' },
    { code: '650', label: 'Pedestrian Collision Warning' },
    { code: '653', label: 'Headway / Following Distance' },
  ]},
  { category: 'BSD — Blind Spot Detection', items: [
    { code: '669', label: 'Front' },
    { code: '670', label: 'Behind' },
    { code: '671', label: 'Left' },
    { code: '672', label: 'Right' },
  ]},
  { category: 'Video Health', items: [
    { code: '576', label: 'CH1 Video Loss' }, { code: '610', label: 'CH1 Recovery' },
    { code: '577', label: 'CH2 Video Loss' }, { code: '611', label: 'CH2 Recovery' },
    { code: '578', label: 'CH3 Video Loss' }, { code: '612', label: 'CH3 Recovery' },
    { code: '579', label: 'CH4 Video Loss' }, { code: '613', label: 'CH4 Recovery' },
    { code: '580', label: 'CH5 Video Loss' }, { code: '614', label: 'CH5 Recovery' },
    { code: '581', label: 'CH6 Video Loss' }, { code: '615', label: 'CH6 Recovery' },
  ]},
  { category: 'Storage', items: [
    { code: '608', label: 'Storage Failure' },
    { code: '609', label: 'Storage Full' },
  ]},
  { category: 'Other', items: [
    { code: '647', label: 'Face ID Alarm' },
  ]},
  { category: 'Inputs Active', items: [
    { code: '1', label: 'SOS Pressed' },
    { code: '2', label: 'Input 2 Active' },
    { code: '3', label: 'Input 3 Active' },
    { code: '4', label: 'Input 4 Active' },
    { code: '5', label: 'Input 5 Active' },
    { code: '6', label: 'Input 6 Active' },
    { code: '7', label: 'Input 7 Active' },
    { code: '8', label: 'Input 8 Active' },
    { code: '118', label: 'Input 9 Active' },
  ]},
  { category: 'Inputs Inactive', items: [
    { code: '9', label: 'Input 1 Inactive' },
    { code: '10', label: 'Input 2 Inactive' },
    { code: '11', label: 'Input 3 Inactive' },
    { code: '12', label: 'Input 4 Inactive' },
    { code: '13', label: 'Input 5 Inactive' },
    { code: '14', label: 'Input 6 Inactive' },
    { code: '15', label: 'Input 7 Inactive' },
    { code: '16', label: 'Input 8 Inactive' },
    { code: '119', label: 'Input 9 Inactive' },
  ]},
  { category: 'Movement & Driving', items: [
    { code: '19', label: 'Speeding' },
    { code: '55', label: 'Harsh Cornering' },
    { code: '56', label: 'Idle Alarm' },
    { code: '57', label: 'Fatigue Driving' },
    { code: '58', label: 'Parking (Long)' },
    { code: '59', label: 'Accident' },
    { code: '78', label: 'Impact' },
    { code: '90', label: 'Sharp Turn Left' },
    { code: '91', label: 'Sharp Turn Right' },
    { code: '129', label: 'Harsh Braking' },
    { code: '130', label: 'Harsh Acceleration' },
    { code: '133', label: 'Idle Overtime' },
    { code: '134', label: 'Idle Recovery' },
    { code: '135', label: 'Fatigue Driving Alert' },
    { code: '136', label: 'Enough Rest' },
    { code: '144', label: 'Ignition On' },
    { code: '145', label: 'Ignition Off' },
  ]},
  { category: 'Fuel', items: [
    { code: '54', label: 'Fuel Theft' },
  ]},
  { category: 'Outputs Active', items: [
    { code: '94', label: 'Output 1 Active' },
    { code: '95', label: 'Output 2 Active' },
    { code: '96', label: 'Output 3 Active' },
    { code: '97', label: 'Output 4 Active' },
    { code: '98', label: 'Output 5 Active' },
    { code: '159', label: 'Output 6 Active' },
    { code: '160', label: 'Output 7 Active' },
    { code: '161', label: 'Output 8 Active' },
  ]},
  { category: 'Outputs Inactive', items: [
    { code: '99', label: 'Output 1 Inactive' },
    { code: '100', label: 'Output 2 Inactive' },
    { code: '101', label: 'Output 3 Inactive' },
    { code: '102', label: 'Output 4 Inactive' },
    { code: '103', label: 'Output 5 Inactive' },
    { code: '162', label: 'Output 6 Inactive' },
    { code: '163', label: 'Output 7 Inactive' },
    { code: '164', label: 'Output 8 Inactive' },
  ]},
];

// All known codes from catalog for distinguishing custom entries
const ALL_KNOWN_VIDEO_CODES = new Set(VIDEO_EVENT_CATALOG.flatMap(g => g.items.map(i => i.code)));

const DEVICE_EVENT_CATALOG = [
  { category: 'Inputs Active', items: [
    { code: '1', label: 'SOS Pressed' },
    { code: '2', label: 'Input 2 Active' },
    { code: '3', label: 'Input 3 Active' },
    { code: '4', label: 'Input 4 Active' },
    { code: '5', label: 'Input 5 Active' },
    { code: '6', label: 'Input 6 Active' },
    { code: '7', label: 'Input 7 Active' },
    { code: '8', label: 'Input 8 Active' },
    { code: '118', label: 'Input 9 Active' },
  ]},
  { category: 'Inputs Inactive', items: [
    { code: '9', label: 'Input 1 Inactive' },
    { code: '10', label: 'Input 2 Inactive' },
    { code: '11', label: 'Input 3 Inactive' },
    { code: '12', label: 'Input 4 Inactive' },
    { code: '13', label: 'Input 5 Inactive' },
    { code: '14', label: 'Input 6 Inactive' },
    { code: '15', label: 'Input 7 Inactive' },
    { code: '16', label: 'Input 8 Inactive' },
    { code: '119', label: 'Input 9 Inactive' },
  ]},
  { category: 'Power & Battery', items: [
    { code: '17', label: 'Low Battery' },
    { code: '18', label: 'Low External Voltage' },
    { code: '22', label: 'External Battery On' },
    { code: '23', label: 'External Battery Cut' },
    { code: '40', label: 'Power On' },
  ]},
  { category: 'GPS & Geofence', items: [
    { code: '20', label: 'Geo-fence Enter' },
    { code: '21', label: 'Geo-fence Exit' },
    { code: '24', label: 'GPS Signal Lost' },
    { code: '25', label: 'GPS Signal Recovery' },
    { code: '28', label: 'GPS Antenna Cut' },
  ]},
  { category: 'Device Status', items: [
    { code: '26', label: 'Enter Sleep' },
    { code: '27', label: 'Exit Sleep' },
    { code: '29', label: 'Device Reboot' },
    { code: '30', label: 'Motion Alarm' },
    { code: '31', label: 'Heartbeat' },
    { code: '33', label: 'Track by Distance' },
    { code: '34', label: 'Command Response' },
    { code: '35', label: 'Track by Interval' },
    { code: '36', label: 'Towing Alarm' },
    { code: '37', label: 'RFID Event' },
    { code: '39', label: 'Picture Event' },
    { code: '122', label: 'SD State' },
    { code: '165', label: 'Bluetooth Peripheral Alarm' },
  ]},
  { category: 'Movement & Driving', items: [
    { code: '19', label: 'Speeding' },
    { code: '32', label: 'Cornering' },
    { code: '41', label: 'Stop Moving' },
    { code: '42', label: 'Start Moving' },
    { code: '55', label: 'Harsh Cornering' },
    { code: '56', label: 'Idle Alarm' },
    { code: '57', label: 'Fatigue Driving' },
    { code: '58', label: 'Parking (Long)' },
    { code: '59', label: 'Accident' },
    { code: '65', label: 'Collision' },
    { code: '66', label: 'Rollover' },
    { code: '78', label: 'Impact' },
    { code: '90', label: 'Sharp Turn Left' },
    { code: '91', label: 'Sharp Turn Right' },
    { code: '128', label: 'Rollover' },
    { code: '129', label: 'Harsh Braking' },
    { code: '130', label: 'Harsh Acceleration' },
    { code: '133', label: 'Idle Overtime' },
    { code: '134', label: 'Idle Recovery' },
    { code: '135', label: 'Fatigue Driving Alert' },
    { code: '136', label: 'Enough Rest' },
    { code: '138', label: 'Speed Recovery' },
    { code: '144', label: 'Ignition On' },
    { code: '145', label: 'Ignition Off' },
  ]},
  { category: 'Network & Calls', items: [
    { code: '44', label: 'GSM Jamming' },
    { code: '63', label: 'No GSM Jamming' },
    { code: '70', label: 'Reject Incoming Call' },
    { code: '71', label: 'Get Location by Call' },
    { code: '72', label: 'Auto-answer Incoming Call' },
  ]},
  { category: 'Temperature, Fuel & CAN', items: [
    { code: '50', label: 'Temperature High' },
    { code: '51', label: 'Temperature Low' },
    { code: '52', label: 'Full Fuel' },
    { code: '53', label: 'Low Fuel' },
    { code: '54', label: 'Fuel Theft' },
    { code: '64', label: 'CAN Bus Data' },
    { code: '82', label: 'Fuel Filling' },
    { code: '83', label: 'Ultrasonic Sensor Drop' },
    { code: '87', label: 'TPMS Alarm' },
    { code: '139', label: 'Maintenance Notice' },
  ]},
  { category: 'Outputs Active', items: [
    { code: '94', label: 'Output 1 Active' },
    { code: '95', label: 'Output 2 Active' },
    { code: '96', label: 'Output 3 Active' },
    { code: '97', label: 'Output 4 Active' },
    { code: '98', label: 'Output 5 Active' },
    { code: '159', label: 'Output 6 Active' },
    { code: '160', label: 'Output 7 Active' },
    { code: '161', label: 'Output 8 Active' },
  ]},
  { category: 'Outputs Inactive', items: [
    { code: '99', label: 'Output 1 Inactive' },
    { code: '100', label: 'Output 2 Inactive' },
    { code: '101', label: 'Output 3 Inactive' },
    { code: '102', label: 'Output 4 Inactive' },
    { code: '103', label: 'Output 5 Inactive' },
    { code: '162', label: 'Output 6 Inactive' },
    { code: '163', label: 'Output 7 Inactive' },
    { code: '164', label: 'Output 8 Inactive' },
  ]},
  { category: 'People', items: [
    { code: '117', label: 'People Counter' },
  ]},
];

const ALL_KNOWN_DEVICE_CODES = new Set(DEVICE_EVENT_CATALOG.flatMap(g => g.items.map(i => i.code)));

// Codes that are exclusively Video Event triggers (not also present in the
// Device catalog). Only these are blocked from Device Event rules — the
// dual-purpose codes now appear in both catalogs and are allowed in both.
const VIDEO_EXCLUSIVE_CODES = new Set(
  [...ALL_KNOWN_VIDEO_CODES].filter(code => !ALL_KNOWN_DEVICE_CODES.has(code))
);

const PROXIMITY_PAIR_MODE_OPTIONS = [
  { value: 'any_pair', label: 'Any Pair', description: 'Any combination of selected devices triggers the event (per pair)' },
  { value: 'group_proximity', label: 'Group Proximity', description: 'Fires once per device when it enters/exits proximity of any other monitored device (less noise with 3+ devices)' },
  { value: 'specific_pairs', label: 'Specific Pairs', description: 'Only explicitly defined device pairs (A↔B) trigger the event' },
  { value: 'target_watch', label: 'Target & Watch', description: 'Alert when a target device comes near any of the watch devices' },
];

const buildProximityEventConfig = (eventType, prevConfig = {}) => ({
  ...prevConfig,
  proximity_distance_m: prevConfig.proximity_distance_m ?? 500,
  trigger_duration_secs: prevConfig.trigger_duration_secs ?? 0,
  pair_mode: prevConfig.pair_mode || 'any_pair',
  device_pairs: prevConfig.device_pairs || [],
  target_device_id: prevConfig.target_device_id || '',
  watch_device_ids: prevConfig.watch_device_ids || [],
});

const matchesSourceKeywords = (option, keywords = []) => {
  const haystack = `${option.display_name || ''} ${option.io_type || ''}`.toLowerCase();
  return keywords.some((keyword) => haystack.includes(keyword));
};

const toSensorSourceDef = (option) => ({
  selection_mode: option.io_type ? 'mapped_io_by_type' : 'mapped_io_by_display_name',
  match_value: option.display_name,
  display_label: option.display_name,
  io_type: option.io_type || '',
});

const getSensorPresetSourceMatches = (presetKey, options = []) => {
  if (presetKey === 'ignition_on' || presetKey === 'ignition_off') {
    return options.filter((option) => matchesSourceKeywords(option, ['ignition'])).slice(0, 1);
  }
  if (presetKey === 'door_open_any' || presetKey === 'door_closed_any' || presetKey === 'door_closed_all') {
    return options.filter((option) => matchesSourceKeywords(option, ['door']));
  }
  if (presetKey === 'park_brake_on' || presetKey === 'park_brake_off') {
    return options.filter((option) => matchesSourceKeywords(option, ['park brake', 'parking brake', 'handbrake', 'brake'])).slice(0, 1);
  }
  if (presetKey === 'pto_on' || presetKey === 'pto_off') {
    return options.filter((option) => matchesSourceKeywords(option, ['pto'])).slice(0, 1);
  }
  if (presetKey === 'immobilizer_on' || presetKey === 'immobilizer_off') {
    return options.filter((option) => matchesSourceKeywords(option, ['immobilizer', 'immobiliser'])).slice(0, 1);
  }
  if (presetKey === 'vehicle_locked' || presetKey === 'vehicle_unlocked') {
    return options.filter((option) => matchesSourceKeywords(option, ['lock', 'locked', 'unlock', 'unlocked'])).slice(0, 1);
  }
  if (presetKey === 'alarm_activated' || presetKey === 'alarm_deactivated') {
    return options.filter((option) => matchesSourceKeywords(option, ['alarm'])).slice(0, 1);
  }
  if (presetKey === 'temperature_high' || presetKey === 'temperature_low') {
    return options.filter((option) => matchesSourceKeywords(option, ['temperature', 'temp'])).slice(0, 1);
  }
  if (presetKey === 'fuel_low') {
    return options.filter((option) => matchesSourceKeywords(option, ['fuel'])).slice(0, 1);
  }
  if (presetKey === 'battery') {
    return options.filter((option) => matchesSourceKeywords(option, ['battery', 'batt'])).slice(0, 1);
  }
  return [];
};

const buildSensorPresetConfig = (presetKey, options = []) => {
  const matchedOptions = getSensorPresetSourceMatches(presetKey, options);
  const base = {
    template_key: presetKey,
    source_kind: 'mapped_io',
    sensor_sources: matchedOptions.map(toSensorSourceDef),
    sensor_mode: 'transition',
    group_operator: 'single',
    value_mode: 'boolean',
    trigger_definition: {
      transition: { operator: 'eq', value: '1', direction: 'false_to_true' },
      threshold: { operator: 'gt', value: '30', clear_margin: '2' },
    },
    confirm_delay_secs: 0,
    occurrence_behavior: 'instant',
  };

  if (presetKey === 'custom') {
    return {
      ...base,
      sensor_sources: [],
      sensor_mode: 'transition',
      group_operator: 'single',
      value_mode: 'boolean',
      trigger_definition: {
        transition: { operator: 'eq', value: '', direction: 'false_to_true' },
        threshold: { operator: 'gt', value: '', clear_margin: '' },
      },
      occurrence_behavior: 'instant',
    };
  }

  if (presetKey === 'ignition_off') {
    return {
      ...base,
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'eq', value: '1', direction: 'true_to_false' },
      },
    };
  }
  if (['park_brake_on', 'pto_on', 'immobilizer_on', 'vehicle_locked', 'alarm_activated'].includes(presetKey)) {
    return {
      ...base,
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'eq', value: '1', direction: 'false_to_true' },
      },
    };
  }
  if (['park_brake_off', 'pto_off', 'immobilizer_off', 'vehicle_unlocked', 'alarm_deactivated'].includes(presetKey)) {
    return {
      ...base,
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'eq', value: '1', direction: 'true_to_false' },
      },
    };
  }
  if (presetKey === 'door_open_any') {
    return {
      ...base,
      group_operator: 'any',
      per_source_transition: true,
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'eq', value: '1', direction: 'false_to_true' },
      },
    };
  }
  if (presetKey === 'door_closed_any') {
    return {
      ...base,
      group_operator: 'any',
      per_source_transition: true,
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'eq', value: '0', direction: 'false_to_true' },
      },
    };
  }
  if (presetKey === 'door_closed_all') {
    return {
      ...base,
      group_operator: 'all',
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'eq', value: '0', direction: 'false_to_true' },
      },
    };
  }
  if (presetKey === 'temperature_high') {
    return {
      ...base,
      sensor_mode: 'threshold',
      value_mode: 'numeric',
      temp_unit: base.temp_unit || '°C',
      trigger_definition: {
        ...base.trigger_definition,
        threshold: { operator: 'gt', value: '30', clear_margin: '2' },
      },
      occurrence_behavior: 'active_until_clear',
    };
  }
  if (presetKey === 'temperature_low') {
    return {
      ...base,
      sensor_mode: 'threshold',
      value_mode: 'numeric',
      temp_unit: base.temp_unit || '°C',
      trigger_definition: {
        ...base.trigger_definition,
        threshold: { operator: 'lt', value: '2', clear_margin: '1' },
      },
      occurrence_behavior: 'active_until_clear',
    };
  }
  if (presetKey === 'fuel_low') {
    return {
      ...base,
      sensor_mode: 'threshold',
      value_mode: 'numeric',
      fuel_unit: base.fuel_unit || 'L',
      trigger_definition: {
        ...base.trigger_definition,
        threshold: { operator: 'lt', value: '15', clear_margin: '3' },
      },
      occurrence_behavior: 'active_until_clear',
    };
  }
  if (presetKey === 'battery') {
    return {
      ...base,
      sensor_mode: 'threshold',
      value_mode: 'numeric',
      battery_unit: base.battery_unit || 'V',
      trigger_definition: {
        ...base.trigger_definition,
        threshold: { operator: 'lt', value: '11.5', clear_margin: '0.5' },
      },
      occurrence_behavior: 'active_until_clear',
    };
  }
  if (presetKey === 'odometer_reached') {
    return {
      ...base,
      source_kind: 'device_metric',
      sensor_sources: [{ selection_mode: 'device_metric', match_value: 'computed_odometer', display_label: 'Device Odometer', io_type: 'Device Odometer' }],
      sensor_mode: 'transition',
      value_mode: 'numeric',
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'gte', value: '100000', direction: 'false_to_true' },
      },
      occurrence_behavior: 'instant',
    };
  }
  if (presetKey === 'engine_hours_reached') {
    return {
      ...base,
      source_kind: 'device_metric',
      sensor_sources: [{ selection_mode: 'device_metric', match_value: 'computed_engine_hours', display_label: 'Device Engine Hours', io_type: 'Device Engine Hours' }],
      sensor_mode: 'transition',
      value_mode: 'numeric',
      trigger_definition: {
        ...base.trigger_definition,
        transition: { operator: 'gte', value: '5000', direction: 'false_to_true' },
      },
      occurrence_behavior: 'instant',
    };
  }
  return base;
};

const buildOperationalEventConfig = (eventType, prevConfig = {}) => {
  if (eventType === 'stopped' || eventType === 'moving') {
    return {
      ...prevConfig,
      trigger_duration_secs: 0,
    };
  }
  if (eventType === 'excessive_idle') {
    return {
      ...prevConfig,
      idle_duration_secs: prevConfig.idle_duration_secs ?? 120,
    };
  }
  if (eventType === 'underspeed') {
    return {
      ...prevConfig,
      speed_limit_kmh: prevConfig.speed_limit_kmh ?? 20,
      trigger_duration_secs: prevConfig.trigger_duration_secs ?? 10,
      clear_margin_kmh: prevConfig.clear_margin_kmh ?? 3,
      require_ignition_on: prevConfig.require_ignition_on ?? true,
    };
  }
  return prevConfig;
};

const CONNECTION_EVENT_DEFAULTS = {
  gprs_connected: 0,
  gprs_not_connected: 300,
  gps_connected: 0,
  gps_not_connected: 300,
};

const buildConnectionEventConfig = (eventType, prevConfig = {}) => {
  const defaultDelay = CONNECTION_EVENT_DEFAULTS[eventType] ?? 0;
  return {
    ...prevConfig,
    trigger_duration_secs: prevConfig.trigger_duration_secs ?? defaultDelay,
  };
};

const getDeviceScopeLabel = (rule) => {
  // Person place events follow the selected people (their carrying vehicle) — no device scope.
  if (['person_arrival', 'person_departure'].includes(rule.event_type)) {
    return `Follows ${(rule.config?.person_ids || []).length} person(s)`;
  }
  // Asset Movement Watch scopes vehicles via its watch list, not device selection.
  if (rule.event_type === 'asset_movement_watch') {
    const n = (rule.config?.watch_vehicle_ids || []).length;
    return (rule.config?.watch_mode || 'authorized') === 'authorized' ? `Any vehicle except ${n}` : `${n} watched vehicle(s)`;
  }
  if (rule.device_ids?.length > 0) return `${rule.device_ids.length} device(s)`;
  const pm = rule.config?.pair_mode;
  if (['vehicle_proximity', 'vehicle_proximity_exit'].includes(rule.event_type) && ['specific_pairs', 'target_watch'].includes(pm)) {
    if (pm === 'specific_pairs') return `${(rule.config?.device_pairs || []).length} pair(s)`;
    return `Target + ${(rule.config?.watch_device_ids || []).length} watch`;
  }
  if (rule.all_devices) return 'All my devices';
  return 'No devices — inactive';
};

const DEFAULT_FORM = {
  name: '',
  event_type: 'sensor_io_event',
  enabled: true,
  severity: 'warning',
  device_ids: [],
  all_devices: false,
  config: {
    speed_limit_kmh: 120,
    speed_buffer_type: 'fixed_kmh',
    speed_buffer_value: 10,
    speed_tiers: [
      { minSpeed: 40, maxSpeed: 60, bufferType: 'percent', bufferValue: 10 },
      { minSpeed: 61, maxSpeed: 99, bufferType: 'percent', bufferValue: 7 },
      { minSpeed: 100, maxSpeed: null, bufferType: 'kmh', bufferValue: 15 },
    ],
    trigger_duration_secs: 10,
    clear_margin_kmh: 0,
    occurrence_mode: 'single',  // 'single' = 1 per episode, 'interval' = re-trigger every X minutes
    retrigger_interval_minutes: 5,
    monitored_zone_ids: [],
    monitored_place_ids: [],
    monitored_route_ids: [],
    template_key: 'custom',
    source_kind: 'mapped_io',
    sensor_sources: [],
    sensor_mode: 'transition',
    group_operator: 'single',
    value_mode: 'boolean',
    trigger_definition: {
      transition: { operator: 'eq', value: '1', direction: 'false_to_true' },
      threshold: { operator: 'gt', value: '30', clear_margin: '2' },
    },
    confirm_delay_secs: 0,
    occurrence_behavior: 'instant',
    depending_on_zones: false,
    zone_ids: [],
    exclude_zones: false,
    exclude_zone_ids: [],
    depending_on_places: false,
    place_ids: [],
    exclude_places: false,
    exclude_place_ids: [],
    depending_on_routes: false,
    route_ids: [],
    exclude_routes: false,
    exclude_route_ids: [],
    io_conditions_enabled: false,
    io_conditions: {
      main_timer_secs: 0,
      main_timer_mode: 'pre',
      logic: 'AND',
      groups: [
        {
          logic: 'AND',
          conditions: [
            { source: 'io_mapping', display_name: '', io_id: '', io_type: '', operator: 'eq', value: '1', timer_secs: 0, timer_mode: 'pre' },
          ],
        },
      ],
    },
  },
  schedule: {
    cooldown_enabled: false,
    cooldown_minutes: 5,
    weekdays: [true, true, true, true, true, true, true],
    day_time_enabled: false,
    day_time_ranges: {},
  },
  notifications: {
    system_message: true,
    auto_hide: false,
    sound_alert: false,
    sound_file: 'default',
    sound_duration: 'once',
    push_notification: false,
    email_enabled: false,
    email_addresses: '',
    email_template_id: '',
    sms_enabled: false,
    sms_numbers: '',
    sms_template_id: '',
    change_device_card_color: false,
    device_card_color: '#ef4444',
    device_card_color_dark: '#5e2230',
    transition_card_color_mode: '5s',
    transition_card_color_seconds: 5,
  },
  actions: {
    webhook_enabled: false,
    webhook_url: '',
    webhook_method: 'POST',
    webhook_headers: [],
    webhook_payload_mode: 'auto',
    webhook_custom_payload: '',
    webhook_retry_count: 0,
    webhook_retry_delay_secs: 5,
    webhook_auth_type: 'none',
    webhook_auth_token: '',
    command_on_trigger_enabled: false,
    command_on_trigger: { source: 'custom', command_text: '', command_mode: 'single', command_sequence: [], delivery_method: 'gprs', device_command_id: '', template_id: '' },
    command_on_clear_enabled: false,
    command_on_clear: { source: 'custom', command_text: '', command_mode: 'single', command_sequence: [], delivery_method: 'gprs', device_command_id: '', template_id: '' },
  },
};

// Play test tone — same logic as Road Speed Alert in MoreFeaturesModal.jsx
const playTestTone = (toneId) => {
  const tone = ALERT_TONES.find(t => t.id === toneId);
  if (!tone || tone.freq === 0) return;
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const now = ctx.currentTime;
    if (toneId === 'urgent_siren') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = 'sawtooth'; gain.gain.value = 0.3;
      osc.frequency.setValueAtTime(900, now);
      osc.frequency.linearRampToValueAtTime(1400, now + 0.15);
      osc.frequency.linearRampToValueAtTime(900, now + 0.3);
      osc.frequency.linearRampToValueAtTime(1400, now + 0.45);
      osc.frequency.linearRampToValueAtTime(900, now + 0.6);
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.6);
      osc.start(now); osc.stop(now + 0.65);
    } else if (toneId === 'triple_beep') {
      for (let i = 0; i < 3; i++) {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = tone.freq; o.type = 'sine'; g.gain.value = 0.3;
        g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.18 + 0.12);
        o.start(now + i * 0.18); o.stop(now + i * 0.18 + 0.15);
      }
    } else if (toneId === 'continuous') {
      for (let i = 0; i < 3; i++) {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.frequency.value = tone.freq; o.type = 'square'; g.gain.value = 0.2;
        g.gain.exponentialRampToValueAtTime(0.001, now + i * 0.4 + 0.25);
        o.start(now + i * 0.4); o.stop(now + i * 0.4 + 0.3);
      }
    } else if (toneId === 'low_warning') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = tone.freq; osc.type = 'triangle'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + 0.5);
      osc.start(now); osc.stop(now + 0.55);
    } else if (toneId === 'alarm') {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = tone.freq; osc.type = 'sine'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + tone.duration / 1000);
      osc.start(now); osc.stop(now + tone.duration / 1000 + 0.05);
      const osc2 = ctx.createOscillator(); const gain2 = ctx.createGain();
      osc2.connect(gain2); gain2.connect(ctx.destination);
      osc2.frequency.value = tone.freq; osc2.type = 'sine'; gain2.gain.value = 0.3;
      gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.25 + tone.duration / 1000);
      osc2.start(now + 0.25); osc2.stop(now + 0.25 + tone.duration / 1000 + 0.05);
    } else {
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.frequency.value = tone.freq; osc.type = 'sine'; gain.gain.value = 0.3;
      gain.gain.exponentialRampToValueAtTime(0.001, now + tone.duration / 1000);
      osc.start(now); osc.stop(now + tone.duration / 1000 + 0.05);
    }
    setTimeout(() => ctx.close(), 2000);
  } catch { /* Audio not available */ }
};

// Reusable grouped item selector with search — resolves group IDs to names
const GroupedAssetSelector = ({ items, selectedIds, onToggle, nameKey, groupKey, placeholder, geofenceGroups = [], height = 'h-[120px]' }) => {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(new Set());

  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(i => (i[nameKey] || '').toLowerCase().includes(q));
  }, [items, search, nameKey]);

  // Resolve group ID to name using geofenceGroups lookup
  const resolveGroupName = useCallback((groupId) => {
    if (!groupId) return 'Ungrouped';
    const match = geofenceGroups.find(g => g.id === groupId);
    return match ? match.name : 'Ungrouped';
  }, [geofenceGroups]);

  const groups = useMemo(() => {
    const g = {};
    filtered.forEach(i => {
      const name = resolveGroupName(i[groupKey]);
      if (!g[name]) g[name] = [];
      g[name].push(i);
    });
    Object.values(g).forEach(arr => arr.sort((a, b) => (a[nameKey] || '').localeCompare(b[nameKey] || '')));
    return Object.entries(g).sort(([a], [b]) => {
      if (a === 'Ungrouped') return 1;
      if (b === 'Ungrouped') return -1;
      return a.localeCompare(b);
    });
  }, [filtered, groupKey, nameKey, resolveGroupName]);

  const toggleGroup = (groupItems) => {
    const ids = groupItems.map(i => i.id);
    const allSelected = ids.every(id => selectedIds.includes(id));
    if (allSelected) ids.forEach(id => onToggle(id));
    else ids.filter(id => !selectedIds.includes(id)).forEach(id => onToggle(id));
  };

  return (
    <div className="space-y-1">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={placeholder} className="pl-7 h-7 text-xs" />
      </div>
      <ScrollArea className={`${height} border rounded`}>
        {groups.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-3">No items found</p>
        ) : groups.map(([groupName, groupItems]) => {
          const allInGroup = groupItems.every(i => selectedIds.includes(i.id));
          const isCollapsed = collapsed.has(groupName);
          return (
            <div key={groupName}>
              <div className="flex items-center gap-1.5 px-2 py-1 bg-muted border-b cursor-pointer sticky top-0 z-10" onClick={() => setCollapsed(prev => { const n = new Set(prev); n.has(groupName) ? n.delete(groupName) : n.add(groupName); return n; })}>
                {isCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                <Checkbox checked={allInGroup} onCheckedChange={() => toggleGroup(groupItems)} onClick={(e) => e.stopPropagation()} />
                <span className="text-xs font-medium flex-1">{groupName}</span>
                <span className="text-[10px] text-muted-foreground">{groupItems.length}</span>
              </div>
              {!isCollapsed && groupItems.map(item => (
                <div key={item.id} className="flex items-center gap-2 py-0.5 px-3 hover:bg-muted/50 cursor-pointer" onClick={() => onToggle(item.id)}>
                  <Checkbox checked={selectedIds.includes(item.id)} />
                  <span className="text-xs">{item[nameKey]}</span>
                </div>
              ))}
            </div>
          );
        })}
      </ScrollArea>
    </div>
  );
};




// Module-level cache so tab switches (Event Log ↔ Event Rules) that remount
// this component don't flash a full-page spinner — cached rules render
// instantly while a background refetch updates them.
let rulesRemountCache = { rules: [], total: 0, loaded: false };

export const EventRulesManager = ({ devices = [] }) => {
  const { api } = useAuth();
  const isMobileOrWebview = useIsMobileOrWebview();
  const { isFeatureEnabled, isNotificationChannelActive, getNotificationDisabledReason } = useFeatureFlags();
  // Direct Consume from page-level AnalyticsContext so this state survives
  // sub-tab switches (Event Log ↔ Event Rules), page navigation, and F5 refresh.
  // EventRulesManager unmounts whenever the parent flips `eventsView !== 'rules'`,
  // so local useState would lose user-typed search and bulk selection.
  const {
    eventRulesSearchTerm: ruleSearchTerm,
    setEventRulesSearchTerm: setRuleSearchTerm,
    eventRulesSelectedIds: selectedRuleIds,
    setEventRulesSelectedIds: setSelectedRuleIds,
  } = useAnalytics();

  const [rules, setRules] = useState(rulesRemountCache.rules);
  const [rulesPage, setRulesPage] = useUrlPage('rules_page');
  const [rulesPageSize, setRulesPageSize] = useUrlPageSize('event-rules', 25, 'rules_size');
  const [rulesTotal, setRulesTotal] = useState(rulesRemountCache.total);
  const [inactiveRulesCount, setInactiveRulesCount] = useState(0);
  const [ruleStats, setRuleStats] = useState(null);
  const [ruleStatusFilter, setRuleStatusFilter] = useState('all');
  const [rulesSelectAllLoading, setRulesSelectAllLoading] = useState(false);
  // Only show the full-page spinner on the very first load — remounts (Event
  // Log ↔ Event Rules tab switches) render cached rules instantly, no flash.
  const [loading, setLoading] = useState(!rulesRemountCache.loaded);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [activeDialogTab, setActiveDialogTab] = useState('main');
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [mappedSourceCleanupNotice, setMappedSourceCleanupNotice] = useState(null);

  // Import/export state — intentionally local (resets on remount)
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importRules, setImportRules] = useState([]);
  const [importStep, setImportStep] = useState(1);
  const [importDeviceIds, setImportDeviceIds] = useState([]);
  const [importDisableRules, setImportDisableRules] = useState(true);
  const [importKeepSensorConfig, setImportKeepSensorConfig] = useState(true);
  const [importClearGeofenceRefs, setImportClearGeofenceRefs] = useState(true);
  const [importing, setImporting] = useState(false);
  const importFileInputRef = useRef(null);

  const [zones, setZones] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [places, setPlaces] = useState([]);
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [peopleGroups, setPeopleGroups] = useState([]);
  const [groupMgrOpen, setGroupMgrOpen] = useState(false);
  const [groupForm, setGroupForm] = useState(null); // {id?, name, person_ids}
  const [groupSaving, setGroupSaving] = useState(false);
  const [geofenceGroups, setGeofenceGroups] = useState([]);

  // Notification templates state
  const [notifTemplates, setNotifTemplates] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateInitialType, setTemplateInitialType] = useState('email');

  // CORRECT privilege checks — also respects user's personal notification settings
  const hasPushNotif = isNotificationChannelActive('notification_push');
  const hasEmailNotif = isNotificationChannelActive('notification_email');
  const hasSmsNotif = isNotificationChannelActive('notification_sms');
  const pushDisabledReason = getNotificationDisabledReason('notification_push');
  const emailDisabledReason = getNotificationDisabledReason('notification_email');
  const smsDisabledReason = getNotificationDisabledReason('notification_sms');
  const hasRoadSpeedAlert = isFeatureEnabled('speed_alert_config');
  const hasEventActions = isFeatureEnabled('event_webhook_commands');

  const debouncedRuleSearch = useDebouncedValue(ruleSearchTerm || '', 400);

  const fetchRules = useCallback(async () => {
    try {
      // Server-side search + pagination (X-Total-Count drives the pager).
      const params = new URLSearchParams();
      if (debouncedRuleSearch.trim()) params.set('search', debouncedRuleSearch.trim());
      if (ruleStatusFilter === 'enabled') params.set('enabled', 'true');
      else if (ruleStatusFilter === 'disabled') params.set('enabled', 'false');
      else if (ruleStatusFilter === 'needs_attention') params.set('needs_attention', 'true');
      else if (ruleStatusFilter === 'email_on') params.set('notif_channel', 'email');
      else if (ruleStatusFilter === 'sms_on') params.set('notif_channel', 'sms');
      else if (ruleStatusFilter === 'push_on') params.set('notif_channel', 'push');
      params.set('skip', String((rulesPage - 1) * rulesPageSize));
      params.set('limit', String(rulesPageSize));
      const res = await api.get(`/event-rules?${params.toString()}`);
      setRules(res.data);
      const total = parseInt(res.headers?.['x-total-count'], 10);
      const resolvedTotal = Number.isFinite(total) ? total : (res.data || []).length;
      setRulesTotal(resolvedTotal);
      const inactive = parseInt(res.headers?.['x-inactive-count'], 10);
      setInactiveRulesCount(Number.isFinite(inactive) ? inactive : 0);
      const scopeTotal = parseInt(res.headers?.['x-scope-total'], 10);
      const enabledCount = parseInt(res.headers?.['x-enabled-count'], 10);
      const disabledCount = parseInt(res.headers?.['x-disabled-count'], 10);
      if (Number.isFinite(scopeTotal)) {
        setRuleStats({ total: scopeTotal, enabled: enabledCount || 0, disabled: disabledCount || 0 });
      }
      rulesRemountCache = { rules: res.data, total: resolvedTotal, loaded: true };
    }
    catch (err) { console.error('Failed to fetch event rules:', err); }
    finally { setLoading(false); }
  }, [api, debouncedRuleSearch, ruleStatusFilter, rulesPage, rulesPageSize]);

  const fetchGeofenceAssets = useCallback(async () => {
    try {
      const [z, r, p, g, ppl, pg] = await Promise.all([
        api.get('/zones').catch(() => ({ data: [] })),
        api.get('/geo-routes').catch(() => ({ data: [] })),
        api.get('/places').catch(() => ({ data: [] })),
        api.get('/geofence-groups').catch(() => ({ data: [] })),
        api.get('/people/options').catch(() => ({ data: [] })),
        api.get('/people-groups').catch(() => ({ data: [] })),
      ]);
      setZones(z.data || []);
      setRoutes(r.data || []);
      setPlaces(p.data || []);
      setGeofenceGroups(g.data || []);
      setPeopleOptions(ppl.data || []);
      setPeopleGroups(pg.data || []);
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { fetchRules(); fetchGeofenceAssets(); }, [fetchRules, fetchGeofenceAssets]);

  const refreshPeopleGroups = useCallback(async () => {
    try { const res = await api.get('/people-groups'); setPeopleGroups(res.data || []); } catch { /* ignore */ }
  }, [api]);
  const saveGroup = async () => {
    if (!groupForm?.name?.trim()) { toast.error('Group name is required'); return; }
    setGroupSaving(true);
    try {
      const body = { name: groupForm.name.trim(), person_ids: groupForm.person_ids || [] };
      if (groupForm.id) await api.put(`/people-groups/${groupForm.id}`, body);
      else await api.post('/people-groups', body);
      toast.success('Group saved'); setGroupForm(null); refreshPeopleGroups();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save group'); }
    finally { setGroupSaving(false); }
  };
  const deleteGroup = async (id) => {
    try { await api.delete(`/people-groups/${id}`); toast.success('Group deleted'); refreshPeopleGroups(); }
    catch { toast.error('Failed to delete group'); }
  };

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await api.get('/event-notification-templates');
      setNotifTemplates(res.data.templates || []);
    } catch { /* ignore */ }
  }, [api]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // Personal ID beacons are people credentials, not trackable devices — never rule targets
  const trackableDevices = useMemo(
    () => devices.filter(d => !(d.device_type === 'beacon' && d.beacon_purpose === 'personal')),
    [devices]
  );

  const sortedDevices = useMemo(() => {
    const filtered = trackableDevices.filter(d => !deviceSearch || (deviceDisplayName(d) || '').toLowerCase().includes(deviceSearch.toLowerCase()));
    return filtered.sort((a, b) => (deviceDisplayName(a) || '').toLowerCase().localeCompare((deviceDisplayName(b) || '').toLowerCase()));
  }, [trackableDevices, deviceSearch]);

  // Asset beacons (trailer/equipment beacon devices) for the People & Assets event types
  const assetBeacons = useMemo(
    () => devices
      .filter(d => d.device_type === 'beacon' && (d.beacon_purpose || 'asset') === 'asset')
      .sort((a, b) => (deviceDisplayName(a) || '').toLowerCase().localeCompare((deviceDisplayName(b) || '').toLowerCase())),
    [devices]
  );
  const watchVehicles = useMemo(
    () => devices
      .filter(d => d.device_type !== 'beacon')
      .sort((a, b) => (deviceDisplayName(a) || '').toLowerCase().localeCompare((deviceDisplayName(b) || '').toLowerCase())),
    [devices]
  );

  const deviceGroups = useMemo(() => {
    const groups = {};
    sortedDevices.forEach(d => { const g = d.device_group || 'Ungrouped'; if (!groups[g]) groups[g] = []; groups[g].push(d); });
    return Object.entries(groups).sort(([a], [b]) => { if (a === 'Ungrouped') return 1; if (b === 'Ungrouped') return -1; return a.localeCompare(b); });
  }, [sortedDevices]);

  const [collapsedGroups, setCollapsedGroups] = useState(new Set());
  const isZoneTransitionEvent = form.event_type === 'zone_in' || form.event_type === 'zone_out';
  const isPlaceTransitionEvent = form.event_type === 'place_in' || form.event_type === 'place_out';
  const isRouteTransitionEvent = form.event_type === 'route_entering' || form.event_type === 'route_exiting';
  const isGeofenceTransitionEvent = GEOFENCE_TRANSITION_EVENT_TYPES.includes(form.event_type);
  const isSensorIOEvent = form.event_type === 'sensor_io_event';
  const isCustomSensorPreset = (form.config.template_key || 'custom') === 'custom';
  const isDeviceMetricSensorPreset = SENSOR_IO_DEVICE_METRIC_PRESETS.includes(form.config.template_key || '');
  const isConnectionEvent = CONNECTION_EVENT_TYPES.includes(form.event_type);
  const isProximityEvent = PROXIMITY_EVENT_TYPES.includes(form.event_type);
  const isHarshDrivingEvent = HARSH_DRIVING_EVENT_TYPES.includes(form.event_type);
  const isCrashDetectionEvent = CRASH_DETECTION_EVENT_TYPES.includes(form.event_type);
  const isVideoEvent = VIDEO_EVENT_TYPES.includes(form.event_type);
  const isDeviceEvent = DEVICE_EVENT_TYPES.includes(form.event_type);
  const isDriverBindingEvent = DRIVER_BINDING_EVENT_TYPES.includes(form.event_type);
  const isUnmatchedScanEvent = RFID_SCAN_ALERT_EVENT_TYPES.includes(form.event_type);
  const isAssetEvent = ASSET_EVENT_TYPES.includes(form.event_type);
  const isAssetWatchEvent = form.event_type === 'asset_movement_watch';
  const isAssetDropoffEvent = form.event_type === 'asset_dropoff';
  const isLeftBehindEvent = form.event_type === 'left_behind_asset';
  const isPersonPlaceEvent = form.event_type === 'person_arrival' || form.event_type === 'person_departure';
  const isOccupancyEvent = form.event_type === 'occupancy_departure';
  const isPersonBoundEvent = form.event_type === 'person_bound';
  const isOccupancyCountEvent = form.event_type === 'occupancy_count';
  const isBindingDrivenEvent = isDriverBindingEvent || isAssetEvent || isPersonPlaceEvent || isOccupancyEvent || isPersonBoundEvent || isOccupancyCountEvent || isUnmatchedScanEvent;
  const supportsAllDevicesToggle = !isSensorIOEvent && !isHarshDrivingEvent && !isCrashDetectionEvent && !isVideoEvent && !isDeviceEvent && !isProximityEvent;
  const supportsTransitionCardColorTiming = isGeofenceTransitionEvent || isConnectionEvent || isProximityEvent || isHarshDrivingEvent || isCrashDetectionEvent || isVideoEvent || isDeviceEvent || isDriverBindingEvent || isAssetEvent || isUnmatchedScanEvent || OPERATIONAL_TRANSITION_EVENT_TYPES.includes(form.event_type) || (isSensorIOEvent && form.config.sensor_mode === 'transition' && form.config.occurrence_behavior === 'instant');

  // Collect IO mappings from devices in the rule's scope (selected devices, or
  // ALL of the user's devices when All My Devices is on), deduped by display_name
  const ioSourceDevices = useMemo(
    () => (form.all_devices ? devices : devices.filter(d => form.device_ids.includes(d.id))),
    [devices, form.device_ids, form.all_devices]
  );
  const selectedDeviceIOMappings = useMemo(() => {
    if (ioSourceDevices.length === 0) return [];
    const seen = new Map();
    ioSourceDevices.forEach(d => {
      (d.io_mappings || []).forEach(m => {
        const displayName = m.display_name || m.io_type || m.io_id;
        if (displayName && !seen.has(displayName)) {
          seen.set(displayName, {
            display_name: displayName,
            io_id: m.io_id,
            io_type: m.io_type,
          });
        }
      });
    });
    return Array.from(seen.values()).sort((a, b) => a.display_name.localeCompare(b.display_name));
  }, [ioSourceDevices]);

  // Duplicate-mapping warnings for in-scope devices (same IO ID mapped 2+ times)
  const selectedDeviceDuplicateWarnings = useMemo(() => {
    const out = [];
    ioSourceDevices.forEach(d => {
      const byId = {};
      (d.io_mappings || []).forEach(m => {
        const k = String(m.io_id ?? '').trim();
        if (!k) return;
        (byId[k] = byId[k] || []).push(m.display_name || m.io_type || k);
      });
      Object.entries(byId).filter(([, n]) => n.length > 1).forEach(([ioId, names]) => {
        out.push({ device: d.name, ioId, names });
      });
    });
    return out;
  }, [ioSourceDevices]);

  const sensorPresetSourceMatches = useMemo(
    () => getSensorPresetSourceMatches(form.config.template_key || 'custom', selectedDeviceIOMappings),
    [form.config.template_key, selectedDeviceIOMappings]
  );

  const postTimersSupported = ['sensor_io_event', 'zone_in', 'zone_out', 'place_in', 'place_out'].includes(form.event_type);

  // Live "current value" snapshot for IO condition hints (first in-scope device)
  const [ioSnapshot, setIoSnapshot] = useState(null);
  useEffect(() => {
    const deviceId = form.device_ids[0] || (form.all_devices ? devices[0]?.id : undefined);
    if (!dialogOpen || !form.config.io_conditions_enabled || !deviceId) { setIoSnapshot(null); return; }
    let cancelled = false;
    const fetchSnap = async () => {
      try {
        const res = await api.get(`/event-rules/device-io-snapshot/${deviceId}`);
        if (!cancelled) setIoSnapshot(res.data);
      } catch { /* scope or transient error — hide hints */ }
    };
    fetchSnap();
    const t = setInterval(fetchSnap, 10000);
    return () => { cancelled = true; clearInterval(t); };
  }, [dialogOpen, form.config.io_conditions_enabled, form.device_ids, form.all_devices, devices, api]);

  const formatHeld = (secs) => {
    if (secs == null) return '';
    if (secs < 60) return `${Math.round(secs)}s`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${Math.round(secs % 60)}s`;
    return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`;
  };

  const openCreate = () => { setEditingRule(null); setMappedSourceCleanupNotice(null); setForm(JSON.parse(JSON.stringify(DEFAULT_FORM))); setActiveDialogTab('main'); setDeviceSearch(''); setDialogOpen(true); };

  const openEdit = (rule) => {
    setEditingRule(rule);
    setMappedSourceCleanupNotice(null);
    const cfg = { ...DEFAULT_FORM.config, ...(rule.config || {}) };
    // Legacy person_bound rules (entity_types + optional person_ids) → explicit staff/passenger lists
    if (rule.event_type === 'person_bound' && cfg.staff_ids === undefined && cfg.passenger_ids === undefined) {
      const legacyTypes = cfg.entity_types || ['staff', 'passenger'];
      const legacyIds = cfg.person_ids || [];
      const pick = (t) => legacyTypes.includes(t)
        ? peopleOptions.filter(p => p.person_type === t && (!legacyIds.length || legacyIds.includes(p.id))).map(p => p.id)
        : [];
      cfg.staff_ids = pick('staff');
      cfg.passenger_ids = pick('passenger');
    }
    setForm({
      name: rule.name || '', event_type: rule.event_type || 'overspeed_fixed', enabled: rule.enabled !== false, severity: rule.severity || 'warning', device_ids: rule.device_ids || [],
      all_devices: !!rule.all_devices,
      config: cfg,
      schedule: { ...DEFAULT_FORM.schedule, ...(rule.schedule || {}) },
      notifications: { ...DEFAULT_FORM.notifications, ...(rule.notifications || {}) },
      actions: { ...DEFAULT_FORM.actions, ...(rule.actions || {}),
        command_on_trigger: { ...DEFAULT_FORM.actions.command_on_trigger, ...((rule.actions || {}).command_on_trigger || {}) },
        command_on_clear: { ...DEFAULT_FORM.actions.command_on_clear, ...((rule.actions || {}).command_on_clear || {}) },
      },
    });
    setActiveDialogTab('main'); setDeviceSearch(''); setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Please enter a rule name'); return; }
    if (form.config.io_conditions_enabled) {
      const iocGroups = form.config.io_conditions?.groups || [];
      if (!iocGroups.length) { toast.error('IO Mapped Logic Condition is enabled but has no condition groups'); return; }
      for (const g of iocGroups) {
        if (!(g.conditions || []).length) { toast.error('An IO condition group has no conditions — add one or remove the group'); return; }
        for (const c of g.conditions) {
          if ((c.source || 'io_mapping') === 'io_mapping' && !c.io_id && !c.display_name) {
            toast.error('IO Mapped Logic Condition has an unconfigured row — select IO Mapped Data or remove it');
            return;
          }
        }
      }
    }
    if (isZoneTransitionEvent && !(form.config.monitored_zone_ids || []).length) {
      toast.error('Please select at least one monitored zone');
      return;
    }
    if (isPlaceTransitionEvent && !(form.config.monitored_place_ids || []).length) {
      toast.error('Please select at least one monitored place');
      return;
    }
    if (isRouteTransitionEvent && !(form.config.monitored_route_ids || []).length) {
      toast.error('Please select at least one monitored route');
      return;
    }
    if (isSensorIOEvent && form.device_ids.length === 0) {
      toast.error('Please select one or more devices for Sensor / IO events');
      return;
    }
    if (isAssetWatchEvent && !(form.config.watch_vehicle_ids || []).length) {
      toast.error('Please select at least one vehicle for the Asset Movement Watch list');
      return;
    }
    if (isPersonPlaceEvent && !((form.config.person_ids || []).length + (form.config.person_group_ids || []).length)) {
      toast.error('Please select at least one person or people group');
      return;
    }
    if ((isPersonPlaceEvent || isOccupancyEvent) && !((form.config.monitored_place_ids || []).length + (form.config.monitored_zone_ids || []).length + (form.config.monitored_route_ids || []).length)) {
      toast.error('Please select at least one Place, Zone or Route to monitor');
      return;
    }
    if (isAssetEvent && !(form.config.asset_ids || []).length) {
      toast.error('Please select at least one asset to watch');
      return;
    }
    if (isPersonBoundEvent && !((form.config.staff_ids || []).length + (form.config.passenger_ids || []).length + (form.config.group_ids || []).length)) {
      toast.error('Please select at least one staff member, passenger or group');
      return;
    }
    if (isOccupancyCountEvent) {
      if (!(parseInt(form.config.occupancy_threshold, 10) > 0)) {
        toast.error('Please enter an occupancy limit (1 or more)');
        return;
      }
      if (!(form.config.count_entities || ['driver', 'staff', 'passenger']).length) {
        toast.error('Please tick at least one category to count');
        return;
      }
    }
    if (isSensorIOEvent && !(form.config.sensor_sources || []).length) {
      toast.error('Please select at least one mapped source for this Sensor / IO event');
      return;
    }
    if (isSensorIOEvent && form.config.sensor_mode === 'threshold' && (form.config.group_operator || 'single') !== 'single') {
      toast.error('Threshold sensor events currently support a single source only');
      return;
    }
    if (isSensorIOEvent && form.config.sensor_mode === 'transition' && !(form.config.trigger_definition?.transition?.value ?? '').toString().trim()) {
      toast.error('Please enter a Trigger Value for this Sensor / IO transition rule');
      return;
    }
    if (isSensorIOEvent && form.config.sensor_mode === 'threshold' && !(form.config.trigger_definition?.threshold?.value ?? '').toString().trim()) {
      toast.error('Please enter a Threshold Value for this Sensor / IO rule');
      return;
    }
    if (form.event_type === 'excessive_idle' && (!form.config.idle_duration_secs || form.config.idle_duration_secs <= 0)) {
      toast.error('Please enter an idle duration for Excessive Idling');
      return;
    }
    if (form.event_type === 'underspeed' && (!form.config.speed_limit_kmh || form.config.speed_limit_kmh <= 0)) {
      toast.error('Please enter an underspeed threshold');
      return;
    }
    if (isProximityEvent) {
      const pm = form.config.pair_mode || 'any_pair';
      if ((pm === 'any_pair' || pm === 'group_proximity') && form.device_ids.length < 2) {
        toast.error(`Please select at least 2 devices for "${pm === 'group_proximity' ? 'Group Proximity' : 'Any Pair'}" mode`);
        return;
      }
      if (pm === 'specific_pairs' && !(form.config.device_pairs || []).length) {
        toast.error('Please add at least one device pair');
        return;
      }
      if (pm === 'target_watch') {
        if (!form.config.target_device_id) {
          toast.error('Please select a target device');
          return;
        }
        if (!(form.config.watch_device_ids || []).length) {
          toast.error('Please select at least one watch device');
          return;
        }
      }
    }
    if (isHarshDrivingEvent) {
      if (form.device_ids.length === 0) {
        toast.error('Please select one or more devices for Harsh Driving');
        return;
      }
      if (!(form.config.sensor_sources || []).length) {
        toast.error('Please select a mapped IO source for Harsh Driving');
        return;
      }
      const hc = form.config;
      const hasAny = [hc.acceleration_values, hc.braking_values, hc.cornering_values, hc.cornering_left_values, hc.cornering_right_values]
        .some(arr => Array.isArray(arr) && arr.length > 0);
      if (!hasAny) {
        // Fallback check for legacy single-value format
        const hasLegacy = [hc.acceleration_value, hc.braking_value, hc.cornering_value, hc.cornering_left_value, hc.cornering_right_value]
          .some(v => v !== null && v !== undefined && String(v).trim() !== '' && String(v).trim().toLowerCase() !== 'disabled');
        if (!hasLegacy) {
          toast.error('Please set at least one trigger value (Acceleration, Braking, or Cornering)');
          return;
        }
      }
    }
    if (isCrashDetectionEvent) {
      if (form.device_ids.length === 0) {
        toast.error('Please select one or more devices for Crash Detection');
        return;
      }
      if (!(form.config.sensor_sources || []).length) {
        toast.error('Please select a mapped IO source for Crash Detection');
        return;
      }
      if (!(form.config.trigger_values || []).length) {
        toast.error('Please configure at least one crash trigger value');
        return;
      }
    }
    if (isVideoEvent) {
      if (form.device_ids.length === 0) {
        toast.error('Please select one or more devices for Video Event');
        return;
      }
      if (!(form.config.sensor_sources || []).length) {
        toast.error('Please select a mapped IO source for Video Event');
        return;
      }
      if (!(form.config.trigger_values || []).length) {
        toast.error('Please select at least one video event trigger');
        return;
      }
    }
    if (isDeviceEvent) {
      if (form.device_ids.length === 0) {
        toast.error('Please select one or more devices for Device Event');
        return;
      }
      if (!(form.config.sensor_sources || []).length) {
        toast.error('Please select a mapped IO source for Device Event');
        return;
      }
      if (!(form.config.trigger_values || []).length) {
        toast.error('Please select at least one device event trigger');
        return;
      }
      const videoCodes = (form.config.trigger_values || []).filter((code) => VIDEO_EXCLUSIVE_CODES.has(String(code)));
      if (videoCodes.length > 0) {
        toast.error('Device Event cannot include video event trigger codes');
        return;
      }
    }
    setSaving(true);
    try {
      if (editingRule) { await api.put(`/event-rules/${editingRule.id}`, form); toast.success('Event rule updated'); }
      else { await api.post('/event-rules', form); toast.success('Event rule created'); }
      setDialogOpen(false); fetchRules();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save event rule'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (ruleId) => {
    try {
      await api.delete(`/event-rules/${ruleId}`);
      toast.success('Event rule deleted');
      setDeleteConfirm(null);
      // Clear the deleted id from any selection so the "N selected" chip
      // stays accurate without needing a full page reload.
      setSelectedRuleIds(prev => prev.filter(id => id !== ruleId));
      // If we just emptied the current page and we're past page 1, walk back
      // one page — otherwise the list flashes empty until the clamp effect
      // eventually snaps to page 1 (losing the user's context).
      if (rules.length === 1 && rulesPage > 1) setRulesPage(rulesPage - 1);
      else fetchRules();
    }
    catch { toast.error('Failed to delete event rule'); }
  };

  const handleToggleEnabled = async (rule) => {
    try { await api.put(`/event-rules/${rule.id}`, { enabled: !rule.enabled }); fetchRules(); }
    catch { toast.error('Failed to toggle rule'); }
  };

  // --- Rows come pre-filtered / paged from the server ---
  const filteredRules = rules;
  const paginatedRules = rules;
  // Reset to page 1 when search / page-size changes (skips mount so ?rules_page= deep-links survive).
  useSkipFirstEffect(() => { setRulesPage(1); }, [debouncedRuleSearch, ruleStatusFilter, rulesPageSize]);
  // Clamp page if the result set shrinks below the current page (only once
  // the real total is known — clamping against the pre-fetch 0 would wipe
  // ?rules_page= deep-links).
  useEffect(() => {
    if (rulesTotal <= 0) return;
    const total = Math.max(1, Math.ceil(rulesTotal / rulesPageSize));
    if (rulesPage > total) setRulesPage(1);
  }, [rulesTotal, rulesPage, rulesPageSize, setRulesPage]);

  // --- Select all / toggle selection ---
  // Header checkbox = current page only (matches Event Log / Driver Corrections).
  // Cross-page selection lives on the "Select All" button in the selection bar.
  const pageRuleIds = filteredRules.map(r => r.id);
  const bulkSetChannel = async (channel, enabled) => {
    try {
      const res = await api.put('/event-rules-bulk/notifications', { rule_ids: selectedRuleIds, channel, enabled });
      toast.success(`${channel === 'email' ? 'Email' : 'SMS'} turned ${enabled ? 'on' : 'off'} for ${res.data.updated} rule${res.data.updated === 1 ? '' : 's'}`);
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update notifications');
    }
  };

  const bulkSetSeverity = async (severity) => {
    try {
      const res = await api.put('/event-rules-bulk/severity', { rule_ids: selectedRuleIds, severity });
      toast.success(`Severity set to ${severity} for ${res.data.updated} rule${res.data.updated === 1 ? '' : 's'}`);
      fetchRules();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update severity');
    }
  };

  const allPageSelected = filteredRules.length > 0 && pageRuleIds.every(id => selectedRuleIds.includes(id));
  const somePageSelected = pageRuleIds.some(id => selectedRuleIds.includes(id));
  const allAcrossPagesSelected = rulesTotal > 0 && selectedRuleIds.length >= rulesTotal;

  const togglePageSelection = (checked) => {
    setSelectedRuleIds(prev => checked
      ? Array.from(new Set([...prev, ...pageRuleIds]))
      : prev.filter(id => !pageRuleIds.includes(id)));
  };

  const toggleSelectAllAcrossPages = async () => {
    // Selection-bar button: unchecked → select EVERY rule matching the current search
    // (all pages, ids fetched server-side); checked → clear the whole selection.
    if (allAcrossPagesSelected) {
      setSelectedRuleIds([]);
      return;
    }
    setRulesSelectAllLoading(true);
    try {
      const params = new URLSearchParams();
      if (debouncedRuleSearch.trim()) params.set('search', debouncedRuleSearch.trim());
      params.set('ids_only', 'true');
      const res = await api.get(`/event-rules?${params.toString()}`);
      setSelectedRuleIds(Array.isArray(res.data?.ids) ? res.data.ids : []);
    } catch (err) {
      toast.error('Failed to select all rules');
    } finally {
      setRulesSelectAllLoading(false);
    }
  };

  const toggleSelectRule = (ruleId) => {
    setSelectedRuleIds(prev => prev.includes(ruleId) ? prev.filter(id => id !== ruleId) : [...prev, ruleId]);
  };

  const cloneJson = (value) => JSON.parse(JSON.stringify(value || {}));

  const removeAccountSpecificConfigRefs = (config = {}) => {
    const next = cloneJson(config);
    [
      'monitored_zone_ids', 'monitored_place_ids', 'monitored_route_ids',
      'zone_ids', 'exclude_zone_ids', 'place_ids', 'exclude_place_ids', 'route_ids', 'exclude_route_ids',
      'target_device_id', 'watch_device_ids', 'device_pairs',
      '_pair_a', '_pair_b', '_pair_a_search', '_pair_b_search', '_target_search', '_watch_search',
    ].forEach((key) => { delete next[key]; });
    return next;
  };

  const buildRuleTemplate = (rule) => {
    const config = cloneJson(rule.config || {});
    return {
      template_type: 'event_rule_template',
      name: rule.name || 'Event Rule Template',
      event_type: rule.event_type || 'overspeed_fixed',
      severity: rule.severity || 'warning',
      enabled: rule.enabled !== false,
      config,
      schedule: cloneJson(rule.schedule || {}),
      notifications: cloneJson(rule.notifications || {}),
      actions: cloneJson(rule.actions || {}),
      template_dependencies: {
        requires_device_remap: (rule.device_ids || []).length > 0 || ['sensor_io_event', 'harsh_driving', 'crash_detection', 'video_event', 'device_event'].includes(rule.event_type),
        exported_device_count: (rule.device_ids || []).length,
        sensor_sources: config.sensor_sources || [],
        io_conditions_enabled: !!config.io_conditions_enabled,
        io_conditions: config.io_conditions || null,
        geofence_refs: {
          monitored_zone_ids: config.monitored_zone_ids || [],
          monitored_place_ids: config.monitored_place_ids || [],
          monitored_route_ids: config.monitored_route_ids || [],
          zone_ids: config.zone_ids || [],
          place_ids: config.place_ids || [],
          route_ids: config.route_ids || [],
          exclude_zone_ids: config.exclude_zone_ids || [],
          exclude_place_ids: config.exclude_place_ids || [],
          exclude_route_ids: config.exclude_route_ids || [],
        },
        trigger_values: config.trigger_values || [],
        command_actions_enabled: !!(rule.actions?.command_on_trigger_enabled || rule.actions?.command_on_clear_enabled),
      },
    };
  };

  // --- Export ---
  const handleExport = async () => {
    // Fetch ALL rules matching the current search (no skip/limit = full set) so
    // exports aren't limited to the visible page.
    let toExport = [];
    try {
      const params = new URLSearchParams();
      if (debouncedRuleSearch.trim()) params.set('search', debouncedRuleSearch.trim());
      const res = await api.get(`/event-rules?${params.toString()}`);
      const all = Array.isArray(res.data) ? res.data : [];
      toExport = selectedRuleIds.length > 0 ? all.filter(r => selectedRuleIds.includes(r.id)) : all;
    } catch (err) {
      toast.error('Failed to fetch rules for export');
      return;
    }
    const exportData = toExport.map(buildRuleTemplate);
    const blob = new Blob([JSON.stringify({
      version: 2,
      export_type: 'event_rule_templates',
      exported_at: new Date().toISOString(),
      notes: 'Tenant-safe templates. Device assignments and owner data are excluded; sensor/IO configuration, schedules, notifications and actions are included for remapping during import.',
      rules: exportData,
    }, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `event-rules-export-${getFileTimestamp()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${exportData.length} rule${exportData.length !== 1 ? 's' : ''}`);
  };

  // --- Import file picker ---
  const handleImportFile = () => {
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
      importFileInputRef.current.click();
    }
  };

  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target.result);
        if (!parsed || parsed.version !== 2 || parsed.export_type !== 'event_rule_templates') {
          toast.error('Invalid template file — expected Event Rule Templates v2 export');
          return;
        }
        const rulesList = parsed.rules;
        if (!Array.isArray(rulesList) || rulesList.length === 0) {
          toast.error('Invalid format — expected a rules array with at least one rule');
          return;
        }
        if (rulesList.some(rule => rule.template_type && rule.template_type !== 'event_rule_template')) {
          toast.error('Invalid template file — unsupported rule template type');
          return;
        }
        setImportRules(rulesList.map((rule) => ({ ...rule, config: rule.config || {}, schedule: rule.schedule || {}, notifications: rule.notifications || {}, actions: rule.actions || {} })));
        setImportStep(1);
        setImportDeviceIds([]);
        setImportDisableRules(true);
        setImportKeepSensorConfig(true);
        setImportClearGeofenceRefs(true);
        setImportModalOpen(true);
      } catch { toast.error('Failed to parse JSON file'); }
    };
    reader.readAsText(file);
  };

  // --- Import confirm ---
  const handleImportConfirm = async () => {
    if (importDeviceIds.length === 0) {
      toast.error('Please select at least one current-account device before importing');
      setImportStep(2);
      return;
    }
    setImporting(true);
    let success = 0, failed = 0;
    for (const rule of importRules) {
      try {
        const importedConfig = importClearGeofenceRefs ? removeAccountSpecificConfigRefs(rule.config || {}) : cloneJson(rule.config || {});
        if (!importKeepSensorConfig) {
          importedConfig.sensor_sources = [];
          importedConfig.io_conditions_enabled = false;
          importedConfig.io_conditions = cloneJson(DEFAULT_FORM.config.io_conditions);
        }
        const payload = {
          name: rule.name || 'Imported Rule',
          event_type: rule.event_type || 'overspeed_fixed',
          enabled: importDisableRules ? false : (rule.enabled !== undefined ? rule.enabled : true),
          severity: rule.severity || 'warning',
          device_ids: importDeviceIds,
          device_group_ids: [],
          config: importedConfig,
          schedule: rule.schedule || {},
          notifications: rule.notifications || {},
          actions: rule.actions || {},
        };
        await api.post('/event-rules', payload);
        success++;
      } catch { failed++; }
    }
    setImporting(false);
    setImportModalOpen(false);
    setImportRules([]);
    setImportStep(1);
    setImportDeviceIds([]);
    fetchRules();
    if (failed === 0) toast.success(`Imported ${success} rule${success !== 1 ? 's' : ''}`);
    else toast.warning(`Imported ${success}, failed ${failed}`);
  };

  // --- Duplicate ---
  // Shared "⋮" overflow actions menu (same style as People / Shared Evidence).
  const renderRuleActions = (rule, suffix = '') => {
    const itemCls = isMobileOrWebview ? 'min-h-[52px] text-sm px-3 py-3' : '';
    const iconCls = isMobileOrWebview ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2';
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline" className={isMobileOrWebview ? 'h-9 w-9 p-0' : 'h-8 w-8 p-0'} title="Actions" data-testid={`rule-actions-menu${suffix}-${rule.id}`}>
            <MoreVertical className={isMobileOrWebview ? '!w-5 !h-5' : 'w-4 h-4'} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={isMobileOrWebview ? 'w-64 p-1.5' : 'w-48'}>
          <DropdownMenuItem className={itemCls} onSelect={() => openEdit(rule)} data-testid={`edit-rule${suffix}-${rule.id}`}>
            <Pencil className={iconCls} /> Edit
          </DropdownMenuItem>
          <DropdownMenuItem className={itemCls} onSelect={() => handleDuplicate(rule)} data-testid={`duplicate-rule${suffix}-${rule.id}`}>
            <Copy className={iconCls} /> Clone
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className={`${itemCls} text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400`} onSelect={() => setDeleteConfirm(rule)} data-testid={`delete-rule${suffix}-${rule.id}`}>
            <Trash2 className={iconCls} /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const handleDuplicate = (rule) => {
    setEditingRule(null);
    setMappedSourceCleanupNotice(null);
    setForm({
      name: `${rule.name} (Copy)`,
      event_type: rule.event_type || 'overspeed_fixed',
      enabled: rule.enabled !== false,
      severity: rule.severity || 'warning',
      device_ids: rule.device_ids || [],
      all_devices: !!rule.all_devices,
      device_group_ids: rule.device_group_ids || [],
      config: { ...DEFAULT_FORM.config, ...(rule.config || {}) },
      schedule: { ...DEFAULT_FORM.schedule, ...(rule.schedule || {}) },
      notifications: { ...DEFAULT_FORM.notifications, ...(rule.notifications || {}) },
      actions: { ...DEFAULT_FORM.actions, ...(rule.actions || {}),
        command_on_trigger: { ...DEFAULT_FORM.actions.command_on_trigger, ...((rule.actions || {}).command_on_trigger || {}) },
        command_on_clear: { ...DEFAULT_FORM.actions.command_on_clear, ...((rule.actions || {}).command_on_clear || {}) },
      },
    });
    setActiveDialogTab('main');
    setDeviceSearch('');
    setDialogOpen(true);
  };

  const updateConfig = (key, value) => setForm(prev => ({ ...prev, config: { ...prev.config, [key]: value } }));
  const updateSchedule = (key, value) => setForm(prev => ({ ...prev, schedule: { ...prev.schedule, [key]: value } }));
  const updateNotifications = (key, value) => setForm(prev => ({ ...prev, notifications: { ...prev.notifications, [key]: value } }));
  const updateActions = (key, value) => setForm(prev => ({ ...prev, actions: { ...prev.actions, [key]: value } }));
  const updateCommandConfig = (phase, key, value) => setForm(prev => ({
    ...prev, actions: { ...prev.actions, [phase]: { ...(prev.actions[phase] || {}), [key]: value } }
  }));
  const toggleWeekday = (idx) => setForm(prev => { const days = [...(prev.schedule.weekdays || Array(7).fill(true))]; days[idx] = !days[idx]; return { ...prev, schedule: { ...prev.schedule, weekdays: days } }; });
  const applySensorPreset = useCallback((presetKey) => setForm(prev => {
    const presetConfig = buildSensorPresetConfig(presetKey, selectedDeviceIOMappings);
    return {
      ...prev,
      name: (!prev.name || SENSOR_IO_PRESET_LABELS[prev.config.template_key] === prev.name)
        ? (SENSOR_IO_PRESET_LABELS[presetKey] || prev.name)
        : prev.name,
      config: {
        ...prev.config,
        ...presetConfig,
      },
    };
  }), [selectedDeviceIOMappings]);
  const handleCustomSensorModeChange = useCallback((mode) => setForm(prev => ({
    ...prev,
    config: {
      ...prev.config,
      sensor_mode: mode,
      value_mode: mode === 'threshold' ? 'numeric' : 'boolean',
      group_operator: mode === 'threshold' ? 'single' : (prev.config.group_operator || 'single'),
      occurrence_behavior: mode === 'threshold' ? 'active_until_clear' : 'instant',
      trigger_definition: {
        transition: {
          operator: prev.config.trigger_definition?.transition?.operator || 'eq',
          value: prev.config.trigger_definition?.transition?.value ?? '',
          direction: prev.config.trigger_definition?.transition?.direction || 'false_to_true',
        },
        threshold: {
          operator: prev.config.trigger_definition?.threshold?.operator || 'gt',
          value: prev.config.trigger_definition?.threshold?.value ?? '',
          clear_margin: prev.config.trigger_definition?.threshold?.clear_margin ?? '',
        },
      },
    },
  })), []);
  const handleEventTypeChange = (eventType) => {
    setMappedSourceCleanupNotice(null);
    const isRfidScanAlert = RFID_SCAN_ALERT_EVENT_TYPES.includes(eventType);
    const wasRfidScanAlert = RFID_SCAN_ALERT_EVENT_TYPES.includes(form.event_type);
    setForm(prev => ({
      ...prev,
      name: eventType === 'sensor_io_event' && !prev.name ? 'Custom' : prev.name,
      event_type: eventType,
      // RFID scan alerts default to a 60-minute cooldown (editable).
      schedule: isRfidScanAlert && !wasRfidScanAlert
        ? { ...prev.schedule, cooldown_enabled: true, cooldown_minutes: 60 }
        : prev.schedule,
      config: {
        ...buildOperationalEventConfig(eventType, prev.config),
        ...(CONNECTION_EVENT_TYPES.includes(eventType)
          ? buildConnectionEventConfig(eventType, prev.config)
          : {}),
        ...(PROXIMITY_EVENT_TYPES.includes(eventType)
          ? buildProximityEventConfig(eventType, prev.config)
          : {}),
        trigger_duration_secs: GEOFENCE_TRANSITION_EVENT_TYPES.includes(eventType)
          ? 0
          : CONNECTION_EVENT_TYPES.includes(eventType)
            ? (CONNECTION_EVENT_DEFAULTS[eventType] ?? prev.config.trigger_duration_secs)
            : PROXIMITY_EVENT_TYPES.includes(eventType)
              ? 0
              : (GEOFENCE_TRANSITION_EVENT_TYPES.includes(prev.event_type) ? 10 : prev.config.trigger_duration_secs),
        ...(eventType === 'sensor_io_event' && prev.event_type !== 'sensor_io_event'
          ? buildSensorPresetConfig('custom', selectedDeviceIOMappings)
          : {}),
        ...(eventType === 'harsh_driving' && prev.event_type !== 'harsh_driving'
          ? { sensor_sources: [], acceleration_values: [], braking_values: [], cornering_values: [], cornering_left_values: [], cornering_right_values: [] }
          : {}),
        ...(eventType === 'crash_detection' && prev.event_type !== 'crash_detection'
          ? { sensor_sources: [], trigger_values: [] }
          : {}),
        ...(eventType === 'video_event' && prev.event_type !== 'video_event'
          ? { sensor_sources: [], trigger_values: [] }
          : {}),
        ...(eventType === 'device_event' && prev.event_type !== 'device_event'
          ? { sensor_sources: [], trigger_values: [] }
          : {}),
      },
      notifications: {
        ...prev.notifications,
        transition_card_color_mode: TRANSITION_CARD_COLOR_EVENT_TYPES.includes(eventType)
          ? (prev.notifications.transition_card_color_mode || '5s')
          : (prev.notifications.transition_card_color_mode || '5s'),
        transition_card_color_seconds: TRANSITION_CARD_COLOR_EVENT_TYPES.includes(eventType)
          ? Math.max(1, parseInt(prev.notifications.transition_card_color_seconds, 10) || 5)
          : Math.max(1, parseInt(prev.notifications.transition_card_color_seconds, 10) || 5),
      },
    }));
  };

  const toggleDevice = (deviceId) => {
    setMappedSourceCleanupNotice(null);
    setForm(prev => {
    const ids = prev.device_ids.includes(deviceId) ? prev.device_ids.filter(id => id !== deviceId) : [...prev.device_ids, deviceId];
    return { ...prev, device_ids: ids };
    });
  };

  const toggleGroupDevices = (groupDevices) => {
    setMappedSourceCleanupNotice(null);
    setForm(prev => {
    const groupIds = groupDevices.map(d => d.id);
    const allSelected = groupIds.every(id => prev.device_ids.includes(id));
    const newIds = allSelected ? prev.device_ids.filter(id => !groupIds.includes(id)) : [...new Set([...prev.device_ids, ...groupIds])];
    return { ...prev, device_ids: newIds };
    });
  };

  const toggleAssetId = (field, id) => setForm(prev => {
    const arr = prev.config[field] || [];
    const next = arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
    return { ...prev, config: { ...prev.config, [field]: next } };
  });

  const toggleSensorSource = (option) => {
    setMappedSourceCleanupNotice(null);
    setForm(prev => {
    const key = `${option.display_name}::${option.io_type || ''}`;
    const existing = prev.config.sensor_sources || [];
    const exists = existing.some(src => `${src.match_value}::${src.io_type || ''}` === key);
    const groupOp = prev.config.group_operator || 'single';

    let nextSources;
    if (exists) {
      // Deselect
      nextSources = existing.filter(src => `${src.match_value}::${src.io_type || ''}` !== key);
    } else if (groupOp === 'single') {
      // Single source mode — replace current selection
      nextSources = [toSensorSourceDef(option)];
    } else {
      // Multi-select (any/all) — add to list
      nextSources = [...existing, toSensorSourceDef(option)];
    }
    return { ...prev, config: { ...prev.config, sensor_sources: nextSources } };
    });
  };

  const resetSensorSources = useCallback(() => {
    setMappedSourceCleanupNotice(null);
    setForm(prev => ({
      ...prev,
      config: {
        ...prev.config,
        sensor_sources: [],
      },
    }));
  }, []);

  useEffect(() => {
    if (!isSensorIOEvent) return;
    const availableKeys = new Set(selectedDeviceIOMappings.map((option) => `${option.display_name}::${option.io_type || ''}`));
    setForm(prev => {
      const currentSources = prev.config.sensor_sources || [];
      if (!currentSources.length) return prev;
      const nextSources = currentSources.filter((src) => src.selection_mode === 'device_metric' || availableKeys.has(`${src.match_value}::${src.io_type || ''}`));
      if (nextSources.length === currentSources.length) return prev;
      const removedCount = currentSources.length - nextSources.length;
      setMappedSourceCleanupNotice(`${removedCount} unavailable mapped source${removedCount === 1 ? '' : 's'} ${removedCount === 1 ? 'was' : 'were'} removed because ${removedCount === 1 ? 'it is' : 'they are'} not present on the selected devices.`);
      return {
        ...prev,
        config: {
          ...prev.config,
          sensor_sources: nextSources,
        },
      };
    });
  }, [isSensorIOEvent, selectedDeviceIOMappings]);

  // Also prune stale sources for harsh_driving, crash_detection, video_event, and device_event
  useEffect(() => {
    if (!isHarshDrivingEvent && !isCrashDetectionEvent && !isVideoEvent && !isDeviceEvent) return;
    const availableKeys = new Set(selectedDeviceIOMappings.map((option) => `${option.display_name}::${option.io_type || ''}`));
    setForm(prev => {
      const currentSources = prev.config.sensor_sources || [];
      if (!currentSources.length) return prev;
      const nextSources = currentSources.filter((src) => availableKeys.has(`${src.match_value}::${src.io_type || ''}`));
      if (nextSources.length === currentSources.length) return prev;
      return { ...prev, config: { ...prev.config, sensor_sources: nextSources } };
    });
  }, [isHarshDrivingEvent, isCrashDetectionEvent, isVideoEvent, isDeviceEvent, selectedDeviceIOMappings]);

  // Auto-suggest sources for harsh_driving, crash_detection, video_event, and device_event when sources are empty
  useEffect(() => {
    if (!isHarshDrivingEvent && !isCrashDetectionEvent && !isVideoEvent && !isDeviceEvent) return;
    if ((form.config.sensor_sources || []).length > 0) return;
    if (!selectedDeviceIOMappings.length) return;
    const suggested = isHarshDrivingEvent
      ? getHarshDrivingSuggestedSources(selectedDeviceIOMappings)
      : isCrashDetectionEvent
        ? getCrashDetectionSuggestedSources(selectedDeviceIOMappings)
        : isVideoEvent
          ? getVideoEventSuggestedSources(selectedDeviceIOMappings)
          : getDeviceEventSuggestedSources(selectedDeviceIOMappings);
    if (!suggested.length) return;
    const nextSources = suggested.map(o => ({ selection_mode: 'mapped_io', match_value: o.display_name, display_label: o.display_name, io_type: o.io_type || '' }));
    setForm(prev => {
      if ((prev.config.sensor_sources || []).length > 0) return prev;
      return { ...prev, config: { ...prev.config, sensor_sources: nextSources } };
    });
  }, [isHarshDrivingEvent, isCrashDetectionEvent, isVideoEvent, isDeviceEvent, selectedDeviceIOMappings, form.config.sensor_sources]);

  useEffect(() => {
    if (!isSensorIOEvent) return;
    if ((form.config.template_key || 'custom') === 'custom') return;
    if ((form.config.sensor_sources || []).length > 0) return;
    if (!selectedDeviceIOMappings.length) return;
    const presetConfig = buildSensorPresetConfig(form.config.template_key || 'custom', selectedDeviceIOMappings);
    if (!(presetConfig.sensor_sources || []).length) return;
    setForm(prev => {
      const prevSources = prev.config.sensor_sources || [];
      const nextSources = presetConfig.sensor_sources || [];
      const isSame = prevSources.length === nextSources.length && prevSources.every((src, idx) => (
        src.match_value === nextSources[idx]?.match_value &&
        (src.io_type || '') === (nextSources[idx]?.io_type || '')
      ));
      if (isSame) return prev;
      return {
        ...prev,
        config: {
          ...prev.config,
          ...presetConfig,
        },
      };
    });
  }, [isSensorIOEvent, form.config.template_key, form.config.sensor_sources, selectedDeviceIOMappings]);

  if (loading) return <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" /></div>;

  return (
    <div className="space-y-4" data-testid="event-rules-manager">
      <input
        ref={importFileInputRef}
        type="file"
        accept=".json,application/json"
        className="hidden"
        onChange={handleImportFileChange}
        data-testid="event-rule-template-file-input"
      />
      {/* Title row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Settings className="w-5 h-5 text-foreground" />
          <h3 className="text-lg font-semibold">Event Rules</h3>
        </div>
      </div>

      {/* Rule stats — clickable cards drive the Status dropdown (like Maintenance). */}
      {ruleStats && (() => {
        const toggle = (v) => setRuleStatusFilter(ruleStatusFilter === v ? 'all' : v);
        return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="event-rules-summary">
          <button type="button" className="text-left w-full h-full" onClick={() => setRuleStatusFilter('all')} data-testid="event-rules-stat-total">
            <Card className="h-full transition-shadow hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Rules</p>
                    <p className="text-2xl font-bold" data-testid="event-rules-summary-total">{ruleStats.total}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
          <button type="button" className="text-left w-full h-full" onClick={() => toggle('enabled')} data-testid="event-rules-stat-enabled">
            <Card className={`h-full transition-shadow hover:shadow-md ${ruleStatusFilter === 'enabled' ? 'ring-2 ring-[#2563eb]' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Enabled</p>
                    <p className="text-2xl font-bold" data-testid="event-rules-summary-enabled">{ruleStats.enabled}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                    <Power className="w-5 h-5 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
          <button type="button" className="text-left w-full h-full" onClick={() => toggle('disabled')} data-testid="event-rules-stat-disabled">
            <Card className={`h-full transition-shadow hover:shadow-md ${ruleStatusFilter === 'disabled' ? 'ring-2 ring-[#2563eb]' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Disabled</p>
                    <p className="text-2xl font-bold" data-testid="event-rules-summary-disabled">{ruleStats.disabled}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                    <PowerOff className="w-5 h-5 text-slate-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
          <button type="button" className="text-left w-full h-full" onClick={() => toggle('needs_attention')} data-testid="event-rules-stat-inactive">
            <Card className={`h-full transition-shadow hover:shadow-md ${ruleStatusFilter === 'needs_attention' ? 'ring-2 ring-[#2563eb]' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Needs Attention</p>
                    <p className="text-2xl font-bold" data-testid="event-rules-summary-inactive">{inactiveRulesCount}</p>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </button>
        </div>
        );
      })()}

      {/* Full-width controls row — search on its own row on phones, buttons
          expand to equal widths below it. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full sm:w-auto sm:flex-1 sm:min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, type, severity..."
            value={ruleSearchTerm}
            onChange={(e) => setRuleSearchTerm(e.target.value)}
            className="h-8 pl-10 pr-8"
            data-testid="search-event-rules"
          />
          {ruleSearchTerm && (
            <button
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setRuleSearchTerm('')}
              data-testid="search-event-rules-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={ruleStatusFilter} onValueChange={setRuleStatusFilter}>
          <SelectTrigger className="w-full sm:w-40 h-8" data-testid="event-rules-status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="enabled">Enabled</SelectItem>
            <SelectItem value="disabled">Disabled</SelectItem>
            <SelectItem value="needs_attention">Needs Attention</SelectItem>
            <SelectItem value="email_on">Email On</SelectItem>
            <SelectItem value="sms_on">SMS On</SelectItem>
            <SelectItem value="push_on">Push On</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex w-full sm:w-auto items-center gap-2 flex-wrap">
          <EventRulesHelpPanel triggerTestId="event-rules-help-btn-header" triggerClassName="flex-1 sm:flex-none gap-1.5" />
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={handleImportFile} data-testid="import-rules-btn">
            <Download className="w-4 h-4 mr-2" />
            Import
          </Button>
          <Button variant="outline" size="sm" className="flex-1 sm:flex-none" onClick={handleExport} disabled={selectedRuleIds.length === 0} data-testid="export-rules-btn">
            <Upload className="w-4 h-4 mr-2" />
            Export{selectedRuleIds.length > 0 ? ` (${selectedRuleIds.length})` : ''}
          </Button>
          <Button onClick={openCreate} className="flex-1 sm:flex-none bg-[#2563eb] hover:bg-[#1d4ed8]" size="sm" data-testid="create-event-rule-btn"><Plus className="w-4 h-4 mr-2" />Create Rule</Button>
        </div>
      </div>

      {inactiveRulesCount > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-sm text-amber-700 dark:text-amber-400" data-testid="inactive-rules-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            <span className="font-semibold">{inactiveRulesCount} enabled rule{inactiveRulesCount === 1 ? '' : 's'}</span> {inactiveRulesCount === 1 ? 'has' : 'have'} no devices selected and {inactiveRulesCount === 1 ? 'is' : 'are'} <span className="font-semibold">INACTIVE</span> — open each rule (shown as &quot;No devices — inactive&quot;) and select devices or All My Devices.
          </span>
        </div>
      )}
      <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid="event-rules-selection-bar">
          <span data-testid="event-rules-selection-count">{selectedRuleIds.length} selected</span>
{" "}
                  {selectedRuleIds.length > 0 && (<>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            data-testid="event-rules-select-all-toggle"
            onClick={toggleSelectAllAcrossPages}
          >
            {allAcrossPagesSelected ? 'Deselect All' : 'Select All'}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-xs"
            onClick={() => setSelectedRuleIds([])}
            data-testid="event-rules-selection-clear"
          >
            Clear
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" data-testid="event-rules-bulk-notifications-btn">
                <Bell className="w-3.5 h-3.5 mr-1" />
                Notifications
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-44">
              <DropdownMenuItem onClick={() => bulkSetChannel('email', true)} data-testid="bulk-email-on">
                <Mail className="w-4 h-4 mr-2 text-emerald-600" /> Email On
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkSetChannel('email', false)} data-testid="bulk-email-off">
                <Mail className="w-4 h-4 mr-2 text-slate-400" /> Email Off
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => bulkSetChannel('sms', true)} data-testid="bulk-sms-on">
                <Smartphone className="w-4 h-4 mr-2 text-violet-600" /> SMS On
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => bulkSetChannel('sms', false)} data-testid="bulk-sms-off">
                <Smartphone className="w-4 h-4 mr-2 text-slate-400" /> SMS Off
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" data-testid="event-rules-bulk-severity-btn">
                <AlertTriangle className="w-3.5 h-3.5 mr-1" />
                Severity
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-40">
              {SEVERITY_OPTIONS.map(opt => (
                <DropdownMenuItem key={opt.value} onClick={() => bulkSetSeverity(opt.value)} data-testid={`bulk-severity-${opt.value}`}>
                  <Badge variant="outline" className={`${opt.color} text-[10px] mr-2`}>{opt.label}</Badge>
                  Set {opt.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </>)}
                  </div>

      {rulesTotal === 0 && !debouncedRuleSearch && ruleStatusFilter === 'all' ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Settings className="w-12 h-12 mx-auto mb-4 opacity-50" /><p>No event rules configured</p></CardContent></Card>
      ) : filteredRules.length === 0 ? (
        <Card><CardContent className="py-8 text-center text-muted-foreground"><Search className="w-8 h-8 mx-auto mb-3 opacity-50" /><p>No rules match {ruleSearchTerm ? `"${ruleSearchTerm}"` : 'the current filter'}</p></CardContent></Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            {/* Horizontal scroll wrapper keeps the layout mobile-webview
                friendly: the table can scroll sideways on narrow screens
                without breaking the surrounding page layout. */}
            {/* Mobile (<640px): compact card list — every action (toggle,
                clone, edit, delete) reachable without sideways scrolling. */}
            <div className="sm:hidden divide-y" data-testid="event-rules-mobile-list">
              {paginatedRules.map(rule => {
                const typeDef = EVENT_TYPES.find(t => t.value === rule.event_type);
                const TypeIcon = typeDef?.icon || Zap;
                const severityDef = SEVERITY_OPTIONS.find(s => s.value === rule.severity);
                const isSelected = selectedRuleIds.includes(rule.id);
                return (
                  <div key={rule.id} className={`p-3 space-y-2.5 ${isSelected ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`} data-testid={`event-rule-mobile-${rule.id}`}>
                    <div className="flex items-start gap-2.5">
                      <Checkbox className="mt-1" checked={isSelected} onCheckedChange={() => toggleSelectRule(rule.id)} data-testid={`select-rule-mobile-${rule.id}`} />
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${rule.enabled ? 'bg-[#2563eb]/10' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <TypeIcon className={`w-4 h-4 ${rule.enabled ? 'text-[#2563eb]' : 'text-slate-400'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-medium text-sm break-words">{rule.name}</span>
                          {!rule.enabled && <Badge variant="secondary" className="text-[10px]">Disabled</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">{typeDef?.label || rule.event_type}{getRuleConfigSummary(rule)}</p>
                      </div>
                      <Switch checked={rule.enabled} onCheckedChange={() => handleToggleEnabled(rule)} data-testid={`toggle-rule-mobile-${rule.id}`} />
                    </div>
                    <div className="flex items-center justify-between gap-2 pl-7">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground min-w-0">
                        <Badge variant="outline" className={`text-[10px] ${severityDef?.color || ''}`}>{rule.severity}</Badge>
                        <span data-testid={`device-scope-mobile-${rule.id}`}>{getDeviceScopeLabel(rule)}</span>
                        <span className="tabular-nums">· {rule.trigger_count || 0} triggered</span>
                      </div>
                      <div className="inline-flex items-center gap-1 shrink-0">
                        {renderRuleActions(rule, '-mobile')}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="hidden sm:block overflow-x-auto">
              <Table className="min-w-[820px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={allPageSelected}
                        ref={(el) => { if (el) el.dataset.indeterminate = somePageSelected && !allPageSelected; }}
                        onCheckedChange={togglePageSelection}
                        data-testid="select-all-rules-checkbox"
                        aria-label="Select all rules on this page"
                      />
                    </TableHead>
                    <TableHead>Rule</TableHead>
                    <TableHead className="w-28">Severity</TableHead>
                    <TableHead className="w-36">Devices</TableHead>
                    <TableHead className="w-28">Notifications</TableHead>
                    <TableHead className="w-28 text-right">Triggered</TableHead>
                    <TableHead className="w-24 text-center">Enabled</TableHead>
                    <TableHead className="w-32 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRules.map(rule => {
                    const typeDef = EVENT_TYPES.find(t => t.value === rule.event_type);
                    const TypeIcon = typeDef?.icon || Zap;
                    const severityDef = SEVERITY_OPTIONS.find(s => s.value === rule.severity);
                    const isSelected = selectedRuleIds.includes(rule.id);
                    return (
                      <TableRow
                        key={rule.id}
                        data-testid={`event-rule-${rule.id}`}
                        className={`hover:bg-muted/50 ${isSelected ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelectRule(rule.id)}
                            data-testid={`select-rule-${rule.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-3 min-w-0">
                            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${rule.enabled ? 'bg-[#2563eb]/10' : 'bg-slate-100 dark:bg-slate-800'}`}>
                              <TypeIcon className={`w-4 h-4 ${rule.enabled ? 'text-[#2563eb]' : 'text-slate-400'}`} />
                            </div>
                            <div className="min-w-0">
                              <div className="flex flex-wrap items-center gap-2">
                                <span className="font-medium text-sm">{rule.name}</span>
                                {!rule.enabled && <Badge variant="secondary" className="text-[10px]">Disabled</Badge>}
                              </div>
                              <p className="text-xs text-muted-foreground truncate max-w-[420px]">
                                {typeDef?.label || rule.event_type}{getRuleConfigSummary(rule)}
                              </p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`text-[10px] ${severityDef?.color || ''}`}>{rule.severity}</Badge>
                        </TableCell>
                        <TableCell>
                          <span className="text-xs text-muted-foreground" data-testid={`device-scope-${rule.id}`}>
                            {getDeviceScopeLabel(rule)}
                          </span>
                        </TableCell>
                        <TableCell>
                          <RuleNotificationIcons rule={rule} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
                          {rule.trigger_count || 0}
                        </TableCell>                        <TableCell className="text-center">
                          <Switch checked={rule.enabled} onCheckedChange={() => handleToggleEnabled(rule)} data-testid={`toggle-rule-${rule.id}`} />
                        </TableCell>
                        <TableCell className="text-right">
                          {renderRuleActions(rule)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {/* Pagination footer INSIDE the Card, at the bottom of the table
                (matches Shared Evidence layout). */}
            <PaginationFooter
              page={rulesPage}
              setPage={setRulesPage}
              totalItems={rulesTotal}
              itemsPerPage={rulesPageSize}
              setItemsPerPage={setRulesPageSize}
              testIdPrefix="event-rules"
              alwaysShow
            />
          </CardContent>
        </Card>
      )}

      {/* ===== CREATE/EDIT DIALOG — matches User Management sizing ===== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="touch-dialog max-w-4xl h-[85vh] p-0 flex flex-col">
          <div className="px-6 pt-6 pb-0">
            <DialogHeader>
              <div className="flex items-center justify-between gap-2 pr-6">
                <DialogTitle>{editingRule ? 'Edit Event Rule' : 'Create Event Rule'}</DialogTitle>
                <EventRulesHelpPanel />
              </div>
              <DialogDescription>Configure event detection parameters</DialogDescription>
            </DialogHeader>
          </div>

          <Tabs value={activeDialogTab} onValueChange={setActiveDialogTab} className="flex-1 flex flex-col overflow-hidden">
            <div className="px-6 pt-3">
              <TabsList className={`w-full grid ${hasEventActions ? 'grid-cols-4' : 'grid-cols-3'}`}>
                <TabsTrigger value="main">Main</TabsTrigger>
                <TabsTrigger value="time">Time</TabsTrigger>
                <TabsTrigger value="notifications">Notifications</TabsTrigger>
                {hasEventActions && <TabsTrigger value="actions">Actions</TabsTrigger>}
              </TabsList>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">

              {/* ===== MAIN TAB ===== */}
              <TabsContent value="main" className="space-y-4 mt-0" forceMount={activeDialogTab === 'main' ? undefined : true} hidden={activeDialogTab !== 'main'}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2 flex items-center justify-between p-3 rounded-lg bg-muted">
                    <div><p className="font-medium text-sm">Active</p><p className="text-xs text-muted-foreground">Rule will evaluate incoming GPS data</p></div>
                    <Switch checked={form.enabled} onCheckedChange={(v) => setForm(prev => ({ ...prev, enabled: v }))} data-testid="rule-enabled-switch" />
                  </div>
                  <div className="sm:col-span-2 space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g., Highway Speed Limit" data-testid="rule-name-input" /></div>
                  <div className="space-y-2"><Label>Type</Label>
                    <Select value={form.event_type} onValueChange={handleEventTypeChange}><SelectTrigger data-testid="rule-type-select"><span className="truncate">{EVENT_TYPES.find(t => t.value === form.event_type)?.label || 'Select type'}</span></SelectTrigger><SelectContent>{EVENT_TYPES.map(t => {
                      const isGated = t.value === 'overspeed_road_sign' && !hasRoadSpeedAlert;
                      return <SelectItem key={t.value} value={t.value} disabled={t.disabled || isGated} data-testid={`rule-type-option-${t.value}`}>
                        <span className="flex flex-col items-start text-left">
                          <span>{t.label}{t.disabled ? ' (Coming Soon)' : ''}{isGated ? ' (Privilege Required)' : ''}</span>
                          {t.description && <span className="text-[10px] text-muted-foreground leading-tight max-w-[320px] whitespace-normal">{t.description}</span>}
                        </span>
                      </SelectItem>;
                    })}</SelectContent></Select>
                  </div>
                  <div className="space-y-2"><Label>Severity</Label>
                    <Select value={form.severity} onValueChange={(v) => setForm(prev => ({ ...prev, severity: v }))}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{SEVERITY_OPTIONS.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}</SelectContent></Select>
                  </div>
                </div>

                {/* Devices */}
                {!(isProximityEvent && ['specific_pairs', 'target_watch'].includes(form.config.pair_mode)) && !isAssetWatchEvent && !isPersonPlaceEvent && (
                <div className="space-y-2 p-4 rounded-lg border bg-muted/30" data-testid="devices-selection-card">
                  <div className="flex items-center justify-between">
                    <Label>Devices</Label>
                    {form.device_ids.length > 0 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 gap-1 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                        onClick={() => setForm(prev => ({ ...prev, device_ids: [] }))}
                        data-testid="clear-all-devices-btn"
                      >
                        <X className="w-3.5 h-3.5" /> Clear all
                      </Button>
                    )}
                  </div>
                  {supportsAllDevicesToggle && (
                    <div className="flex items-center justify-between gap-2 p-2 rounded-md border bg-background">
                      <div>
                        <p className="text-xs font-medium">All My Devices</p>
                        <p className="text-[10px] text-muted-foreground">Apply this rule to every device in your account, including devices added later</p>
                      </div>
                      <Switch
                        checked={!!form.all_devices}
                        onCheckedChange={(v) => setForm(prev => ({ ...prev, all_devices: !!v, device_ids: v ? [] : prev.device_ids }))}
                        data-testid="all-devices-toggle"
                      />
                    </div>
                  )}
                  {form.all_devices ? (
                    <p className="text-xs text-muted-foreground" data-testid="all-devices-active-note">Applies to ALL devices in your account</p>
                  ) : form.device_ids.length === 0 ? (
                    <div className="p-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-xs text-amber-700 dark:text-amber-400" data-testid="no-devices-inactive-warning">
                      No devices selected — this rule is <span className="font-semibold">INACTIVE</span> and will not trigger until you select devices{supportsAllDevicesToggle ? ' or enable All My Devices' : ''}.
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">{form.device_ids.length} device(s) selected</p>
                  )}
                  {!form.all_devices && (
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                    <Input value={deviceSearch} onChange={(e) => setDeviceSearch(e.target.value)} placeholder="Search devices..." className="pl-8 h-8 text-sm" data-testid="device-search-input" />
                  </div>
                  )}
                  {!form.all_devices && (
                  <ScrollArea className="h-[330px] border rounded-lg">
                    {deviceGroups.map(([groupName, groupDevices]) => {
                      const allInGroup = groupDevices.every(d => form.device_ids.includes(d.id));
                      const isCollapsed = collapsedGroups.has(groupName);
                      return (
                        <div key={groupName}>
                          <div className="flex items-center gap-1.5 px-2 py-1.5 bg-muted border-b sticky top-0 z-10 cursor-pointer" onClick={() => setCollapsedGroups(prev => { const n = new Set(prev); n.has(groupName) ? n.delete(groupName) : n.add(groupName); return n; })}>
                            {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                            <Checkbox checked={allInGroup} onCheckedChange={() => toggleGroupDevices(groupDevices)} onClick={(e) => e.stopPropagation()} />
                            <span className="text-xs font-medium flex-1">{groupName}</span>
                            <span className="text-xs text-muted-foreground">{groupDevices.length}</span>
                          </div>
                          {!isCollapsed && groupDevices.map(device => (
                            <div key={device.id} className="flex items-center gap-2 py-1 px-3 hover:bg-muted/50 cursor-pointer" onClick={() => toggleDevice(device.id)}>
                              <Checkbox checked={form.device_ids.includes(device.id)} />
                              <span className="text-xs">{deviceDisplayName(device)}</span>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </ScrollArea>
                  )}
                </div>
                )}

                {/* People & Assets configs */}
                {isDriverBindingEvent && (
                  <div className="p-3 rounded-lg border bg-muted/30 text-xs text-muted-foreground" data-testid="driver-event-info">
                    {form.event_type === 'driver_bound'
                      ? 'Fires every time a driver is bound to one of the selected vehicles (BLE beacon confirmation, RFID / iButton scan, or ballot resolution).'
                      : 'Fires only when the active driver of a selected vehicle CHANGES from one person to another. First bind of a trip does not trigger.'}
                  </div>
                )}
                {isUnmatchedScanEvent && (
                  <div className="p-3 rounded-lg border bg-muted/30 text-xs text-muted-foreground space-y-2" data-testid="unmatched-scan-event-info">
                    <p>
                      {form.event_type === 'disabled_rfid_scan' ? (
                        <>Fires the moment a card or iButton that is <span className="font-medium text-foreground">disabled in your card list</span> is
                        scanned in one of the selected vehicles. Re-enable or remove the card in People &gt; RFID / iButton Cards.</>
                      ) : form.event_type === 'unallocated_rfid_scan' ? (
                        <>Fires the moment a card or iButton that is <span className="font-medium text-foreground">in your card list but not allocated to a person</span> is
                        scanned in one of the selected vehicles. Allocate the card in People &gt; RFID / iButton Cards to stop alerts for it.</>
                      ) : (
                        <>Fires the moment a card or iButton that is <span className="font-medium text-foreground">not registered to your account</span> is
                        scanned in one of the selected vehicles. Register the card in People &gt; RFID / iButton Cards to stop alerts for that credential.</>
                      )}
                    </p>
                    <p>Cooldown (default 60 minutes per card, per vehicle) is adjustable in the <span className="font-medium text-foreground">Time</span> tab.</p>
                  </div>
                )}
                {isAssetEvent && (
                  <div className="space-y-2 p-4 rounded-lg border bg-muted/30" data-testid="asset-picker-card">
                    <p className="text-[10px] text-muted-foreground">
                      Trailer / equipment beacons. Only ticked assets trigger this rule.
                      {isLeftBehindEvent && <span className="block text-amber-600 dark:text-amber-400 mt-1">Only assets with a Home vehicle set (Fleet &gt; edit asset) can trigger this event.</span>}
                    </p>
                    {!(form.config.asset_ids || []).length && (
                      <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="no-assets-selected-warning">No assets selected — this rule will not trigger until you select at least one asset.</p>
                    )}
                    <PickList
                      label="Assets to watch"
                      items={assetBeacons.map(a => ({ id: a.id, label: deviceDisplayName(a) }))}
                      selectedIds={form.config.asset_ids || []}
                      onChange={(ids) => updateConfig('asset_ids', ids)}
                      testIdPrefix="rule-assets"
                      height={160}
                      emptyNote="No asset beacons found in your account — register trailer/equipment beacons first."
                    />
                  </div>
                )}
                {isAssetEvent && (form.event_type === 'asset_in_transit' || form.event_type === 'asset_movement_watch') && (
                  <div className="space-y-2 p-4 rounded-lg border bg-muted/30" data-testid="moving-delay-card">
                    <Label>Movement Trigger Delay</Label>
                    <p className="text-[10px] text-muted-foreground">
                      Fires only after the vehicle has actually been underway (moving) for this many seconds — ignition-only tests and idling never trigger. 0 = fire on first movement.
                    </p>
                    <div className="flex items-center gap-2">
                      <NumberInput min="0" max="600" className="w-32" data-testid="moving-delay-input"
                        value={form.config.moving_delay_seconds ?? 60}
                        onValueChange={(n) => updateConfig('moving_delay_seconds', n)} />
                      <span className="text-xs text-muted-foreground">seconds after moving starts (default 60)</span>
                    </div>
                  </div>
                )}
                {isAssetWatchEvent && (
                  <div className="space-y-2 p-4 rounded-lg border bg-muted/30" data-testid="watch-config-card">
                    <Label>Vehicle list mode</Label>
                    <Select value={form.config.watch_mode || 'authorized'} onValueChange={(v) => updateConfig('watch_mode', v)}>
                      <SelectTrigger data-testid="watch-mode-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="authorized">Alert in any vehicle EXCEPT the list (authorized vehicles)</SelectItem>
                        <SelectItem value="unauthorized">Alert ONLY in vehicles on the list (watch list)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">
                      {(form.config.watch_mode || 'authorized') === 'authorized'
                        ? 'The listed vehicles are allowed to carry the asset — anything else moving it fires the event.'
                        : 'The event fires only when the asset travels in one of the listed vehicles.'}
                    </p>
                    <PickList
                      label="Vehicles"
                      items={watchVehicles.map(d => ({ id: d.id, label: deviceDisplayName(d) }))}
                      selectedIds={form.config.watch_vehicle_ids || []}
                      onChange={(ids) => updateConfig('watch_vehicle_ids', ids)}
                      testIdPrefix="rule-watch-vehicles"
                      height={160}
                      emptyNote="No vehicles found in your account."
                    />
                  </div>
                )}
                {(isAssetDropoffEvent || isLeftBehindEvent) && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="dropoff-config-card">
                    <h4 className="font-medium text-sm">{isLeftBehindEvent ? 'Departure Grace' : 'Drop-off Confirmation Grace'}</h4>
                    <p className="text-[10px] text-muted-foreground">
                      {isLeftBehindEvent
                        ? 'The alert fires once the home vehicle has departed beyond this grace without the asset re-detected — a slow re-detection at trip start never alerts.'
                        : 'The drop-off fires once the vehicle has departed beyond this grace — a re-detected asset within the grace never alerts.'}
                    </p>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Minutes into next trip</Label>
                        <Input type="number" min="0" value={form.config.grace_minutes ?? 3} onChange={(e) => updateConfig('grace_minutes', Math.max(0, parseFloat(e.target.value) || 0))} data-testid="dropoff-grace-minutes" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">OR metres from drop point</Label>
                        <NumberInput min="0" value={form.config.grace_meters ?? 500} onValueChange={(n) => updateConfig('grace_meters', n)} emptyValue={500} data-testid="dropoff-grace-meters" />
                      </div>
                    </div>
                    {isAssetDropoffEvent && (
                      <div className="space-y-2 pt-2 border-t" data-testid="removal-while-parked-block">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label className="text-xs">Alert on removal while parked</Label>
                            <p className="text-[10px] text-muted-foreground">Also fire when the asset disappears from a PARKED vehicle (possible removal/theft) — no departure needed. Cancelled if the asset re-binds anywhere first.</p>
                          </div>
                          <Switch checked={!!form.config.removal_while_parked} onCheckedChange={(v) => updateConfig('removal_while_parked', v)} data-testid="removal-while-parked-toggle" />
                        </div>
                        {!!form.config.removal_while_parked && (
                          <div className="flex items-center gap-2">
                            <Input type="number" min="0" max="60" className="w-28" data-testid="removal-confirm-minutes"
                              value={form.config.removal_confirm_minutes ?? 5}
                              onChange={(e) => updateConfig('removal_confirm_minutes', Math.max(0, Math.min(60, parseFloat(e.target.value) || 0)))} />
                            <span className="text-xs text-muted-foreground">minutes confirmation delay (default 5)</span>
                          </div>
                        )}
                        <div className="flex items-center justify-between pt-2 border-t">
                          <div>
                            <Label className="text-xs">Notify when the asset re-attaches</Label>
                            <p className="text-[10px] text-muted-foreground">After a drop-off has ALERTED, send an all-clear if the asset re-binds to the same vehicle within the same parked timeline — e.g. "Tool A re-attached to 00001 — earlier drop-off resolved". A new trip starts a new timeline.</p>
                          </div>
                          <Switch checked={!!form.config.notify_reattach} onCheckedChange={(v) => updateConfig('notify_reattach', v)} data-testid="notify-reattach-toggle" />
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(isPersonPlaceEvent || isOccupancyEvent) && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="person-place-config-card">
                    {isPersonPlaceEvent && (
                      <div className="space-y-2">
                        <p className="text-[10px] text-muted-foreground">A person's position is the vehicle currently carrying them (beacon / RFID / driver session).</p>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <PickList
                            label="People to monitor"
                            items={peopleOptions.map(p => ({ id: p.id, label: p.name, meta: p.person_type }))}
                            selectedIds={form.config.person_ids || []}
                            onChange={(ids) => updateConfig('person_ids', ids)}
                            testIdPrefix="rule-people"
                            height={150}
                            emptyNote="No people found — add drivers, staff or passengers on the People page first."
                          />
                          <div className="space-y-1">
                            <PickList
                              label="People Groups"
                              items={peopleGroups.map(gp => ({ id: gp.id, label: gp.name, meta: `${(gp.person_ids || []).length} member(s)` }))}
                              selectedIds={form.config.person_group_ids || []}
                              onChange={(ids) => updateConfig('person_group_ids', ids)}
                              testIdPrefix="rule-people-groups"
                              height={150}
                              emptyNote="No groups yet — create one with Manage groups below."
                            />
                            <button type="button" className="text-[10px] text-primary hover:underline" onClick={() => setGroupMgrOpen(true)} data-testid="manage-people-groups-btn">Manage groups</button>
                            <p className="text-[10px] text-muted-foreground">Groups are dynamic — people added to a group later automatically apply to this rule.</p>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="space-y-3">
                      {isOccupancyEvent && <p className="text-[10px] text-muted-foreground">Fires when a scoped vehicle LEAVES one of the monitored areas while occupied — the event lists everyone and everything on board.</p>}
                      {isOccupancyEvent && (
                        <div className="space-y-1 pt-1">
                          <Label className="text-xs">Roster grace period (seconds)</Label>
                          <NumberInput min="0" max="600" className="w-32" value={form.config.roster_grace_secs ?? 60}
                            onValueChange={(n) => updateConfig('roster_grace_secs', n)}
                            data-testid="config-roster-grace-secs" />
                          <p className="text-[10px] text-muted-foreground">Wait this long after the vehicle exits before taking the on-board snapshot — catches beacons and the driver decision still confirming at departure. 0 = snapshot immediately. If the vehicle returns within the grace, no event fires.</p>
                        </div>
                      )}
                      <Label>Monitored Areas</Label>
                      <p className="text-[10px] text-muted-foreground -mt-1">Mix Places (point + radius), Zones (drawn shapes for irregular properties) and Routes (corridor). For Routes: {isPersonPlaceEvent ? '"arrival" = joined the route, "departure" = left the route' : 'the event fires when the occupied vehicle leaves the route corridor'} — joining a route needs 2 consecutive engine checks so briefly crossing a route never counts.</p>
                      {!((form.config.monitored_place_ids || []).length + (form.config.monitored_zone_ids || []).length + (form.config.monitored_route_ids || []).length) && (
                        <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="no-areas-selected-warning">No areas selected — this rule will not trigger until you select at least one Place, Zone or Route.</p>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <PickList
                          label="Places"
                          items={places.map(pl => ({ id: pl.id, label: pl.place_name || pl.name }))}
                          selectedIds={form.config.monitored_place_ids || []}
                          onChange={(ids) => updateConfig('monitored_place_ids', ids)}
                          testIdPrefix="rule-places"
                          height={150}
                          emptyNote="No Places found — create Places on the Geofencing page first."
                        />
                        <PickList
                          label="Zones"
                          items={zones.map(z => ({ id: z.id, label: z.zone_name || z.name }))}
                          selectedIds={form.config.monitored_zone_ids || []}
                          onChange={(ids) => updateConfig('monitored_zone_ids', ids)}
                          testIdPrefix="rule-zones"
                          height={150}
                          emptyNote="No Zones found — draw Zones on the Geofencing page first."
                        />
                        <PickList
                          label="Routes"
                          items={routes.map(rt => ({ id: rt.id, label: rt.route_name || rt.name }))}
                          selectedIds={form.config.monitored_route_ids || []}
                          onChange={(ids) => updateConfig('monitored_route_ids', ids)}
                          testIdPrefix="rule-routes"
                          height={150}
                          emptyNote="No Routes found — create Routes on the Geofencing page first."
                        />
                      </div>
                    </div>
                  </div>
                )}

                {isPersonBoundEvent && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="person-bound-config-card">
                    <Label>Who should trigger this?</Label>
                    <p className="text-[10px] text-muted-foreground">Only the ticked staff, passengers and group members fire this rule when they board a scoped vehicle. Groups are dynamic — new members automatically apply.</p>
                    {!((form.config.staff_ids || []).length + (form.config.passenger_ids || []).length + (form.config.group_ids || []).length) && (
                      <p className="text-xs text-amber-600 dark:text-amber-400" data-testid="person-bound-empty-warning">Nobody selected — this rule will not trigger until you select at least one staff member, passenger or group.</p>
                    )}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <PickList
                        label="Staff"
                        items={peopleOptions.filter(p => p.person_type === 'staff').map(p => ({ id: p.id, label: p.name }))}
                        selectedIds={form.config.staff_ids || []}
                        onChange={(ids) => updateConfig('staff_ids', ids)}
                        testIdPrefix="rule-staff"
                        height={130}
                        emptyNote="No staff found — add staff on the People page first."
                      />
                      <PickList
                        label="Passengers"
                        items={peopleOptions.filter(p => p.person_type === 'passenger').map(p => ({ id: p.id, label: p.name }))}
                        selectedIds={form.config.passenger_ids || []}
                        onChange={(ids) => updateConfig('passenger_ids', ids)}
                        testIdPrefix="rule-passengers"
                        height={130}
                        emptyNote="No passengers found — add passengers on the People page first."
                      />
                      <div className="space-y-1">
                        <PickList
                          label="People Groups"
                          items={peopleGroups.map(gp => ({ id: gp.id, label: gp.name, meta: `${(gp.person_ids || []).length} member(s)` }))}
                          selectedIds={form.config.group_ids || []}
                          onChange={(ids) => updateConfig('group_ids', ids)}
                          testIdPrefix="rule-person-groups"
                          height={130}
                          emptyNote="No groups yet — create one with Manage groups below."
                        />
                        <button type="button" className="text-[10px] text-primary hover:underline" onClick={() => setGroupMgrOpen(true)} data-testid="manage-person-groups-btn">Manage groups</button>
                      </div>
                    </div>
                  </div>
                )}

                {isOccupancyCountEvent && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="occupancy-count-config-card">
                    <Label>Occupancy limit</Label>
                    <p className="text-[10px] text-muted-foreground">Fires once when the number of people on board crosses your limit, then re-arms when the count is back within the limit. The alert lists everyone counted.</p>
                    <div className="flex items-center gap-3 flex-wrap">
                      <Select value={form.config.count_mode || 'at_or_above'} onValueChange={(v) => updateConfig('count_mode', v)}>
                        <SelectTrigger className="w-40" data-testid="occupancy-count-mode"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="at_or_above">At or above</SelectItem>
                          <SelectItem value="at_or_below">At or below</SelectItem>
                        </SelectContent>
                      </Select>
                      <Input type="number" min="1" max="200" className="w-24" value={form.config.occupancy_threshold ?? ''} placeholder="e.g. 8"
                        onChange={(e) => updateConfig('occupancy_threshold', parseInt(e.target.value) || '')}
                        data-testid="occupancy-count-threshold" />
                      <span className="text-xs text-muted-foreground">people on board</span>
                    </div>
                    <Label className="text-xs">Who counts</Label>
                    <div className="flex gap-4">
                      {['driver', 'staff', 'passenger'].map(et => (
                        <label key={et} className="flex items-center gap-2 text-xs cursor-pointer capitalize" data-testid={`occupancy-count-entity-${et}`}>
                          <Checkbox
                            checked={(form.config.count_entities || ['driver', 'staff', 'passenger']).includes(et)}
                            onCheckedChange={(checked) => {
                              const cur = new Set(form.config.count_entities || ['driver', 'staff', 'passenger']);
                              checked ? cur.add(et) : cur.delete(et);
                              updateConfig('count_entities', Array.from(cur));
                            }}
                          />
                          {et}s
                        </label>
                      ))}
                    </div>
                    <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="occupancy-count-only-on-trip">
                      <Checkbox checked={!!form.config.only_on_trip} onCheckedChange={(v) => updateConfig('only_on_trip', !!v)} />
                      Only while the vehicle is on a trip (recommended for "At or below" — avoids alerts from empty parked vehicles)
                    </label>
                  </div>
                )}

                {/* Speed configs */}
                {form.event_type === 'overspeed_fixed' && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                    <h4 className="font-medium text-sm">Fixed Speed Limit Configuration</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1"><Label className="text-xs">Speed Limit (km/h)</Label><NumberInput min="0" value={form.config.speed_limit_kmh ?? ''} onValueChange={(n) => updateConfig('speed_limit_kmh', n)} data-testid="config-speed-limit" /></div>
                      <DurationInput value={form.config.trigger_duration_secs ?? 0} onChange={(v) => updateConfig('trigger_duration_secs', v)} label="Trigger Duration" helpText="Must exceed for this long" testId="config-trigger-duration" />
                      <div className="space-y-1"><Label className="text-xs">Clear Margin (km/h)</Label><Input type="number" min="0" step="1" value={form.config.clear_margin_kmh ?? 0} onChange={(e) => updateConfig('clear_margin_kmh', Math.max(0, parseFloat(e.target.value) || 0))} data-testid="config-clear-margin" /><p className="text-[10px] text-muted-foreground">Clears when below limit minus margin</p></div>
                    </div>
                  </div>
                )}
                {form.event_type === 'overspeed_road_sign' && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                    <h4 className="font-medium text-sm">Road Sign Speed Configuration</h4>
                    <p className="text-[10px] text-muted-foreground">Define speed range tiers with buffer thresholds. The road speed limit is fetched from Overpass (OSM) or OSRM estimate.</p>

                    {/* Tier table */}
                    <div className="rounded-lg border overflow-hidden">
                      <div className="grid grid-cols-[1fr_1fr_80px_70px_28px] gap-0 bg-muted/50 px-2.5 py-1.5 text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                        <span>Speed Range</span>
                        <span>Buffer Type</span>
                        <span>Buffer</span>
                        <span>Preview</span>
                        <span></span>
                      </div>
                      {(form.config.speed_tiers || []).map((tier, idx) => (
                        <div key={idx} className="grid grid-cols-[1fr_1fr_80px_70px_28px] gap-0 items-center px-2.5 py-2 border-t">
                          <div className="flex items-center gap-1 text-xs">
                            <NumberInput min="0" value={tier.minSpeed ?? ''} onValueChange={(n) => {
                              const tiers = [...(form.config.speed_tiers || [])];
                              tiers[idx] = { ...tier, minSpeed: n };
                              updateConfig('speed_tiers', tiers);
                            }} className="w-14 h-7 text-xs px-1.5" />
                            <span className="text-muted-foreground">-</span>
                            <NumberInput value={tier.maxSpeed ?? ''} placeholder="..." emptyValue={null} onValueChange={(n) => {
                              const tiers = [...(form.config.speed_tiers || [])];
                              tiers[idx] = { ...tier, maxSpeed: n };
                              updateConfig('speed_tiers', tiers);
                            }} className="w-14 h-7 text-xs px-1.5" />
                            <span className="text-[10px] text-muted-foreground">km/h</span>
                          </div>
                          <Select value={tier.bufferType || 'percent'} onValueChange={(v) => {
                            const tiers = [...(form.config.speed_tiers || [])];
                            tiers[idx] = { ...tier, bufferType: v };
                            updateConfig('speed_tiers', tiers);
                          }}>
                            <SelectTrigger className="h-7 text-xs w-20"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="percent">%</SelectItem>
                              <SelectItem value="kmh">km/h</SelectItem>
                            </SelectContent>
                          </Select>
                          <NumberInput min="0" value={tier.bufferValue ?? ''} onValueChange={(n) => {
                            const tiers = [...(form.config.speed_tiers || [])];
                            tiers[idx] = { ...tier, bufferValue: n };
                            updateConfig('speed_tiers', tiers);
                          }} className="w-16 h-7 text-xs px-1.5" />
                          <span className="text-[10px] text-muted-foreground">+{tier.bufferValue || 0}{tier.bufferType === 'percent' ? '%' : ' km/h'}</span>
                          <button onClick={() => {
                            const tiers = (form.config.speed_tiers || []).filter((_, i) => i !== idx);
                            updateConfig('speed_tiers', tiers);
                          }} disabled={(form.config.speed_tiers || []).length <= 1} className="text-muted-foreground hover:text-destructive disabled:opacity-30">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => {
                      const tiers = form.config.speed_tiers || [];
                      const last = tiers[tiers.length - 1];
                      updateConfig('speed_tiers', [...tiers, { minSpeed: (last?.maxSpeed || 100) + 1, maxSpeed: null, bufferType: 'kmh', bufferValue: 10 }]);
                    }} className="text-xs">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Tier
                    </Button>

                    {/* Trigger duration + clear margin */}
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <DurationInput value={form.config.trigger_duration_secs ?? 0} onChange={(v) => updateConfig('trigger_duration_secs', v)} label="Trigger Duration" helpText="Must exceed for this long" testId="config-road-trigger-duration" />
                      <div className="space-y-1"><Label className="text-xs">Clear Margin (km/h)</Label><Input type="number" min="0" step="1" value={form.config.clear_margin_kmh ?? 0} onChange={(e) => updateConfig('clear_margin_kmh', Math.max(0, parseFloat(e.target.value) || 0))} /><p className="text-[10px] text-muted-foreground">Clears when below threshold minus margin</p></div>
                    </div>
                  </div>
                )}
                {OPERATIONAL_TRANSITION_EVENT_TYPES.includes(form.event_type) && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                    <h4 className="font-medium text-sm">{form.event_type === 'stopped' ? 'Stopped' : 'Moving'} Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DurationInput value={form.config.trigger_duration_secs ?? 0} onChange={(v) => updateConfig('trigger_duration_secs', v)} label="Transition Confirm Delay" helpText="Use 0 for an immediate trigger. A higher value requires the device to remain in that state continuously before the event fires." testId={`config-${form.event_type}-trigger-duration`} />
                    </div>
                  </div>
                )}
                {form.event_type === 'excessive_idle' && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="excessive-idle-config-card">
                    <h4 className="font-medium text-sm">Excessive Idling Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DurationInput value={form.config.idle_duration_secs ?? 120} onChange={(v) => updateConfig('idle_duration_secs', Math.max(1, v))} label="Idle Duration" helpText="Triggers when ignition is ON and the device remains in idle status for this long." testId="config-excessive-idle-duration" />
                    </div>
                  </div>
                )}
                {form.event_type === 'underspeed' && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="underspeed-config-card">
                    <h4 className="font-medium text-sm">Underspeed Configuration</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Minimum Speed (km/h)</Label>
                        <Input type="number" min="1" value={form.config.speed_limit_kmh ?? 20} onChange={(e) => updateConfig('speed_limit_kmh', Math.max(1, parseInt(e.target.value, 10) || 20))} data-testid="config-underspeed-limit" />
                      </div>
                      <DurationInput value={form.config.trigger_duration_secs ?? 10} onChange={(v) => updateConfig('trigger_duration_secs', v)} label="Trigger Duration" testId="config-underspeed-trigger-duration" />
                      <div className="space-y-1">
                        <Label className="text-xs">Clear Margin (km/h)</Label>
                        <Input type="number" min="0" value={form.config.clear_margin_kmh ?? 3} onChange={(e) => updateConfig('clear_margin_kmh', Math.max(0, parseFloat(e.target.value) || 0))} data-testid="config-underspeed-clear-margin" />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Only When Ignition ON</Label>
                        <div className="h-10 flex items-center rounded-md border px-3 bg-background">
                          <Switch checked={form.config.require_ignition_on !== false} onCheckedChange={(v) => updateConfig('require_ignition_on', !!v)} data-testid="config-underspeed-require-ignition" />
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Underspeed is evaluated when the vehicle is moving slowly below the configured minimum. With ignition required, stopped/off vehicles will not trigger this rule.</p>
                  </div>
                )}
                {CONNECTION_EVENT_TYPES.includes(form.event_type) && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="connection-event-config-card">
                    <h4 className="font-medium text-sm">
                      {form.event_type === 'gprs_connected' ? 'GPRS Connected' :
                       form.event_type === 'gprs_not_connected' ? 'GPRS Not Connected' :
                       form.event_type === 'gps_connected' ? 'GPS Connected' :
                       'GPS Not Connected'} Configuration
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DurationInput
                        value={form.config.trigger_duration_secs ?? (CONNECTION_EVENT_DEFAULTS[form.event_type] ?? 0)}
                        onChange={(v) => updateConfig('trigger_duration_secs', v)}
                        label="Transition Confirm Delay"
                        helpText={form.event_type === 'gprs_connected'
                          ? 'Use 0 to trigger immediately when the device reconnects. A higher value requires the device to keep sending data continuously before the event fires.'
                          : form.event_type === 'gprs_not_connected'
                            ? 'How long since the last GPRS data before this event fires. A watchdog checks every 60 seconds.'
                            : form.event_type === 'gps_connected'
                              ? 'Use 0 to trigger immediately when GPS fix is restored. A higher value requires the fix to hold continuously before firing.'
                              : 'How long without a GPS fix (while still sending GPRS data) before this event fires. A watchdog checks every 60 seconds.'}
                        testId={`config-${form.event_type}-trigger-duration`}
                      />
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                      {(form.event_type === 'gprs_not_connected' || form.event_type === 'gps_not_connected')
                        ? 'This rule uses a background watchdog that runs every 60 seconds. If devices are already in a disconnected state when this rule is created, the watchdog will detect and trigger the event on the next cycle.'
                        : 'This rule fires as a transition event at the moment the connection is restored.'}
                    </p>
                  </div>
                )}
                {isProximityEvent && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30" data-testid="proximity-event-config-card">
                    <div>
                      <h4 className="font-medium text-sm">
                        {form.event_type === 'vehicle_proximity' ? 'Proximity Enter' : 'Proximity Exit'} Configuration
                      </h4>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        {form.event_type === 'vehicle_proximity'
                          ? 'Fires when two devices come within the configured distance of each other. Two occurrences are created — one for each device.'
                          : 'Fires when two devices that were in proximity move apart beyond the configured distance. Two occurrences are created — one for each device.'}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Proximity Distance (metres)</Label>
                        <Input
                          type="number"
                          min="1"
                          value={form.config.proximity_distance_m ?? 500}
                          onChange={(e) => updateConfig('proximity_distance_m', Math.max(1, parseInt(e.target.value, 10) || 500))}
                          data-testid="config-proximity-distance"
                        />
                        <p className="text-[10px] text-muted-foreground">
                          Distance threshold in metres. Devices within this range are considered "in proximity".
                        </p>
                      </div>
                      <DurationInput
                        value={form.config.trigger_duration_secs ?? 0}
                        onChange={(v) => updateConfig('trigger_duration_secs', v)}
                        label="Transition Confirm Delay"
                        helpText="How long devices must remain in/out of proximity before the event fires. Use 0 for immediate."
                        testId="config-proximity-trigger-duration"
                      />
                    </div>
                    {/* Pair Mode Selector */}
                    <div className="space-y-2">
                      <Label className="text-xs font-medium">Device Pair Mode</Label>
                      <div className="grid grid-cols-1 gap-2">
                        {PROXIMITY_PAIR_MODE_OPTIONS.map((opt) => (
                          <label key={opt.value} className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ${(form.config.pair_mode || 'any_pair') === opt.value ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50'}`} data-testid={`proximity-pair-mode-${opt.value}`}>
                            <input type="radio" name="pair_mode" className="mt-0.5" checked={(form.config.pair_mode || 'any_pair') === opt.value} onChange={() => updateConfig('pair_mode', opt.value)} />
                            <div>
                              <span className="text-sm font-medium">{opt.label}</span>
                              <p className="text-[10px] text-muted-foreground mt-0.5">{opt.description}</p>
                            </div>
                          </label>
                        ))}
                      </div>
                    </div>
                    {/* Any Pair — uses main device_ids selector */}
                    {(form.config.pair_mode || 'any_pair') === 'any_pair' && (
                      <div className="p-3 rounded-md border bg-background">
                        <p className="text-xs text-muted-foreground">
                          Select devices in the <span className="font-medium">Devices</span> section above. Any pair combination among the selected devices will be monitored. At least 2 devices are required.
                        </p>
                        {form.device_ids.length > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {form.device_ids.length} device(s) selected = {Math.floor(form.device_ids.length * (form.device_ids.length - 1) / 2)} possible pair(s)
                          </p>
                        )}
                      </div>
                    )}
                    {/* Group Proximity */}
                    {(form.config.pair_mode || 'any_pair') === 'group_proximity' && (
                      <div className="p-3 rounded-md border bg-background" data-testid="proximity-group-info">
                        <p className="text-xs text-muted-foreground">
                          Select devices in the <span className="font-medium">Devices</span> section above. Each device fires <span className="font-medium">once</span> when it enters or exits proximity of any other monitored device. At least 2 devices are required.
                        </p>
                        <p className="text-[10px] text-muted-foreground mt-1">
                          Unlike "Any Pair", group mode does not fire per pair — so 3 devices arriving at the same spot produces ~3 occurrences instead of 6.
                        </p>
                        {form.device_ids.length > 0 && (
                          <p className="text-[10px] text-muted-foreground mt-1">
                            {form.device_ids.length} device(s) selected
                          </p>
                        )}
                      </div>
                    )}
                    {/* Specific Pairs */}
                    {(form.config.pair_mode || 'any_pair') === 'specific_pairs' && (
                      <div className="space-y-3 p-3 rounded-md border bg-background" data-testid="proximity-specific-pairs">
                        <div className="flex items-end gap-2">
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">Device A</Label>
                            <div className="space-y-1">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                                <Input
                                  value={form.config._pair_a_search || ''}
                                  onChange={(e) => updateConfig('_pair_a_search', e.target.value)}
                                  placeholder="Search device A..."
                                  className="pl-7 h-7 text-xs"
                                  data-testid="proximity-pair-a-search"
                                />
                              </div>
                              <ScrollArea className="h-[140px] border rounded-md bg-muted/20">
                                {trackableDevices
                                  .filter(d => {
                                    const q = (form.config._pair_a_search || '').toLowerCase();
                                    return !q || (deviceDisplayName(d) || '').toLowerCase().includes(q);
                                  })
                                  .sort((a, b) => (deviceDisplayName(a) || '').localeCompare(deviceDisplayName(b) || ''))
                                  .map(d => (
                                    <div
                                      key={d.id}
                                      className={`flex items-center gap-2 py-1 px-2 text-xs cursor-pointer hover:bg-muted/50 ${form.config._pair_a === d.id ? 'bg-primary/10 font-medium' : ''}`}
                                      onClick={() => updateConfig('_pair_a', d.id)}
                                      data-testid={`proximity-pair-a-${d.id}`}
                                    >
                                      <span className="truncate">{deviceDisplayName(d)}</span>
                                    </div>
                                  ))}
                              </ScrollArea>
                              {form.config._pair_a && (
                                <p className="text-[10px] text-primary font-medium truncate">{(() => { const d = devices.find(x => x.id === form.config._pair_a); return d ? deviceDisplayName(d) : 'Selected'; })()}</p>
                              )}
                            </div>
                          </div>
                          <span className="text-xs text-muted-foreground pb-2 shrink-0">↔</span>
                          <div className="flex-1 space-y-1">
                            <Label className="text-xs">Device B</Label>
                            <div className="space-y-1">
                              <div className="relative">
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                                <Input
                                  value={form.config._pair_b_search || ''}
                                  onChange={(e) => updateConfig('_pair_b_search', e.target.value)}
                                  placeholder="Search device B..."
                                  className="pl-7 h-7 text-xs"
                                  data-testid="proximity-pair-b-search"
                                />
                              </div>
                              <ScrollArea className="h-[140px] border rounded-md bg-muted/20">
                                {trackableDevices
                                  .filter(d => {
                                    if (d.id === form.config._pair_a) return false;
                                    const q = (form.config._pair_b_search || '').toLowerCase();
                                    return !q || (deviceDisplayName(d) || '').toLowerCase().includes(q);
                                  })
                                  .sort((a, b) => (deviceDisplayName(a) || '').localeCompare(deviceDisplayName(b) || ''))
                                  .map(d => (
                                    <div
                                      key={d.id}
                                      className={`flex items-center gap-2 py-1 px-2 text-xs cursor-pointer hover:bg-muted/50 ${form.config._pair_b === d.id ? 'bg-primary/10 font-medium' : ''}`}
                                      onClick={() => updateConfig('_pair_b', d.id)}
                                      data-testid={`proximity-pair-b-${d.id}`}
                                    >
                                      <span className="truncate">{deviceDisplayName(d)}</span>
                                    </div>
                                  ))}
                              </ScrollArea>
                              {form.config._pair_b && (
                                <p className="text-[10px] text-primary font-medium truncate">{(() => { const d = devices.find(x => x.id === form.config._pair_b); return d ? deviceDisplayName(d) : 'Selected'; })()}</p>
                              )}
                            </div>
                          </div>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-8 text-xs shrink-0"
                            data-testid="proximity-add-pair-btn"
                            disabled={!form.config._pair_a || !form.config._pair_b || form.config._pair_a === form.config._pair_b}
                            onClick={() => {
                              const existing = form.config.device_pairs || [];
                              const a = form.config._pair_a;
                              const b = form.config._pair_b;
                              const dup = existing.some(p => (p.device_a_id === a && p.device_b_id === b) || (p.device_a_id === b && p.device_b_id === a));
                              if (dup) { toast.error('This pair already exists'); return; }
                              setForm(prev => ({
                                ...prev,
                                config: {
                                  ...prev.config,
                                  device_pairs: [...existing, { device_a_id: a, device_b_id: b }],
                                  _pair_a: '', _pair_b: '', _pair_a_search: '', _pair_b_search: '',
                                },
                              }));
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />Add Pair
                          </Button>
                        </div>
                        {(form.config.device_pairs || []).length > 0 && (
                          <div className="space-y-1.5">
                            <Label className="text-[10px] text-muted-foreground">{(form.config.device_pairs || []).length} pair(s) configured</Label>
                            {(form.config.device_pairs || []).map((pair, idx) => {
                              const devA = devices.find(d => d.id === pair.device_a_id);
                              const devB = devices.find(d => d.id === pair.device_b_id);
                              return (
                                <div key={idx} className="flex items-center gap-2 p-2 rounded border bg-muted/30 text-xs" data-testid={`proximity-pair-row-${idx}`}>
                                  <span className="flex-1 truncate">{devA ? deviceDisplayName(devA) : pair.device_a_id}</span>
                                  <span className="text-muted-foreground shrink-0">↔</span>
                                  <span className="flex-1 truncate">{devB ? deviceDisplayName(devB) : pair.device_b_id}</span>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-6 w-6 p-0 text-destructive"
                                    data-testid={`proximity-remove-pair-${idx}`}
                                    onClick={() => {
                                      setForm(prev => ({
                                        ...prev,
                                        config: {
                                          ...prev.config,
                                          device_pairs: (prev.config.device_pairs || []).filter((_, i) => i !== idx),
                                        },
                                      }));
                                    }}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    {/* Target & Watch */}
                    {(form.config.pair_mode || 'any_pair') === 'target_watch' && (
                      <div className="space-y-3 p-3 rounded-md border bg-background" data-testid="proximity-target-watch">
                        <div className="space-y-1">
                          <Label className="text-xs">Target Device</Label>
                          <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
                            <Input
                              value={form.config._target_search || ''}
                              onChange={(e) => updateConfig('_target_search', e.target.value)}
                              placeholder="Search target device..."
                              className="pl-7 h-7 text-xs"
                              data-testid="proximity-target-search"
                            />
                          </div>
                          <ScrollArea className="h-[140px] border rounded-md bg-muted/20">
                            {trackableDevices
                              .filter(d => {
                                const q = (form.config._target_search || '').toLowerCase();
                                return !q || (deviceDisplayName(d) || '').toLowerCase().includes(q);
                              })
                              .sort((a, b) => (deviceDisplayName(a) || '').localeCompare(deviceDisplayName(b) || ''))
                              .map(d => (
                                <div
                                  key={d.id}
                                  className={`flex items-center gap-2 py-1 px-2 text-xs cursor-pointer hover:bg-muted/50 ${form.config.target_device_id === d.id ? 'bg-primary/10 font-medium' : ''}`}
                                  onClick={() => updateConfig('target_device_id', d.id)}
                                  data-testid={`proximity-target-${d.id}`}
                                >
                                  <span className="truncate">{deviceDisplayName(d)}</span>
                                </div>
                              ))}
                          </ScrollArea>
                          {form.config.target_device_id && (
                            <p className="text-[10px] text-primary font-medium">Target: {(() => { const d = devices.find(x => x.id === form.config.target_device_id); return d ? deviceDisplayName(d) : 'Selected'; })()}</p>
                          )}
                          <p className="text-[10px] text-muted-foreground">The primary device to monitor.</p>
                        </div>
                        <div className="space-y-1.5">
                          <Label className="text-xs">Watch Devices</Label>
                          <div className="flex items-center justify-between">
                            <p className="text-[10px] text-muted-foreground">Alert when the target comes near any of these devices.</p>
                            <button
                              type="button"
                              className="text-[10px] text-primary hover:underline shrink-0"
                              data-testid="proximity-watch-select-all"
                              onClick={() => {
                                const eligible = trackableDevices.filter(d => d.id !== form.config.target_device_id).map(d => d.id);
                                const allSelected = eligible.length > 0 && eligible.every(id => (form.config.watch_device_ids || []).includes(id));
                                setForm(prev => ({ ...prev, config: { ...prev.config, watch_device_ids: allSelected ? [] : eligible } }));
                              }}
                            >
                              {(() => { const eligible = trackableDevices.filter(d => d.id !== form.config.target_device_id).map(d => d.id); return eligible.length > 0 && eligible.every(id => (form.config.watch_device_ids || []).includes(id)) ? 'Deselect All' : 'Select All'; })()}
                            </button>
                          </div>
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                            <Input
                              value={form.config._watch_search || ''}
                              onChange={(e) => updateConfig('_watch_search', e.target.value)}
                              placeholder="Search watch devices..."
                              className="pl-8 h-8 text-sm"
                              data-testid="proximity-watch-search"
                            />
                          </div>
                          <ScrollArea className="h-[220px] border rounded-lg">
                            {(() => {
                              const q = (form.config._watch_search || '').toLowerCase();
                              const filtered = trackableDevices
                                .filter(d => d.id !== form.config.target_device_id)
                                .filter(d => !q || (deviceDisplayName(d) || '').toLowerCase().includes(q))
                                .sort((a, b) => (deviceDisplayName(a) || '').localeCompare(deviceDisplayName(b) || ''));
                              const groups = {};
                              filtered.forEach(d => { const g = d.device_group || 'Ungrouped'; if (!groups[g]) groups[g] = []; groups[g].push(d); });
                              const sortedGroups = Object.entries(groups).sort(([a], [b]) => { if (a === 'Ungrouped') return 1; if (b === 'Ungrouped') return -1; return a.localeCompare(b); });
                              return sortedGroups.map(([groupName, groupDevices]) => {
                                const watchIds = form.config.watch_device_ids || [];
                                const allInGroup = groupDevices.every(d => watchIds.includes(d.id));
                                const isCollapsed = collapsedGroups.has('watch_' + groupName);
                                return (
                                  <div key={groupName}>
                                    <div className="flex items-center gap-1.5 px-2 py-1.5 bg-muted border-b sticky top-0 z-10 cursor-pointer" onClick={() => setCollapsedGroups(prev => { const n = new Set(prev); const k = 'watch_' + groupName; n.has(k) ? n.delete(k) : n.add(k); return n; })}>
                                      {isCollapsed ? <ChevronRight className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                      <Checkbox checked={allInGroup} onCheckedChange={() => {
                                        setForm(prev => {
                                          const current = prev.config.watch_device_ids || [];
                                          const groupIds = groupDevices.map(d => d.id);
                                          const updated = allInGroup ? current.filter(id => !groupIds.includes(id)) : [...new Set([...current, ...groupIds])];
                                          return { ...prev, config: { ...prev.config, watch_device_ids: updated } };
                                        });
                                      }} onClick={(e) => e.stopPropagation()} />
                                      <span className="text-xs font-medium flex-1">{groupName}</span>
                                      <span className="text-xs text-muted-foreground">{groupDevices.length}</span>
                                    </div>
                                    {!isCollapsed && groupDevices.map(device => {
                                      const checked = (form.config.watch_device_ids || []).includes(device.id);
                                      return (
                                        <div key={device.id} className="flex items-center gap-2 py-1 px-3 hover:bg-muted/50 cursor-pointer" onClick={() => {
                                          setForm(prev => {
                                            const current = prev.config.watch_device_ids || [];
                                            const updated = checked ? current.filter(id => id !== device.id) : [...current, device.id];
                                            return { ...prev, config: { ...prev.config, watch_device_ids: updated } };
                                          });
                                        }} data-testid={`proximity-watch-device-${device.id}`}>
                                          <Checkbox checked={checked} />
                                          <span className="text-xs">{deviceDisplayName(device)}</span>
                                        </div>
                                      );
                                    })}
                                  </div>
                                );
                              });
                            })()}
                          </ScrollArea>
                          {(form.config.watch_device_ids || []).length > 0 && (
                            <p className="text-[10px] text-muted-foreground">{(form.config.watch_device_ids || []).length} watch device(s) selected</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {isGeofenceTransitionEvent && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="zone-transition-config-card">
                    <div>
                      <h4 className="font-medium text-sm">
                        {isZoneTransitionEvent ? 'Zone' : isPlaceTransitionEvent ? 'Place' : 'Route'} Transition Configuration
                      </h4>
                      <p className="text-[10px] text-muted-foreground mt-1">
                        Trigger when a device
                        {' '}
                        {['zone_in', 'place_in', 'route_entering'].includes(form.event_type) ? 'enters' : 'exits'}
                        {' '}
                        the selected {isZoneTransitionEvent ? 'zone(s)' : isPlaceTransitionEvent ? 'place(s)' : 'route(s)'}. A confirm delay can reduce GPS edge jitter.
                      </p>
                    </div>
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs">
                          {isZoneTransitionEvent ? 'Monitored Zones' : isPlaceTransitionEvent ? 'Monitored Places' : 'Monitored Routes'}
                        </Label>
                        {(isZoneTransitionEvent ? (form.config.monitored_zone_ids || []) : isPlaceTransitionEvent ? (form.config.monitored_place_ids || []) : (form.config.monitored_route_ids || [])).length > 0 && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1 text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
                            onClick={() => updateConfig(isZoneTransitionEvent ? 'monitored_zone_ids' : isPlaceTransitionEvent ? 'monitored_place_ids' : 'monitored_route_ids', [])}
                            data-testid="clear-all-monitored-assets-btn"
                          >
                            <X className="w-3.5 h-3.5" /> Clear all
                          </Button>
                        )}
                      </div>
                      <GroupedAssetSelector
                        items={isZoneTransitionEvent ? zones : isPlaceTransitionEvent ? places : routes}
                        selectedIds={isZoneTransitionEvent ? (form.config.monitored_zone_ids || []) : isPlaceTransitionEvent ? (form.config.monitored_place_ids || []) : (form.config.monitored_route_ids || [])}
                        onToggle={(id) => toggleAssetId(isZoneTransitionEvent ? 'monitored_zone_ids' : isPlaceTransitionEvent ? 'monitored_place_ids' : 'monitored_route_ids', id)}
                        nameKey={isZoneTransitionEvent ? 'zone_name' : isPlaceTransitionEvent ? 'place_name' : 'route_name'}
                        groupKey={isZoneTransitionEvent ? 'zone_group' : isPlaceTransitionEvent ? 'place_group' : 'route_group'}
                        placeholder={isZoneTransitionEvent ? 'Search monitored zones...' : isPlaceTransitionEvent ? 'Search monitored places...' : 'Search monitored routes...'}
                        geofenceGroups={geofenceGroups}
                        height="h-[180px]"
                      />
                      <p className="text-[10px] text-muted-foreground" data-testid="zone-transition-zone-count">
                        {(() => {
                          const count = isZoneTransitionEvent
                            ? (form.config.monitored_zone_ids || []).length
                            : isPlaceTransitionEvent
                              ? (form.config.monitored_place_ids || []).length
                              : (form.config.monitored_route_ids || []).length;
                          if (count === 0) return `No ${isZoneTransitionEvent ? 'zones' : isPlaceTransitionEvent ? 'places' : 'routes'} selected yet`;
                          return `${count} ${isZoneTransitionEvent ? 'zone' : isPlaceTransitionEvent ? 'place' : 'route'}${count === 1 ? '' : 's'} selected`;
                        })()}
                      </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DurationInput
                        value={form.config.trigger_duration_secs ?? 0}
                        onChange={(v) => updateConfig('trigger_duration_secs', v)}
                        label="Transition Confirm Delay"
                        helpText="Use 0 for an immediate trigger the moment the boundary is crossed."
                        testId="config-zone-trigger-duration"
                      />
                    </div>
                  </div>
                )}
                {isSensorIOEvent && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30" data-testid="sensor-io-config-card">
                    <div>
                      <h4 className="font-medium text-sm">Sensor / IO Event Configuration</h4>
                      <p className="text-[10px] text-muted-foreground mt-1">Use a logical mapped source selected by the user. Presets provide defaults, but the source can always be changed.</p>
                    </div>

                    {selectedDeviceDuplicateWarnings.length > 0 && (
                      <div className="p-2 rounded-md border border-amber-500/40 bg-amber-500/10 text-[11px] text-amber-700 dark:text-amber-400 space-y-0.5" data-testid="rule-duplicate-mapping-warning">
                        {selectedDeviceDuplicateWarnings.slice(0, 3).map((w, i) => (
                          <p key={i}>Device "{w.device}" has {w.names.length} mappings on IO {w.ioId} ({w.names.join(', ')}) — the first match wins. Fix in Device Management → IO Mapping.</p>
                        ))}
                        {selectedDeviceDuplicateWarnings.length > 3 && <p>+{selectedDeviceDuplicateWarnings.length - 3} more duplicate(s) on selected devices…</p>}
                      </div>
                    )}

                    <div className={`grid grid-cols-1 gap-4 ${isCustomSensorPreset ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
                      <div className="space-y-1.5">
                        <Label className="text-xs">Preset</Label>
                        <Select value={form.config.template_key || 'custom'} onValueChange={applySensorPreset}>
                          <SelectTrigger data-testid="sensor-io-preset-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {SENSOR_IO_PRESETS.map((preset) => (
                              <SelectItem key={preset.key} value={preset.key}>{preset.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {isCustomSensorPreset && (
                        <div className="space-y-1.5">
                          <Label className="text-xs">Mode</Label>
                          <Select value={form.config.sensor_mode || 'transition'} onValueChange={handleCustomSensorModeChange}>
                            <SelectTrigger data-testid="sensor-io-custom-mode-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="transition">Transition</SelectItem>
                              <SelectItem value="threshold">Threshold</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="space-y-1.5">
                        <Label className="text-xs">Group Logic</Label>
                        <Select value={form.config.group_operator || 'single'} onValueChange={(v) => {
                          updateConfig('group_operator', v);
                          // When switching to 'single', trim to first source only
                          if (v === 'single') {
                            setForm(prev => {
                              const sources = prev.config.sensor_sources || [];
                              if (sources.length > 1) return { ...prev, config: { ...prev.config, sensor_sources: [sources[0]] } };
                              return prev;
                            });
                          }
                        }} disabled={isCustomSensorPreset && form.config.sensor_mode === 'threshold'}>
                          <SelectTrigger data-testid="sensor-io-group-operator"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="single">Single source</SelectItem>
                            <SelectItem value="any">Any selected source</SelectItem>
                            <SelectItem value="all">All selected sources</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {isCustomSensorPreset && (
                      <p className="text-[10px] text-muted-foreground" data-testid="sensor-io-custom-help">
                        Custom does not auto-fill mapped sources or trigger values. Choose your own source, mode, operator, and trigger behavior. Threshold mode currently evaluates a single selected source.
                      </p>
                    )}

                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">{isDeviceMetricSensorPreset ? 'Source' : 'Mapped Sources'}</Label>
                        {!isDeviceMetricSensorPreset && (
                          <button
                            type="button"
                            onClick={resetSensorSources}
                            className="text-[10px] text-[#2563eb] hover:underline disabled:text-muted-foreground disabled:no-underline"
                            disabled={(form.config.sensor_sources || []).length === 0}
                            data-testid="sensor-io-deselect-all-sources"
                          >
                            De-select All
                          </button>
                        )}
                      </div>
                      {mappedSourceCleanupNotice && (
                        <p className="rounded border border-amber-200 bg-amber-50 px-2.5 py-2 text-[10px] text-amber-800" data-testid="sensor-io-source-cleanup-notice">
                          {mappedSourceCleanupNotice}
                        </p>
                      )}
                      {isDeviceMetricSensorPreset ? (
                        <div className="rounded border bg-background px-3 py-4 text-xs text-muted-foreground" data-testid="sensor-io-device-metric-source-note">
                          This preset uses the device’s main <span className="font-medium">{form.config.template_key === 'odometer_reached' ? 'odometer' : 'engine hours'}</span> reading from Device Setup, including the source selected there (GPS, ignition, or IO mapped source where applicable).
                        </div>
                      ) : form.device_ids.length === 0 ? (
                        <div className="rounded border bg-background px-3 py-4 text-xs text-muted-foreground" data-testid="sensor-io-source-empty-state">
                          Select one or more devices first so the available mapped sources can be listed.
                        </div>
                      ) : (
                        <div className="space-y-2">
                          <div className="border rounded bg-background">
                            <div className="p-2 space-y-1">
                              {selectedDeviceIOMappings.length === 0 ? (
                                <p className="text-xs text-muted-foreground px-1 py-2">No mapped IO sources found for the selected device(s).</p>
                              ) : selectedDeviceIOMappings.map((option) => {
                                const selected = (form.config.sensor_sources || []).some((src) => src.match_value === option.display_name && (src.io_type || '') === (option.io_type || ''));
                                const suggested = sensorPresetSourceMatches.some((src) => src.display_name === option.display_name && (src.io_type || '') === (option.io_type || ''));
                                return (
                                  <label
                                    key={`${option.display_name}-${option.io_type || 'na'}`}
                                    className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer border ${selected ? 'bg-primary/10 border-primary/30' : 'bg-background border-transparent hover:bg-muted/50'}`}
                                    data-testid={`sensor-source-${(option.display_name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                                  >
                                    <input type={(form.config.group_operator || 'single') === 'single' ? 'radio' : 'checkbox'} name="sensor-io-source" checked={selected} onChange={() => toggleSensorSource(option)} className="accent-primary" />
                                    <span className="font-medium">{option.display_name}</span>
                                    <span className="text-muted-foreground">({option.io_type || 'Mapped IO'})</span>
                                    {suggested && <Badge variant="outline" className="text-[10px] ml-auto">Suggested</Badge>}
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                          <p className="text-[10px] text-muted-foreground" data-testid="sensor-io-source-count">
                            {(form.config.sensor_sources || []).length === 0
                              ? 'No mapped sources selected yet'
                              : `${(form.config.sensor_sources || []).length} mapped source(s) selected`}
                          </p>
                          <p className="text-[10px] text-muted-foreground" data-testid="sensor-io-source-resolution-help">
                            Evaluation uses the exact mapped source you selected first (for example, <span className="font-medium">Vehicle is</span>), then falls back to compatible source types across devices only when needed.
                          </p>
                        </div>
                      )}
                    </div>

                    {form.config.sensor_mode === 'transition' && (
                      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 pt-1">
                        <div className="space-y-1 md:col-span-3">
                          <div className="min-h-[20px] flex items-center gap-1">
                            <Label className="text-xs">Trigger Condition</Label>
                            <HelpTip topic="trigger_condition" />
                          </div>
                          <Select value={form.config.trigger_definition?.transition?.operator || 'eq'} onValueChange={(v) => updateConfig('trigger_definition', { ...(form.config.trigger_definition || {}), transition: { ...(form.config.trigger_definition?.transition || {}), operator: v } })}>
                            <SelectTrigger className="h-10" data-testid="sensor-io-transition-operator"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="eq">Equals</SelectItem>
                              <SelectItem value="gt">Greater than</SelectItem>
                              <SelectItem value="lt">Less than</SelectItem>
                              <SelectItem value="gte">Greater or equal</SelectItem>
                              <SelectItem value="lte">Less or equal</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="min-h-[44px] text-[10px] text-transparent select-none" aria-hidden="true">.</p>
                        </div>
                        <div className="space-y-1 md:col-span-3">
                          <div className="min-h-[20px] flex items-center">
                            <Label className="text-xs">Trigger Value</Label>
                          </div>
                          <Input className="h-10" value={form.config.trigger_definition?.transition?.value ?? ''} placeholder="1 or LOCKED" onChange={(e) => updateConfig('trigger_definition', { ...(form.config.trigger_definition || {}), transition: { ...(form.config.trigger_definition?.transition || {}), value: e.target.value } })} data-testid="sensor-io-transition-value" />
                          <p className="min-h-[44px] text-[10px] text-muted-foreground" data-testid="sensor-io-trigger-value-help">
                            {isDeviceMetricSensorPreset ? (
                              <>
                                Enter the milestone target value. Example: <span className="font-medium">100000</span> for odometer or <span className="font-medium">5000</span> for engine hours.
                              </>
                            ) : (
                              <>
                                Logic mappings support either raw values like <span className="font-medium">1 / 0</span> or mapped labels like <span className="font-medium">YES / NO</span>, <span className="font-medium">LOCKED / UNLOCKED</span>, <span className="font-medium">OPEN / CLOSED</span>, or <span className="font-medium">ARMED / DISARMED</span>. Not case sensitive — <span className="font-medium">YES</span>, <span className="font-medium">Yes</span> and <span className="font-medium">yes</span> all match.
                              </>
                            )}
                          </p>
                        </div>
                        <div className="space-y-1 md:col-span-3">
                          <div className="min-h-[20px] flex items-center gap-1">
                            <Label className="text-xs">Transition Direction</Label>
                            <HelpTip topic="transition_direction" />
                          </div>
                          <Select value={form.config.trigger_definition?.transition?.direction || 'false_to_true'} onValueChange={(v) => updateConfig('trigger_definition', { ...(form.config.trigger_definition || {}), transition: { ...(form.config.trigger_definition?.transition || {}), direction: v } })}>
                            <SelectTrigger className="h-10" data-testid="sensor-io-transition-direction"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="false_to_true">False → True</SelectItem>
                              <SelectItem value="true_to_false">True → False</SelectItem>
                              <SelectItem value="any_change">Any Change</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="min-h-[44px] text-[10px] text-muted-foreground" data-testid="sensor-io-transition-direction-help">
                            {isDeviceMetricSensorPreset ? (
                              <>
                                Use <span className="font-medium">False → True</span> so the event fires once when the reading first reaches or exceeds the target.
                              </>
                            ) : (
                              <>
                                Example: for <span className="font-medium">Vehicle Locked</span>, use <span className="font-medium">LOCKED</span> or <span className="font-medium">1</span> with <span className="font-medium">False → True</span>.
                              </>
                            )}
                          </p>
                        </div>
                        <div className="space-y-1 md:col-span-3">
                          <DurationInput
                            value={form.config.confirm_delay_secs ?? 0}
                            onChange={(v) => updateConfig('confirm_delay_secs', v)}
                            label="Confirm Delay"
                            labelSuffix={<HelpTip topic="confirm_delay" />}
                            size="lg"
                            helpText="Use 0 for an immediate trigger. A higher value requires the condition to stay matched continuously before the event fires."
                            testId="sensor-io-confirm-delay"
                          />
                        </div>
                      </div>
                    )}

                    {form.config.sensor_mode === 'threshold' && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-1">
                        <div className="space-y-1">
                          <Label className="text-xs">Threshold Condition</Label>
                          <Select value={form.config.trigger_definition?.threshold?.operator || 'gt'} onValueChange={(v) => updateConfig('trigger_definition', { ...(form.config.trigger_definition || {}), threshold: { ...(form.config.trigger_definition?.threshold || {}), operator: v } })}>
                            <SelectTrigger className="h-10" data-testid="sensor-io-threshold-operator"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="gt">Greater than</SelectItem>
                              <SelectItem value="lt">Less than</SelectItem>
                              <SelectItem value="gte">Greater or equal</SelectItem>
                              <SelectItem value="lte">Less or equal</SelectItem>
                              <SelectItem value="eq">Equals</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Threshold Value</Label>
                          <Input className="h-10" value={form.config.trigger_definition?.threshold?.value ?? ''} placeholder="30" onChange={(e) => updateConfig('trigger_definition', { ...(form.config.trigger_definition || {}), threshold: { ...(form.config.trigger_definition?.threshold || {}), value: e.target.value } })} data-testid="sensor-io-threshold-value" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Clear Margin</Label>
                          <Input className="h-10" value={form.config.trigger_definition?.threshold?.clear_margin ?? ''} placeholder="2" onChange={(e) => updateConfig('trigger_definition', { ...(form.config.trigger_definition || {}), threshold: { ...(form.config.trigger_definition?.threshold || {}), clear_margin: e.target.value } })} data-testid="sensor-io-threshold-clear-margin" />
                        </div>
                        <DurationInput
                          value={form.config.confirm_delay_secs ?? 0}
                          onChange={(v) => updateConfig('confirm_delay_secs', v)}
                          label="Confirm Delay"
                          labelSuffix={<HelpTip topic="threshold" />}
                          size="lg"
                          helpText="Use 0 for an immediate trigger. A higher value requires the threshold to remain true continuously before the event fires."
                          testId="sensor-io-threshold-confirm-delay"
                        />
                      </div>
                    )}
                    {/* Notification Message override: Default View vs Custom Text */}
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 pt-1">
                      <div className="space-y-1 md:col-span-3">
                        <Label className="text-xs">Notification Message</Label>
                        <Select value={form.config.notification_message_mode || 'default'} onValueChange={(v) => updateConfig('notification_message_mode', v)}>
                          <SelectTrigger className="h-10" data-testid="sensor-notification-message-mode"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="default">Default View</SelectItem>
                            <SelectItem value="custom">Custom Text</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {(form.config.notification_message_mode || 'default') === 'custom' && (
                        <div className="space-y-1 md:col-span-9">
                          <Label className="text-xs">Custom Message Text</Label>
                          <Input className="h-10" value={form.config.notification_message_text || ''} placeholder="e.g. {sensor} is now {label}" onChange={(e) => updateConfig('notification_message_text', e.target.value)} data-testid="sensor-notification-message-text" />
                          <p className="text-[10px] text-muted-foreground">Replaces the sensor state line (e.g. "Ignition ON") in system, push, SMS and email notifications. Placeholders: {'{device}'} {'{rule}'} {'{sensor}'} {'{value}'} {'{label}'} {'{default}'}</p>
                        </div>
                      )}
                    </div>
                    {/* Fuel Unit selector — visible only for fuel_low preset */}
                    {form.config.sensor_mode === 'threshold' && (form.config.template_key || 'custom') === 'fuel_low' && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Fuel Unit</Label>
                          <Select value={form.config.fuel_unit || 'L'} onValueChange={(v) => updateConfig('fuel_unit', v)}>
                            <SelectTrigger className="h-10" data-testid="fuel-unit-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="L">Litres (L)</SelectItem>
                              <SelectItem value="gal">Gallons (gal)</SelectItem>
                              <SelectItem value="km">Kilometres (km)</SelectItem>
                              <SelectItem value="mi">Miles (mi)</SelectItem>
                              <SelectItem value="%">Percentage (%)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground">Unit shown in notifications. Use km/mi for fuel range events.</p>
                        </div>
                      </div>
                    )}
                    {/* Temperature Unit selector — visible only for temperature_high / temperature_low presets */}
                    {form.config.sensor_mode === 'threshold' && ['temperature_high', 'temperature_low'].includes(form.config.template_key || 'custom') && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Temperature Unit</Label>
                          <Select value={form.config.temp_unit || '°C'} onValueChange={(v) => updateConfig('temp_unit', v)}>
                            <SelectTrigger className="h-10" data-testid="temp-unit-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="°C">Celsius (°C)</SelectItem>
                              <SelectItem value="°F">Fahrenheit (°F)</SelectItem>
                              <SelectItem value="%">Percentage (%)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground">Unit shown in notifications.</p>
                        </div>
                      </div>
                    )}
                    {/* Battery Unit selector — visible only for battery preset */}
                    {form.config.sensor_mode === 'threshold' && (form.config.template_key || 'custom') === 'battery' && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Battery Unit</Label>
                          <Select value={form.config.battery_unit || 'V'} onValueChange={(v) => updateConfig('battery_unit', v)}>
                            <SelectTrigger className="h-10" data-testid="battery-unit-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="V">Volts (V)</SelectItem>
                              <SelectItem value="mV">Millivolts (mV)</SelectItem>
                              <SelectItem value="%">Percentage (%)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground">Unit shown in notifications.</p>
                        </div>
                      </div>
                    )}
                    {/* Custom Unit selector — visible only for custom preset in threshold mode */}
                    {form.config.sensor_mode === 'threshold' && (form.config.template_key || 'custom') === 'custom' && (
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="space-y-1">
                          <Label className="text-xs">Unit</Label>
                          <Select value={form.config.custom_unit || 'None'} onValueChange={(v) => updateConfig('custom_unit', v)}>
                            <SelectTrigger className="h-10" data-testid="custom-unit-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {['None', 'V', 'mV', 'km/h', 'mph', 'km', 'm', '°C', '°F', '%', 'L', 'gal', 'hours', 'min', 'sec', 'dBm', 'RPM', 'bar', 'psi', 'kPa', 'g', 'mAh', 'W'].map(u => (
                                <SelectItem key={u} value={u}>{u === 'None' ? 'No unit' : u}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="text-[10px] text-muted-foreground">Unit appended to the value in notifications.</p>
                        </div>
                      </div>
                    )}

                  </div>
                )}

                {/* Occurrence Mode — shared by both speed types */}
                {(form.event_type === 'overspeed_fixed' || form.event_type === 'overspeed_road_sign') && (
                  <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
                    <h4 className="font-medium text-sm">Occurrence Mode</h4>
                    <div className="flex gap-3">
                      <button
                        onClick={() => updateConfig('occurrence_mode', 'single')}
                        className={`flex-1 p-3 rounded-lg border text-left transition-colors ${
                          (form.config.occurrence_mode || 'single') === 'single'
                            ? 'border-[#2563eb] bg-[#2563eb]/10'
                            : 'border-border hover:bg-muted/50'
                        }`}
                      >
                        <p className="text-sm font-medium">Single per episode</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">One occurrence stays active until the condition clears. Tracks total duration.</p>
                      </button>
                      <button
                        onClick={() => updateConfig('occurrence_mode', 'interval')}
                        className={`flex-1 p-3 rounded-lg border text-left transition-colors ${
                          form.config.occurrence_mode === 'interval'
                            ? 'border-[#2563eb] bg-[#2563eb]/10'
                            : 'border-border hover:bg-muted/50'
                        }`}
                      >
                        <p className="text-sm font-medium">Re-trigger at intervals</p>
                        <p className="text-[10px] text-muted-foreground mt-0.5">Creates a new occurrence every X minutes while the condition stays true.</p>
                      </button>
                    </div>
                    {form.config.occurrence_mode === 'interval' && (
                      <div className="flex items-center gap-2">
                        <Label className="text-xs whitespace-nowrap">Re-trigger every:</Label>
                        <Input type="number" min="1" value={form.config.retrigger_interval_minutes || 5} onChange={(e) => updateConfig('retrigger_interval_minutes', Math.max(1, parseInt(e.target.value) || 5))} className="w-20 h-8 text-sm" />
                        <span className="text-xs text-muted-foreground">minutes</span>
                      </div>
                    )}
                  </div>
                )}

                {/* Harsh Driving Configuration */}
                {isHarshDrivingEvent && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30" data-testid="harsh-driving-config-card">
                    <div>
                      <h4 className="font-medium text-sm">Harsh Driving Configuration</h4>
                      <p className="text-[10px] text-muted-foreground mt-1">Select the mapped IO source (e.g., Green Driving Type for Teltonika, Event Code for Meitrack) and set the trigger values for each driving event type.</p>
                    </div>

                    {/* Mapped Source Selector */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">Mapped IO Source</Label>
                        {(form.config.sensor_sources || []).length > 0 && (
                          <button type="button" onClick={() => setForm(prev => ({...prev, config: {...prev.config, sensor_sources: []}}))} className="text-[10px] text-[#2563eb] hover:underline">De-select All</button>
                        )}
                      </div>
                      {selectedDeviceIOMappings.length === 0 ? (
                        <p className="text-[10px] text-muted-foreground italic">Select devices first to see available mapped sources.</p>
                      ) : (
                        <div className="border rounded bg-background">
                          <div className="p-2 space-y-1">
                          {selectedDeviceIOMappings.map((option) => {
                            const isSelected = (form.config.sensor_sources || []).some(s => s.match_value === option.display_name && (s.io_type || '') === (option.io_type || ''));
                            const isSuggested = matchesSourceKeywords(option, HARSH_DRIVING_SOURCE_KEYWORDS);
                            return (
                              <label key={`hd-${option.display_name}::${option.io_type || ''}`} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer border ${isSelected ? 'bg-primary/10 border-primary/30' : 'bg-background border-transparent hover:bg-muted/50'}`}>
                                <input type="checkbox" checked={isSelected} onChange={() => {
                                  const srcDef = { selection_mode: 'mapped_io', match_value: option.display_name, display_label: option.display_name, io_type: option.io_type || '' };
                                  setForm(prev => {
                                    const existing = prev.config.sensor_sources || [];
                                    const idx = existing.findIndex(s => s.match_value === option.display_name && (s.io_type || '') === (option.io_type || ''));
                                    const next = idx >= 0 ? existing.filter((_, i) => i !== idx) : [...existing, srcDef];
                                    return {...prev, config: {...prev.config, sensor_sources: next}};
                                  });
                                }} className="accent-primary" />
                                <span className="font-medium">{option.display_name}</span>
                                {option.io_type && <span className="text-muted-foreground">({option.io_type})</span>}
                                {isSuggested && <Badge variant="outline" className="text-[10px] ml-auto">Suggested</Badge>}
                              </label>
                            );
                          })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* 5 Trigger Value Fields — Multi-value pill UI */}
                    <div className="space-y-3">
                      <Label className="text-xs">Trigger Values</Label>
                      <p className="text-[10px] text-muted-foreground -mt-2">Add IO values for each driving event type. Multiple values allow one rule to cover multiple device protocols (e.g., Teltonika + Meitrack).</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Acceleration</Label>
                          <ValuePillInput testId="harsh-acceleration-values" values={form.config.acceleration_values || []} onChange={v => updateConfig('acceleration_values', v)} placeholder="e.g. 1 or 130" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Braking</Label>
                          <ValuePillInput testId="harsh-braking-values" values={form.config.braking_values || []} onChange={v => updateConfig('braking_values', v)} placeholder="e.g. 2 or 129" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Cornering</Label>
                          <ValuePillInput testId="harsh-cornering-values" values={form.config.cornering_values || []} onChange={v => updateConfig('cornering_values', v)} placeholder="e.g. 3" />
                          <p className="text-[9px] text-muted-foreground">For devices without left/right distinction</p>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Cornering Left</Label>
                          <ValuePillInput testId="harsh-cornering-left-values" values={form.config.cornering_left_values || []} onChange={v => updateConfig('cornering_left_values', v)} placeholder="e.g. 90" />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-muted-foreground">Cornering Right</Label>
                          <ValuePillInput testId="harsh-cornering-right-values" values={form.config.cornering_right_values || []} onChange={v => updateConfig('cornering_right_values', v)} placeholder="e.g. 91" />
                          <p className="text-[9px] text-muted-foreground">For devices with left/right distinction</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Crash Detection Configuration */}
                {isCrashDetectionEvent && (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30" data-testid="crash-detection-config-card">
                    <div>
                      <h4 className="font-medium text-sm">Crash Detection Configuration</h4>
                      <p className="text-[10px] text-muted-foreground mt-1">Select the mapped IO source (e.g., Crash Detection for Teltonika IO 247, Event Code for Meitrack) and set which values trigger a crash event.</p>
                    </div>

                    {/* Mapped Source Selector */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">Mapped IO Source</Label>
                        {(form.config.sensor_sources || []).length > 0 && (
                          <button type="button" onClick={() => setForm(prev => ({...prev, config: {...prev.config, sensor_sources: []}}))} className="text-[10px] text-[#2563eb] hover:underline">De-select All</button>
                        )}
                      </div>
                      {selectedDeviceIOMappings.length === 0 ? (
                        <p className="text-[10px] text-muted-foreground italic">Select devices first to see available mapped sources.</p>
                      ) : (
                        <div className="border rounded bg-background">
                          <div className="p-2 space-y-1">
                          {selectedDeviceIOMappings.map((option) => {
                            const isSelected = (form.config.sensor_sources || []).some(s => s.match_value === option.display_name && (s.io_type || '') === (option.io_type || ''));
                            const isSuggested = matchesSourceKeywords(option, CRASH_DETECTION_SOURCE_KEYWORDS);
                            return (
                              <label key={`cd-${option.display_name}::${option.io_type || ''}`} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer border ${isSelected ? 'bg-primary/10 border-primary/30' : 'bg-background border-transparent hover:bg-muted/50'}`}>
                                <input type="checkbox" checked={isSelected} onChange={() => {
                                  const srcDef = { selection_mode: 'mapped_io', match_value: option.display_name, display_label: option.display_name, io_type: option.io_type || '' };
                                  setForm(prev => {
                                    const existing = prev.config.sensor_sources || [];
                                    const idx = existing.findIndex(s => s.match_value === option.display_name && (s.io_type || '') === (option.io_type || ''));
                                    const next = idx >= 0 ? existing.filter((_, i) => i !== idx) : [...existing, srcDef];
                                    return {...prev, config: {...prev.config, sensor_sources: next}};
                                  });
                                }} className="accent-primary" />
                                <span className="font-medium">{option.display_name}</span>
                                {option.io_type && <span className="text-muted-foreground">({option.io_type})</span>}
                                {isSuggested && <Badge variant="outline" className="text-[10px] ml-auto">Suggested</Badge>}
                              </label>
                            );
                          })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Trigger Values */}
                    <div className="space-y-1.5">
                      <Label className="text-xs">Trigger Values</Label>
                      <p className="text-[10px] text-muted-foreground">Add the IO values that should trigger a crash event. For Teltonika IO 247: values 1-6. For Meitrack Event Code: 78.</p>
                      <ValuePillInput testId="crash-trigger-values" values={form.config.trigger_values || []} onChange={v => updateConfig('trigger_values', v)} placeholder="e.g. 1, 78" />
                    </div>
                  </div>
                )}

                {/* Event Code Configuration */}
                {(isVideoEvent || isDeviceEvent) && (() => {
                  const triggerCatalog = isVideoEvent ? VIDEO_EVENT_CATALOG : DEVICE_EVENT_CATALOG;
                  const knownCodes = isVideoEvent ? ALL_KNOWN_VIDEO_CODES : ALL_KNOWN_DEVICE_CODES;
                  const sourceKeywords = isVideoEvent ? VIDEO_EVENT_SOURCE_KEYWORDS : DEVICE_EVENT_SOURCE_KEYWORDS;
                  const testPrefix = isVideoEvent ? 'video-event' : 'device-event';
                  const configTitle = isVideoEvent ? 'Video Event Configuration' : 'Device Event Configuration';
                  const configHelp = isVideoEvent
                    ? 'Select the mapped IO source (e.g., Event Code for Meitrack) and choose which video events to monitor.'
                    : 'Select the mapped IO source (e.g., Event Code for Meitrack) and choose which non-video device events to monitor.';
                  return (
                  <div className="space-y-4 p-4 rounded-lg border bg-muted/30" data-testid={`${testPrefix}-config-card`}>
                    <div>
                      <h4 className="font-medium text-sm">{configTitle}</h4>
                      <p className="text-[10px] text-muted-foreground mt-1">{configHelp}</p>
                    </div>

                    {/* Mapped Source Selector */}
                    <div className="space-y-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <Label className="text-xs">Mapped IO Source</Label>
                        {(form.config.sensor_sources || []).length > 0 && (
                          <button type="button" onClick={() => setForm(prev => ({...prev, config: {...prev.config, sensor_sources: []}}))} className="text-[10px] text-[#2563eb] hover:underline" data-testid={`${testPrefix}-clear-sources`}>De-select All</button>
                        )}
                      </div>
                      {selectedDeviceIOMappings.length === 0 ? (
                        <p className="text-[10px] text-muted-foreground italic">Select devices first to see available mapped sources.</p>
                      ) : (
                        <div className="border rounded bg-background">
                          <div className="p-2 space-y-1">
                          {selectedDeviceIOMappings.map((option) => {
                            const isSelected = (form.config.sensor_sources || []).some(s => s.match_value === option.display_name && (s.io_type || '') === (option.io_type || ''));
                            const isSuggested = matchesSourceKeywords(option, sourceKeywords);
                            return (
                              <label key={`${testPrefix}-${option.display_name}::${option.io_type || ''}`} className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs cursor-pointer border ${isSelected ? 'bg-primary/10 border-primary/30' : 'bg-background border-transparent hover:bg-muted/50'}`}>
                                <input type="checkbox" checked={isSelected} onChange={() => {
                                  const srcDef = { selection_mode: 'mapped_io', match_value: option.display_name, display_label: option.display_name, io_type: option.io_type || '' };
                                  setForm(prev => {
                                    const existing = prev.config.sensor_sources || [];
                                    const idx = existing.findIndex(s => s.match_value === option.display_name && (s.io_type || '') === (option.io_type || ''));
                                    const next = idx >= 0 ? existing.filter((_, i) => i !== idx) : [...existing, srcDef];
                                    return {...prev, config: {...prev.config, sensor_sources: next}};
                                  });
                                }} className="accent-primary" data-testid={`${testPrefix}-source-${option.display_name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`} />
                                <span className="font-medium">{option.display_name}</span>
                                {option.io_type && <span className="text-muted-foreground">({option.io_type})</span>}
                                {isSuggested && <Badge variant="outline" className="text-[10px] ml-auto">Suggested</Badge>}
                              </label>
                            );
                          })}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Event Triggers — Grouped Checklist */}
                    <div className="space-y-2">
                      <Label className="text-xs">Event Triggers</Label>
                      <p className="text-[10px] text-muted-foreground -mt-1">Select pre-defined events or add custom event codes below.</p>
                      {triggerCatalog.map((group) => {
                        const groupCodes = group.items.map(i => i.code);
                        const selectedInGroup = groupCodes.filter(c => (form.config.trigger_values || []).includes(c));
                        const allSelected = selectedInGroup.length === groupCodes.length;
                        const groupSlug = group.category.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                        return (
                          <div key={group.category} className="border rounded bg-background">
                            <label className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium hover:bg-muted/50 cursor-pointer">
                              <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = selectedInGroup.length > 0 && !allSelected; }}
                                onChange={() => {
                                  setForm(prev => {
                                    const current = prev.config.trigger_values || [];
                                    const next = allSelected
                                      ? current.filter(c => !groupCodes.includes(c))
                                      : [...new Set([...current, ...groupCodes])];
                                    return { ...prev, config: { ...prev.config, trigger_values: next } };
                                  });
                                }} className="accent-primary" data-testid={`${testPrefix}-trigger-group-${groupSlug}`} />
                              <span className="flex-1">{group.category}</span>
                              <span className="text-muted-foreground">{selectedInGroup.length}/{groupCodes.length}</span>
                            </label>
                            <div className="px-3 pb-2 grid grid-cols-1 sm:grid-cols-2 gap-1">
                              {group.items.map((item) => {
                                const isChecked = (form.config.trigger_values || []).includes(item.code);
                                return (
                                  <label key={item.code} className={`flex items-center gap-2 px-2 py-1 rounded text-xs cursor-pointer ${isChecked ? 'bg-primary/10' : 'hover:bg-muted/30'}`}>
                                    <input type="checkbox" checked={isChecked} onChange={() => {
                                      setForm(prev => {
                                        const current = prev.config.trigger_values || [];
                                        const next = isChecked ? current.filter(c => c !== item.code) : [...current, item.code];
                                        return { ...prev, config: { ...prev.config, trigger_values: next } };
                                      });
                                    }} className="accent-primary" data-testid={`${testPrefix}-trigger-${item.code}`} />
                                    <span>{item.label}</span>
                                    <span className="text-muted-foreground">({item.code})</span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })}

                      {/* Custom Event Codes with Labels */}
                      <div className="border rounded bg-background p-3 space-y-2">
                        <Label className="text-[11px] text-muted-foreground">Custom Event Codes</Label>
                        <p className="text-[9px] text-muted-foreground">Add any {isDeviceEvent ? 'non-video device' : 'video'} event code not listed above. Labels are used in notifications.</p>
                        {(() => {
                          const customCodes = (form.config.trigger_values || []).filter(v => !knownCodes.has(String(v)));
                          const customLabels = form.config.custom_labels || {};
                          return (
                            <>
                              {customCodes.map((code) => (
                                <div key={code} className="flex items-center gap-2">
                                  <Badge variant="outline" className="text-xs font-mono shrink-0">{code}</Badge>
                                  <Input
                                    data-testid={`${testPrefix}-custom-label-${code}`}
                                    value={customLabels[code] || ''}
                                    onChange={(e) => {
                                      const val = e.target.value;
                                      setForm(prev => {
                                        const labels = { ...(prev.config.custom_labels || {}) };
                                        if (val.trim()) labels[code] = val;
                                        else delete labels[code];
                                        return { ...prev, config: { ...prev.config, custom_labels: labels } };
                                      });
                                    }}
                                    placeholder="Label (e.g. Driver Drowsy Level 2)"
                                    className="h-7 text-xs flex-1"
                                  />
                                  <button type="button" onClick={() => {
                                    setForm(prev => {
                                      const next = (prev.config.trigger_values || []).filter(c => c !== code);
                                      const labels = { ...(prev.config.custom_labels || {}) };
                                      delete labels[code];
                                      return { ...prev, config: { ...prev.config, trigger_values: next, custom_labels: labels } };
                                    });
                                  }} className="text-muted-foreground hover:text-foreground" data-testid={`${testPrefix}-remove-custom-${code}`}><X className="w-3.5 h-3.5" /></button>
                                </div>
                              ))}
                              <ValuePillInput
                                testId={`${testPrefix}-custom-trigger-values`}
                                values={[]}
                                onChange={(newCodes) => {
                                  setForm(prev => {
                                    const current = prev.config.trigger_values || [];
                                    const rejectedListedCodes = newCodes.filter(code => knownCodes.has(String(code)));
                                    const rejectedVideoCodes = isDeviceEvent
                                      ? newCodes.filter(code => VIDEO_EXCLUSIVE_CODES.has(String(code)))
                                      : [];
                                    const filteredNewCodes = newCodes.filter(code => !knownCodes.has(String(code)) && !(isDeviceEvent && VIDEO_EXCLUSIVE_CODES.has(String(code))));
                                    if (rejectedListedCodes.length > 0) {
                                      toast.error(`${rejectedListedCodes.join(', ')} already ${rejectedListedCodes.length === 1 ? 'exists' : 'exist'} in the checklist above`);
                                    }
                                    if (isDeviceEvent && filteredNewCodes.length !== newCodes.length) {
                                      if (rejectedVideoCodes.length > 0) {
                                        toast.error('Device Event cannot include video event trigger codes');
                                      }
                                    }
                                    const merged = [...new Set([...current, ...filteredNewCodes])];
                                    return { ...prev, config: { ...prev.config, trigger_values: merged } };
                                  });
                                }}
                                placeholder={isDeviceEvent ? 'Add custom code (e.g. 999)' : 'Add code (e.g. 742)'}
                              />
                            </>
                          );
                        })()}
                      </div>

                      {/* Summary */}
                      {(form.config.trigger_values || []).length > 0 && (
                        <p className="text-[10px] text-muted-foreground">{(form.config.trigger_values || []).length} trigger(s) selected</p>
                      )}
                    </div>
                  </div>
                  );
                })()}

                {/* Geofencing Optional Conditions — hidden for events whose trigger is already
                    a geofence/Place transition (zone/place/route in-out, person place, occupancy) */}
                {!isGeofenceTransitionEvent && !isPersonPlaceEvent && !isOccupancyEvent && (
                  <div className="space-y-3 p-4 rounded-lg border bg-muted/30" data-testid="geofencing-optional-conditions-card">
                    <h4 className="font-medium text-sm">Geofencing Optional Conditions</h4>
                    <p className="text-[10px] text-muted-foreground -mt-1">Include = event only triggers in these areas (OR between all). Exclude = event never triggers in these areas (checked first).</p>

                    {/* Zones */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={form.config.depending_on_zones || false} onCheckedChange={(v) => updateConfig('depending_on_zones', !!v)} data-testid="condition-zones-toggle" />
                        <Label className="text-sm cursor-pointer select-none" onClick={() => updateConfig('depending_on_zones', !form.config.depending_on_zones)} data-testid="condition-zones-label">Only in these Zones</Label>
                      </div>
                      {form.config.depending_on_zones && (
                        <GroupedAssetSelector items={zones} selectedIds={form.config.zone_ids || []} onToggle={(id) => toggleAssetId('zone_ids', id)} nameKey="zone_name" groupKey="zone_group" placeholder="Search zones..." geofenceGroups={geofenceGroups} />
                      )}
                      <div className="flex items-center gap-2">
                        <Checkbox checked={form.config.exclude_zones || false} onCheckedChange={(v) => updateConfig('exclude_zones', !!v)} data-testid="condition-exclude-zones-toggle" />
                        <Label className="text-sm text-red-600 dark:text-red-400 cursor-pointer select-none" onClick={() => updateConfig('exclude_zones', !form.config.exclude_zones)} data-testid="condition-exclude-zones-label">Exclude these Zones</Label>
                      </div>
                      {form.config.exclude_zones && (
                        <GroupedAssetSelector items={zones} selectedIds={form.config.exclude_zone_ids || []} onToggle={(id) => toggleAssetId('exclude_zone_ids', id)} nameKey="zone_name" groupKey="zone_group" placeholder="Search zones to exclude..." geofenceGroups={geofenceGroups} />
                      )}
                    </div>

                    {/* Places */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={form.config.depending_on_places || false} onCheckedChange={(v) => updateConfig('depending_on_places', !!v)} data-testid="condition-places-toggle" />
                        <Label className="text-sm cursor-pointer select-none" onClick={() => updateConfig('depending_on_places', !form.config.depending_on_places)} data-testid="condition-places-label">Only near these Places</Label>
                      </div>
                      {form.config.depending_on_places && (
                        <GroupedAssetSelector items={places} selectedIds={form.config.place_ids || []} onToggle={(id) => toggleAssetId('place_ids', id)} nameKey="place_name" groupKey="place_group" placeholder="Search places..." geofenceGroups={geofenceGroups} />
                      )}
                      <div className="flex items-center gap-2">
                        <Checkbox checked={form.config.exclude_places || false} onCheckedChange={(v) => updateConfig('exclude_places', !!v)} data-testid="condition-exclude-places-toggle" />
                        <Label className="text-sm text-red-600 dark:text-red-400 cursor-pointer select-none" onClick={() => updateConfig('exclude_places', !form.config.exclude_places)} data-testid="condition-exclude-places-label">Exclude these Places</Label>
                      </div>
                      {form.config.exclude_places && (
                        <GroupedAssetSelector items={places} selectedIds={form.config.exclude_place_ids || []} onToggle={(id) => toggleAssetId('exclude_place_ids', id)} nameKey="place_name" groupKey="place_group" placeholder="Search places to exclude..." geofenceGroups={geofenceGroups} />
                      )}
                    </div>

                    {/* Routes */}
                    <div className="space-y-1.5">
                      <div className="flex items-center gap-2">
                        <Checkbox checked={form.config.depending_on_routes || false} onCheckedChange={(v) => updateConfig('depending_on_routes', !!v)} data-testid="condition-routes-toggle" />
                        <Label className="text-sm cursor-pointer select-none" onClick={() => updateConfig('depending_on_routes', !form.config.depending_on_routes)} data-testid="condition-routes-label">Only on these Routes</Label>
                      </div>
                      {form.config.depending_on_routes && (
                        <GroupedAssetSelector items={routes} selectedIds={form.config.route_ids || []} onToggle={(id) => toggleAssetId('route_ids', id)} nameKey="route_name" groupKey="route_group" placeholder="Search routes..." geofenceGroups={geofenceGroups} />
                      )}
                      <div className="flex items-center gap-2">
                        <Checkbox checked={form.config.exclude_routes || false} onCheckedChange={(v) => updateConfig('exclude_routes', !!v)} data-testid="condition-exclude-routes-toggle" />
                        <Label className="text-sm text-red-600 dark:text-red-400 cursor-pointer select-none" onClick={() => updateConfig('exclude_routes', !form.config.exclude_routes)} data-testid="condition-exclude-routes-label">Exclude these Routes</Label>
                      </div>
                      {form.config.exclude_routes && (
                        <GroupedAssetSelector items={routes} selectedIds={form.config.exclude_route_ids || []} onToggle={(id) => toggleAssetId('exclude_route_ids', id)} nameKey="route_name" groupKey="route_group" placeholder="Search routes to exclude..." geofenceGroups={geofenceGroups} />
                      )}
                    </div>
                  </div>
                )}

                {/* IO Mapped Logic Condition Builder — compound AND/OR groups with timers.
                    Hidden for binding-driven events: IO packet logic cannot apply there. */}
                {!isProximityEvent && !isBindingDrivenEvent && (
                <div className="space-y-2 p-4 rounded-lg border bg-muted/30">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={form.config.io_conditions_enabled || false} onCheckedChange={(v) => updateConfig('io_conditions_enabled', !!v)} data-testid="condition-io-toggle" />
                      <h4 className="font-medium text-sm cursor-pointer select-none" onClick={() => updateConfig('io_conditions_enabled', !form.config.io_conditions_enabled)} data-testid="condition-io-label">IO Mapped Logic Condition</h4>
                      <HelpTip topic="io_conditions" />
                    </div>
                    {form.config.io_conditions_enabled && (
                      <button onClick={() => updateConfig('io_conditions', { main_timer_secs: 0, main_timer_mode: 'pre', logic: 'AND', groups: [{ logic: 'AND', conditions: [{ source: 'io_mapping', display_name: '', io_id: '', io_type: '', operator: 'eq', value: '1', timer_secs: 0, timer_mode: 'pre' }] }] })} className="text-xs text-[#2563eb] hover:underline">Reset All</button>
                    )}
                  </div>
                  {form.config.io_conditions_enabled && (
                    <div className="space-y-3">
                      {ioSourceDevices.length === 0 ? (
                        <p className="text-xs text-muted-foreground text-center py-2 border rounded bg-background p-3">{form.all_devices ? 'No IO mappings found on your devices — configure mappings in Device Management → IO Mapping.' : 'Select one or more devices above (or enable All My Devices) to see available IO Mapped Data.'}</p>
                      ) : (
                        <>
                          {/* Main Timer */}
                          <div className="flex items-center gap-3 p-2 border rounded bg-background flex-wrap">
                            <Label className="text-xs whitespace-nowrap">Main Timer:</Label>
                            <HelpTip topic="main_timer" />
                            <NumberInput min="0" value={form.config.io_conditions?.main_timer_secs || 0} onValueChange={(n) => {
                              const ioc = { ...(form.config.io_conditions || {}) };
                              ioc.main_timer_secs = n;
                              updateConfig('io_conditions', ioc);
                            }} className="h-7 text-xs w-24" data-testid="io-main-timer-input" />
                            {postTimersSupported && (form.config.io_conditions?.main_timer_secs || 0) > 0 && (
                              <Select value={form.config.io_conditions?.main_timer_mode || 'pre'} onValueChange={(v) => {
                                const ioc = { ...(form.config.io_conditions || {}) };
                                ioc.main_timer_mode = v;
                                updateConfig('io_conditions', ioc);
                              }}>
                                <SelectTrigger className="h-7 text-[10px] w-40" data-testid="io-main-timer-mode"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pre">Already true for</SelectItem>
                                  <SelectItem value="post">Sustained after trigger</SelectItem>
                                </SelectContent>
                              </Select>
                            )}
                            <span className="text-[10px] text-muted-foreground">seconds (0 = no main timer). "Already": formula must have held this long. "After trigger": event fires once the formula stays true this long after the trigger.</span>
                          </div>

                          {/* Condition Groups */}
                          {(form.config.io_conditions?.groups || []).map((group, gIdx) => (
                            <div key={gIdx}>
                              {/* Junction operator between this group and the previous one */}
                              {gIdx > 0 && (
                                <div className="flex justify-center py-1">
                                  <Select value={group.junction || form.config.io_conditions?.logic || 'AND'} onValueChange={(v) => {
                                    const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                    ioc.groups[gIdx].junction = v;
                                    updateConfig('io_conditions', ioc);
                                  }}>
                                    <SelectTrigger className="h-8 h-6 w-20 text-xs font-bold border-[#2563eb] text-[#2563eb]" data-testid={`io-group-junction-${gIdx}`}><SelectValue /></SelectTrigger>
                                    <SelectContent><SelectItem value="AND">AND</SelectItem><SelectItem value="OR">OR</SelectItem></SelectContent>
                                  </Select>
                                </div>
                              )}

                              <div className="border rounded bg-background p-3 space-y-2">
                                {/* Group header */}
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <span className="text-[10px] font-medium text-muted-foreground">Group {gIdx + 1}</span>
                                    <HelpTip topic="group_logic" />
                                    {group.conditions.length > 1 && (
                                      <Select value={group.logic || 'AND'} onValueChange={(v) => {
                                        const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                        ioc.groups[gIdx].logic = v;
                                        updateConfig('io_conditions', ioc);
                                      }}>
                                        <SelectTrigger className="h-8 h-5 w-16 text-[10px] font-bold"><SelectValue /></SelectTrigger>
                                        <SelectContent><SelectItem value="AND">AND</SelectItem><SelectItem value="OR">OR</SelectItem></SelectContent>
                                      </Select>
                                    )}
                                  </div>
                                  {(form.config.io_conditions?.groups || []).length > 1 && (
                                    <button onClick={() => {
                                      const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                      ioc.groups.splice(gIdx, 1);
                                      updateConfig('io_conditions', ioc);
                                    }} className="text-[10px] text-red-500 hover:underline">Remove Group</button>
                                  )}
                                </div>

                                {/* Condition rows — dense multi-column grid; scroll
                                    horizontally within the modal instead of overflowing. */}
                                <div className="overflow-x-auto -mx-1 px-1">
                                  <div className="min-w-[620px] space-y-2">
                                    {group.conditions.map((cond, cIdx) => (
                                  <div key={cIdx}>
                                    {cIdx > 0 && (
                                      <div className="text-center py-0.5">
                                        <span className="text-[10px] font-bold text-muted-foreground">{group.logic || 'AND'}</span>
                                      </div>
                                    )}
                                    <div className="grid grid-cols-[85px_1fr_155px_110px_220px_28px] gap-1.5 items-end">
                                      {/* Source type */}
                                      <div className="space-y-0.5">
                                        {cIdx === 0 && <Label className="text-[10px]">Source</Label>}
                                        <Select value={cond.source || 'io_mapping'} onValueChange={(v) => {
                                          const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                          ioc.groups[gIdx].conditions[cIdx] = { ...cond, source: v, display_name: v === 'speed' ? 'Vehicle Speed' : '', io_id: '', io_type: '' };
                                          updateConfig('io_conditions', ioc);
                                        }}>
                                          <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="io_mapping">IO Data</SelectItem>
                                            <SelectItem value="speed">Speed</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>

                                      {/* IO Mapped Data or Speed label */}
                                      <div className="space-y-0.5">
                                        {cIdx === 0 && <Label className="text-[10px]">{cond.source === 'speed' ? 'Parameter' : 'IO Mapped Data'}</Label>}
                                        {cond.source === 'speed' ? (
                                          <Input value="Vehicle Speed (km/h)" disabled className="h-7 text-[10px] bg-muted" />
                                        ) : (
                                          <Select value={cond.display_name || ''} onValueChange={(v) => {
                                            const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                            const mapping = selectedDeviceIOMappings.find(m => m.display_name === v);
                                            ioc.groups[gIdx].conditions[cIdx] = { ...cond, display_name: v, io_id: mapping?.io_id || '', io_type: mapping?.io_type || '' };
                                            updateConfig('io_conditions', ioc);
                                          }}>
                                            <SelectTrigger className="h-7 text-[10px]"><SelectValue placeholder="Select..." /></SelectTrigger>
                                            <SelectContent>
                                              {selectedDeviceIOMappings.map(m => (
                                                <SelectItem key={m.display_name} value={m.display_name}>{m.display_name} ({m.io_type})</SelectItem>
                                              ))}
                                            </SelectContent>
                                          </Select>
                                        )}
                                      </div>
                                      <div className="space-y-0.5">
                                        {cIdx === 0 && <Label className="text-[10px]">Condition</Label>}
                                        <Select value={cond.operator || 'eq'} onValueChange={(v) => {
                                          const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                          ioc.groups[gIdx].conditions[cIdx].operator = v;
                                          updateConfig('io_conditions', ioc);
                                        }}>
                                          <SelectTrigger className="h-7 text-[10px]"><SelectValue /></SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="eq">= Equals</SelectItem>
                                            <SelectItem value="gt">&gt; Greater than</SelectItem>
                                            <SelectItem value="lt">&lt; Less than</SelectItem>
                                            <SelectItem value="gte">&gt;= Greater or equal</SelectItem>
                                            <SelectItem value="lte">&lt;= Less or equal</SelectItem>
                                          </SelectContent>
                                        </Select>
                                      </div>

                                      {/* Value */}
                                      <div className="space-y-0.5">
                                        {cIdx === 0 && <Label className="text-[10px]">Value</Label>}
                                        <Input value={cond.value || ''} onChange={(e) => {
                                          const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                          ioc.groups[gIdx].conditions[cIdx].value = e.target.value;
                                          updateConfig('io_conditions', ioc);
                                        }} placeholder={cond.source === 'speed' ? 'km/h' : '1'} className="h-7 text-[10px]" />
                                      </div>

                                      {/* Per-condition timer + mode */}
                                      <div className="space-y-0.5">
                                        {cIdx === 0 && <div className="flex items-center gap-1"><Label className="text-[10px]">Timer</Label><HelpTip topic="source_timer" /></div>}
                                        <div className="flex items-center gap-0.5">
                                          <NumberInput min="0" value={cond.timer_secs || 0} onValueChange={(n) => {
                                            const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                            ioc.groups[gIdx].conditions[cIdx].timer_secs = n;
                                            updateConfig('io_conditions', ioc);
                                          }} className="h-7 text-[10px] w-[88px] px-2 shrink-0" title="Seconds this condition must be sustained" data-testid={`io-cond-timer-${gIdx}-${cIdx}`} />
                                          {(cond.timer_secs || 0) > 0 && (
                                            <Select value={cond.timer_mode || 'pre'} onValueChange={(v) => {
                                              const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                              ioc.groups[gIdx].conditions[cIdx].timer_mode = v;
                                              updateConfig('io_conditions', ioc);
                                            }}>
                                              <SelectTrigger className="h-7 text-[9px] flex-1 min-w-0 px-1" data-testid={`io-cond-timer-mode-${gIdx}-${cIdx}`}><SelectValue /></SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="pre">Already true for</SelectItem>
                                                {postTimersSupported && <SelectItem value="post">After trigger</SelectItem>}
                                                {form.event_type === 'sensor_io_event' && <SelectItem value="post_detected">After detection</SelectItem>}
                                              </SelectContent>
                                            </Select>
                                          )}
                                        </div>
                                      </div>

                                      {/* Remove condition */}
                                      <div className="space-y-0.5">
                                        {cIdx === 0 && <Label className="text-[10px]">&nbsp;</Label>}
                                        <button onClick={() => {
                                          const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                          if (ioc.groups[gIdx].conditions.length > 1) {
                                            ioc.groups[gIdx].conditions.splice(cIdx, 1);
                                            updateConfig('io_conditions', ioc);
                                          }
                                        }} className="h-7 w-7 flex items-center justify-center rounded border hover:bg-red-50 dark:hover:bg-red-950/30 text-red-500" title="Remove condition" disabled={group.conditions.length <= 1}>
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    </div>
                                    {cond.source !== 'speed' && cond.display_name && ioSnapshot?.values?.[cond.display_name] && (() => {
                                      const snap = ioSnapshot.values[cond.display_name];
                                      return (
                                        <p className="text-[9px] text-muted-foreground mt-0.5 pl-[106px]" data-testid={`io-cond-now-${gIdx}-${cIdx}`}>
                                          Now: {String(snap.raw ?? '—')}{snap.label ? ` → ${snap.label}` : ''}{snap.held_secs != null ? ` (held ${formatHeld(snap.held_secs)})` : ''}
                                        </p>
                                      );
                                    })()}
                                  </div>
                                    ))}
                                  </div>
                                </div>

                                {/* Add condition to group */}
                                <button onClick={() => {
                                  const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                                  ioc.groups[gIdx].conditions.push({ source: 'io_mapping', display_name: '', io_id: '', io_type: '', operator: 'eq', value: '1', timer_secs: 0, timer_mode: 'pre' });
                                  updateConfig('io_conditions', ioc);
                                }} className="text-[10px] text-[#2563eb] hover:underline flex items-center gap-1">
                                  <Plus className="w-3 h-3" /> Add Condition
                                </button>
                              </div>
                            </div>
                          ))}

                          {/* Add group */}
                          <button onClick={() => {
                            const ioc = JSON.parse(JSON.stringify(form.config.io_conditions || {}));
                            ioc.groups.push({ logic: 'AND', junction: 'AND', conditions: [{ source: 'io_mapping', display_name: '', io_id: '', io_type: '', operator: 'eq', value: '1', timer_secs: 0, timer_mode: 'pre' }] });
                            updateConfig('io_conditions', ioc);
                          }} className="text-xs text-[#2563eb] hover:underline flex items-center gap-1">
                            <Plus className="w-3.5 h-3.5" /> Add Condition Group
                          </button>

                          <p className="text-[10px] text-muted-foreground italic">Timer = seconds the condition must be sustained. Main Timer = entire formula must stay true for that duration.</p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                )}

                {/* Live plain-English summary of the current rule configuration */}
                <ExplainRuleBanner form={form} />

                {/* Dry-run the draft rule against live state + recent telemetry */}
                {(form.config.io_conditions_enabled || form.event_type === 'overspeed_fixed') && (
                  <RuleTestPreview api={api} form={form} scopeDevices={ioSourceDevices} />
                )}
              </TabsContent>
              <TabsContent value="time" className="space-y-4 mt-0" forceMount={activeDialogTab === 'time' ? undefined : true} hidden={activeDialogTab !== 'time'}>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Duration from last event</Label>
                    <Switch checked={form.schedule.cooldown_enabled || false} onCheckedChange={(v) => updateSchedule('cooldown_enabled', v)} data-testid="schedule-cooldown-toggle" />
                  </div>
                  {form.schedule.cooldown_enabled && (
                    <div className="flex items-center gap-2">
                      <NumberInput min="0" value={form.schedule.cooldown_minutes || 0} onValueChange={(n) => updateSchedule('cooldown_minutes', n)} className="w-24" />
                      <span className="text-sm text-muted-foreground">minutes between events for same device</span>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Week Days</Label>
                  <div className="flex gap-2">
                    {WEEKDAY_LABELS.map((day, idx) => {
                      const checked = form.schedule.weekdays?.[idx] !== false;
                      return <button key={day} onClick={() => toggleWeekday(idx)} className={`w-10 h-10 rounded-lg text-sm font-medium border transition-colors ${checked ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-white dark:bg-slate-800 text-slate-500 border-slate-200 dark:border-slate-700'}`} data-testid={`weekday-${day.toLowerCase()}`}>{day.charAt(0)}</button>;
                    })}
                  </div>
                </div>

                <div className="flex items-center justify-between p-3 rounded-lg bg-muted">
                  <div><p className="font-medium text-sm">Day Time Restriction</p><p className="text-xs text-muted-foreground">Only evaluate during specific hours each day</p></div>
                  <Switch checked={form.schedule.day_time_enabled || false} onCheckedChange={(v) => updateSchedule('day_time_enabled', v)} data-testid="day-time-toggle" />
                </div>

                {form.schedule.day_time_enabled && (
                  <div className="space-y-1.5 p-3 rounded-lg border">
                    <div className="grid grid-cols-[60px_1fr_16px_1fr] gap-1 mb-1 text-[10px] text-muted-foreground font-medium px-1">
                      <span>Day</span><span>Range 1</span><span></span><span>Range 2 (optional)</span>
                    </div>
                    {WEEKDAY_FULL.map((fullDay, idx) => {
                      const dayKey = fullDay.toLowerCase();
                      const dayEnabled = form.schedule.weekdays?.[idx] !== false;
                      const range = form.schedule.day_time_ranges?.[dayKey] || { start1: '00:00', end1: '12:00', start2: '', end2: '' };
                      const updateRange = (field, val) => {
                        const ranges = { ...(form.schedule.day_time_ranges || {}) };
                        ranges[dayKey] = { ...range, [field]: val };
                        updateSchedule('day_time_ranges', ranges);
                      };
                      return (
                        <div key={fullDay} className={`grid grid-cols-[60px_1fr_16px_1fr] gap-1 items-center ${!dayEnabled ? 'opacity-20 pointer-events-none' : ''}`}>
                          <span className="text-xs font-medium">{fullDay.substring(0, 3)}</span>
                          <div className="flex items-center gap-1">
                            <Input type="time" value={range.start1 || '00:00'} onChange={(e) => updateRange('start1', e.target.value)} className="h-7 text-xs flex-1" />
                            <span className="text-[10px]">-</span>
                            <Input type="time" value={range.end1 || '12:00'} onChange={(e) => updateRange('end1', e.target.value)} className="h-7 text-xs flex-1" />
                          </div>
                          <span className="text-[10px] text-center">&</span>
                          <div className="flex items-center gap-1">
                            <Input type="time" value={range.start2 || ''} onChange={(e) => updateRange('start2', e.target.value)} className="h-7 text-xs flex-1" placeholder="" />
                            <span className="text-[10px]">-</span>
                            <Input type="time" value={range.end2 || ''} onChange={(e) => updateRange('end2', e.target.value)} className="h-7 text-xs flex-1" placeholder="" />
                          </div>
                        </div>
                      );
                    })}
                    <p className="text-[10px] text-muted-foreground mt-1">Leave Range 2 empty if not needed. Deselected days are greyed out.</p>
                  </div>
                )}
              </TabsContent>

              {/* ===== NOTIFICATIONS TAB ===== */}
              <TabsContent value="notifications" className="space-y-3 mt-0" forceMount={activeDialogTab === 'notifications' ? undefined : true} hidden={activeDialogTab !== 'notifications'}>
                <p className="text-sm text-muted-foreground">Configure how you are notified when this event is triggered.</p>

                <NotificationsConfig
                  notifications={form.notifications}
                  onChange={updateNotifications}
                  hasPushNotif={hasPushNotif}
                  hasEmailNotif={hasEmailNotif}
                  hasSmsNotif={hasSmsNotif}
                  pushDisabledReason={pushDisabledReason}
                  emailDisabledReason={emailDisabledReason}
                  smsDisabledReason={smsDisabledReason}
                  notifTemplates={notifTemplates}
                  onManageTemplate={(type) => { setTemplateInitialType(type); setDialogOpen(false); setTimeout(() => setTemplateModalOpen(true), 150); }}
                  supportsTransitionCardColorTiming={supportsTransitionCardColorTiming}
                />
              </TabsContent>

              {/* ===== ACTIONS TAB ===== */}
              {hasEventActions && (
              <TabsContent value="actions" className="space-y-4 mt-0" forceMount={activeDialogTab === 'actions' ? undefined : true} hidden={activeDialogTab !== 'actions'}>
                <p className="text-sm text-muted-foreground">Configure webhook and command actions when this event triggers or clears.</p>

                {/* Webhook Section (shared with Analytics > Alerts) */}
                <WebhookActionConfig actions={form.actions} onChange={updateActions} api={api} testPath="/event-rules/test-webhook" webhookRuleId={editingRule?.id} />

                {/* Command on Trigger Section */}
                {(() => {
                  const selectedDevs = devices.filter(d => form.device_ids.includes(d.id));
                  const protocols = [...new Set(selectedDevs.map(d => d.protocol).filter(Boolean))];
                  const hasMixedProtocols = protocols.length > 1;
                  const noDevicesSelected = form.device_ids.length === 0;
                  const commandsBlocked = hasMixedProtocols;
                  return (
                    <>
                    <div className={`p-3 rounded-lg border space-y-3 ${commandsBlocked ? 'opacity-40 pointer-events-none' : ''}`} data-testid="actions-command-trigger-section">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Switch checked={commandsBlocked ? false : (form.actions.command_on_trigger_enabled || false)} onCheckedChange={(v) => updateActions('command_on_trigger_enabled', v)} data-testid="actions-cmd-trigger-toggle" />
                          <div><p className="font-medium text-sm flex items-center gap-1.5"><Terminal className="w-4 h-4" /> Command on Trigger</p><p className="text-xs text-muted-foreground">Send command to device when event fires</p></div>
                        </div>
                        {commandsBlocked && <Badge variant="secondary" className="text-xs shrink-0">Mixed Protocols</Badge>}
                      </div>
                      {!commandsBlocked && form.actions.command_on_trigger_enabled && (
                        <CommandActionConfig
                          config={form.actions.command_on_trigger || {}}
                          onChange={(key, val) => updateCommandConfig('command_on_trigger', key, val)}
                          devices={devices}
                          selectedDeviceIds={form.device_ids}
                          hasSmsNotif={hasSmsNotif}
                          testIdPrefix="cmd-trigger"
                        />
                      )}
                    </div>
                    {commandsBlocked && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 -mt-2 px-1">Commands require devices with the same protocol. Selected devices have mixed protocols ({protocols.join(', ')}).</p>
                    )}

                    {/* Command on Clear Section — only for active-until-clear event types */}
                    {!['zone_in','zone_out','place_in','place_out','route_entering','route_exiting','gprs_connected','gps_connected','stopped','moving','vehicle_proximity','vehicle_proximity_exit','harsh_driving','crash_detection','video_event','device_event'].includes(form.event_type) && (
                      <div className={`p-3 rounded-lg border space-y-3 ${commandsBlocked ? 'opacity-40 pointer-events-none' : ''}`} data-testid="actions-command-clear-section">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Switch checked={commandsBlocked ? false : (form.actions.command_on_clear_enabled || false)} onCheckedChange={(v) => updateActions('command_on_clear_enabled', v)} data-testid="actions-cmd-clear-toggle" />
                            <div><p className="font-medium text-sm flex items-center gap-1.5"><Terminal className="w-4 h-4" /> Command on Clear</p><p className="text-xs text-muted-foreground">Send command when event condition is resolved</p></div>
                          </div>
                          {commandsBlocked && <Badge variant="secondary" className="text-xs shrink-0">Mixed Protocols</Badge>}
                        </div>
                        {!commandsBlocked && form.actions.command_on_clear_enabled && (
                          <CommandActionConfig
                            config={form.actions.command_on_clear || {}}
                            onChange={(key, val) => updateCommandConfig('command_on_clear', key, val)}
                            devices={devices}
                            selectedDeviceIds={form.device_ids}
                            hasSmsNotif={hasSmsNotif}
                            testIdPrefix="cmd-clear"
                          />
                        )}
                      </div>
                    )}
                    </>
                  );
                })()}
              </TabsContent>
              )}
            </div>
          </Tabs>

          <div className="px-6 py-3 border-t flex justify-end gap-2 flex-shrink-0">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="save-event-rule-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingRule ? 'Update Rule' : 'Create Rule'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Import Template Wizard */}
      <Dialog open={importModalOpen} onOpenChange={(open) => { if (!open && !importing) { setImportModalOpen(false); setImportRules([]); setImportStep(1); setImportDeviceIds([]); } }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="import-wizard-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Download className="w-5 h-5 text-[#2563eb]" />Import Event Rule Templates</DialogTitle>
            <DialogDescription>Templates include rule logic, Sensor / IO configuration, schedules, notifications and actions. Devices and tenant-owned objects are remapped for this account.</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2" data-testid="import-wizard-progress">
            {[1, 2, 3, 4].map((step) => <div key={step} className={`h-2 flex-1 rounded-full ${importStep >= step ? 'bg-[#2563eb]' : 'bg-muted'}`} />)}
          </div>
          {/* !block: override Radix display:table so content wraps on mobile */}
          <ScrollArea className="flex-1 pr-3 [&_[data-radix-scroll-area-viewport]>div]:!block">
            {importStep === 1 && (
              <div className="space-y-3" data-testid="import-wizard-review-step">
                <div className="p-3 rounded-md border bg-blue-50 dark:bg-blue-950/20 text-sm text-blue-900 dark:text-blue-100">New rules will be owned by the importing account. Original owner, device IDs and trigger history are never imported.</div>
                {importRules.map((rule, idx) => {
                  const typeDef = EVENT_TYPES.find(t => t.value === rule.event_type);
                  const TypeIcon = typeDef?.icon || Zap;
                  const deps = rule.template_dependencies || {};
                  return (
                    <div key={idx} className="p-3 rounded-md border bg-muted/20 space-y-2">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-[#2563eb]/10"><TypeIcon className="w-4 h-4 text-[#2563eb]" /></div>
                        <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{rule.name || 'Unnamed Rule'}</p><p className="text-xs text-muted-foreground">{typeDef?.label || rule.event_type} · {rule.severity || 'warning'}</p></div>
                        <Badge variant="outline">Template</Badge>
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {(deps.sensor_sources || rule.config?.sensor_sources || []).length > 0 && <Badge variant="secondary">Sensor / IO config included</Badge>}
                        {(deps.io_conditions_enabled || rule.config?.io_conditions_enabled) && <Badge variant="secondary">IO logic included</Badge>}
                        {(deps.trigger_values || rule.config?.trigger_values || []).length > 0 && <Badge variant="secondary">Trigger values included</Badge>}
                        {deps.command_actions_enabled && <Badge variant="secondary">Command actions included</Badge>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {importStep === 2 && (
              <div className="space-y-3" data-testid="import-wizard-devices-step">
                <div className="flex items-start justify-between gap-3"><div><h4 className="font-medium">Assign devices for this account</h4><p className="text-xs text-muted-foreground">Required so imported templates never default to all devices.</p></div><Badge variant={importDeviceIds.length ? 'outline' : 'destructive'}>{importDeviceIds.length} Selected</Badge></div>
                <Input className="h-8" value={deviceSearch} onChange={(e) => setDeviceSearch(e.target.value)} placeholder="Search devices..." data-testid="import-device-search" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[330px] overflow-y-auto pr-1">
                  {sortedDevices.map((device) => {
                    const checked = importDeviceIds.includes(device.id);
                    return <label key={device.id} className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer ${checked ? 'border-[#2563eb] bg-[#2563eb]/5' : 'hover:bg-muted/40'}`}><Checkbox checked={checked} onCheckedChange={() => setImportDeviceIds(prev => checked ? prev.filter(id => id !== device.id) : [...prev, device.id])} data-testid={`import-device-${device.id}`} /><div className="min-w-0"><p className="text-sm font-medium truncate">{deviceDisplayName(device)}</p><p className="text-xs text-muted-foreground truncate">{device.imei || device.registration || 'No IMEI'}</p></div></label>;
                  })}
                </div>
              </div>
            )}
            {importStep === 3 && (
              <div className="space-y-4" data-testid="import-wizard-remap-step">
                <div className="p-3 rounded-md border bg-amber-50 dark:bg-amber-950/20 text-sm text-amber-900 dark:text-amber-100">Sensor / IO labels are kept as template hints. Geofence IDs from another account are cleared by default and should be remapped after import.</div>
                <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer"><Checkbox checked={importKeepSensorConfig} onCheckedChange={(v) => setImportKeepSensorConfig(!!v)} data-testid="import-keep-sensor-config" /><div><p className="text-sm font-medium">Keep Sensor / IO Event Configuration</p><p className="text-xs text-muted-foreground">Preserves mapped sensor labels, IO conditions, trigger values, thresholds, timers and video/crash/harsh-driving trigger configuration.</p></div></label>
                <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer"><Checkbox checked={importClearGeofenceRefs} onCheckedChange={(v) => setImportClearGeofenceRefs(!!v)} data-testid="import-clear-geofence-refs" /><div><p className="text-sm font-medium">Clear account-specific Geofence references</p><p className="text-xs text-muted-foreground">Recommended. Zones, Places and Routes are tenant-owned and should be selected from this account after import.</p></div></label>
                <label className="flex items-start gap-3 p-3 rounded-md border cursor-pointer"><Checkbox checked={importDisableRules} onCheckedChange={(v) => setImportDisableRules(!!v)} data-testid="import-disabled-toggle" /><div><p className="text-sm font-medium">Import rules disabled for review</p><p className="text-xs text-muted-foreground">Recommended so users can confirm device/sensor/geofence remapping before rules start operating.</p></div></label>
              </div>
            )}
            {importStep === 4 && (
              <div className="space-y-4" data-testid="import-wizard-confirm-step">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3"><div className="p-3 rounded-md border"><p className="text-xs text-muted-foreground">Rules</p><p className="font-semibold">{importRules.length}</p></div><div className="p-3 rounded-md border"><p className="text-xs text-muted-foreground">Devices</p><p className="font-semibold">{importDeviceIds.length}</p></div><div className="p-3 rounded-md border"><p className="text-xs text-muted-foreground">Sensor config</p><p className="font-semibold">{importKeepSensorConfig ? 'Kept' : 'Cleared'}</p></div><div className="p-3 rounded-md border"><p className="text-xs text-muted-foreground">Initial state</p><p className="font-semibold">{importDisableRules ? 'Disabled' : 'As template'}</p></div></div>
                {importDeviceIds.length === 0 && <div className="p-3 rounded-md border border-red-200 bg-red-50 text-sm text-red-700" data-testid="import-device-required-warning">Select at least one device before importing. Empty device selection would operate as All Devices and is not allowed for templates.</div>}
                <p className="text-sm text-muted-foreground">After import, open each rule to confirm device assignments, mapped sensors, geofence references, command actions and notification recipients.</p>
              </div>
            )}
          </ScrollArea>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setImportModalOpen(false); setImportRules([]); setImportStep(1); setImportDeviceIds([]); }} disabled={importing}>Cancel</Button>
            {importStep > 1 && <Button variant="outline" onClick={() => setImportStep(prev => Math.max(1, prev - 1))} disabled={importing} data-testid="import-wizard-back">Back</Button>}
            {importStep < 4 ? <Button onClick={() => setImportStep(prev => Math.min(4, prev + 1))} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="import-wizard-next">Next</Button> : <Button onClick={handleImportConfirm} disabled={importing || importDeviceIds.length === 0} className="bg-[#2563eb] hover:bg-[#1d4ed8]" data-testid="import-wizard-confirm">{importing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</> : <>Import {importRules.length} Template{importRules.length !== 1 ? 's' : ''}</>}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={(open) => { if (!open) setDeleteConfirm(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />Delete Event Rule?</DialogTitle>
            <DialogDescription>Are you sure you want to delete &quot;{deleteConfirm?.name}&quot;? Existing event occurrences will not be deleted.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteConfirm?.id)}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* People Groups manager — compact CRUD used by person-based rules */}
      <Dialog open={groupMgrOpen} onOpenChange={(o) => { setGroupMgrOpen(o); if (!o) setGroupForm(null); }}>
        <DialogContent className="max-w-lg" data-testid="people-groups-dialog">
          <DialogHeader>
            <DialogTitle>People Groups</DialogTitle>
            <DialogDescription>Reusable sets of people for person-based rules. Groups are dynamic — membership changes apply to rules automatically.</DialogDescription>
          </DialogHeader>
          {!groupForm ? (
            <div className="space-y-2">
              <Button size="sm" onClick={() => setGroupForm({ name: '', person_ids: [] })} data-testid="new-people-group-btn">+ New Group</Button>
              <ScrollArea className="max-h-[300px] border rounded-lg">
                {peopleGroups.length === 0 && <p className="text-xs text-muted-foreground p-3">No groups yet.</p>}
                {peopleGroups.map(gp => (
                  <div key={gp.id} className="flex items-center gap-2 px-3 py-2 border-b last:border-b-0" data-testid={`people-group-row-${gp.id}`}>
                    <span className="text-sm">{gp.name}</span>
                    <span className="text-[10px] text-muted-foreground">{(gp.person_ids || []).length} member(s)</span>
                    <div className="ml-auto flex gap-2">
                      <button type="button" className="text-[10px] text-primary hover:underline" onClick={() => setGroupForm({ id: gp.id, name: gp.name, person_ids: gp.person_ids || [] })} data-testid={`edit-people-group-${gp.id}`}>Edit</button>
                      <button type="button" className="text-[10px] text-destructive hover:underline" onClick={() => deleteGroup(gp.id)} data-testid={`delete-people-group-${gp.id}`}>Delete</button>
                    </div>
                  </div>
                ))}
              </ScrollArea>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs">Group name</Label>
                <Input value={groupForm.name} onChange={(e) => setGroupForm(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Morning Crew" data-testid="people-group-name-input" />
              </div>
              <PickList
                label="Members"
                items={peopleOptions.map(p => ({ id: p.id, label: p.name, meta: p.person_type }))}
                selectedIds={groupForm.person_ids || []}
                onChange={(ids) => setGroupForm(prev => ({ ...prev, person_ids: ids }))}
                testIdPrefix="people-group-members"
                height={180}
                emptyNote="No people found — add people on the People page first."
              />
              <div className="flex justify-end gap-2">
                <Button variant="outline" size="sm" onClick={() => setGroupForm(null)} data-testid="people-group-cancel-btn">Back</Button>
                <Button size="sm" onClick={saveGroup} disabled={groupSaving} data-testid="people-group-save-btn">{groupSaving ? 'Saving…' : 'Save Group'}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Template Management Modal (shared with Analytics > Alerts) */}
      <NotificationTemplateModal
        open={templateModalOpen}
        onClose={() => { setTemplateModalOpen(false); setTimeout(() => { setActiveDialogTab('notifications'); setDialogOpen(true); }, 150); }}
        api={api}
        initialType={templateInitialType}
        onTemplatesChanged={fetchTemplates}
      />
    </div>
  );
};